import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, MapPin, Tag, Phone, MessageCircle, Navigation2, Globe, Mail, ChevronRight, ChevronDown, Sparkles, Star, Wrench, LayoutDashboard, ArrowLeft, ExternalLink } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { listingsApi, categoriesApi, analyticsApi, leadsApi } from '@/lib/api';
import { Listing, BlogPost, ProductItem, RealEstateProject } from '@/lib/types';
import { isProductBusiness, isRealEstateBusiness, generateDefaultProducts, generateDefaultRealEstateProjects } from '@/lib/showcase_generators';
import { generateTopTenRealEstateBlog } from '@/lib/blogGenerator';
import { navigate, useRoute, normalizeCategory, linkProps } from '@/lib/router';
import { formatCategoryPlural, slugify } from '@/lib/format';
import { getSeoContentForCategoryAndCity, getFaqJsonLdSchema } from '@/lib/seo_content_generator';
import { INDIAN_CITIES, BUSINESS_CATEGORIES } from '@/lib/data_registry';

import { detectVisitorCity, setUserCity, requestBrowserLocation, trackLandingPageVisit, recordBusinessEnquiry, recordVisitorJourney } from '@/lib/geoTracker';
import { getUpcomingEventsForCity } from '@/lib/events_generator';
import ProductsExplorerPage from './ProductsExplorerPage';
import ProductDetailPage from './ProductDetailPage';
import BlogsExplorerPage from './BlogsExplorerPage';
import BlogDetailPage from './BlogDetailPage';

const rawCategories = [
  'Wine Shops', 'Educational Consultants', 'Restaurant', 'Cafe', 'Home Services', 'Healthcare', 'Beauty & Spa', 'Real Estate', 'Tech Support', 'Automotive', 'Packers & Movers', 'Schools',
  ...BUSINESS_CATEGORIES
];

const rawCities = [
  'Thane', 'Mumbai', 'Delhi NCR', 'Bengaluru', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Kochi',
  ...INDIAN_CITIES
];

const TOP_CATEGORIES = Array.from(new Set(rawCategories));
const TOP_CITIES = Array.from(new Set(rawCities));

export function calculateCityDistanceKm(userCity: string, listingCity: string, listingId: string): number {
  const coords: Record<string, [number, number]> = {
    'chennai': [13.0827, 80.2707],
    'mumbai': [19.0760, 72.8777],
    'delhi ncr': [28.6139, 77.2090],
    'delhi': [28.6139, 77.2090],
    'bengaluru': [12.9716, 77.5946],
    'bangalore': [12.9716, 77.5946],
    'hyderabad': [17.3850, 78.4867],
    'pune': [18.5204, 73.8567],
    'kolkata': [22.5726, 88.3639],
    'abu road': [24.4756, 72.7845],
    'agartala': [23.8315, 91.2868],
    'bhopal': [23.2599, 77.4126],
    'coimbatore': [11.0168, 76.9558],
    'madurai': [9.9252, 78.1198],
    'jaipur': [26.9124, 75.7873],
    'kochi': [9.9312, 76.2673],
    'ahmedabad': [23.0225, 72.5714],
    'surat': [21.1702, 72.8311],
    'lucknow': [26.8467, 80.9462],
    'indore': [22.7196, 75.8577],
    'chandigarh': [30.7333, 76.7794],
  };

  const u = (userCity || 'Chennai').toLowerCase().trim();
  const l = (listingCity || 'Chennai').toLowerCase().trim();

  const c1 = coords[u] || [13.0827, 80.2707];
  const c2 = coords[l] || [13.0827, 80.2707];

  if (c1[0] === c2[0] && c1[1] === c2[1]) {
    const hash = listingId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return Number((0.8 + (hash % 70) * 0.1).toFixed(1));
  }

  const R = 6371; // Earth radius in km
  const dLat = (c2[0] - c1[0]) * Math.PI / 180;
  const dLon = (c2[1] - c1[1]) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(c1[0] * Math.PI / 180) * Math.cos(c2[0] * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return Math.round(R * c);
}

export default function PublicApp({ onAdmin }: { onAdmin: () => void }) {
  const route = useRoute();
  const [detectedCity, setDetectedCity] = useState<string>('Chennai');
  const [dbCategories, setDbCategories] = useState<string[]>([]);

  useEffect(() => {
    let currentPath = window.location.pathname + window.location.search || '/';
    detectVisitorCity().then(geo => {
      setDetectedCity(geo.city);
      trackLandingPageVisit(currentPath, geo.city, geo.ip);
    });

    // Request precise browser location with user permission
    if (typeof window !== 'undefined' && navigator?.geolocation && !sessionStorage.getItem('zha_geo_prompted')) {
      sessionStorage.setItem('zha_geo_prompted', 'true');
      requestBrowserLocation().then(res => {
        if (res && res.city) {
          setDetectedCity(res.city);
          setUserCity(res.city);
        }
      });
    }
  }, [route]);

  useEffect(() => {
    const fetchDbCategories = async () => {
      try {
        const cats = await categoriesApi.getCategories();
        if (cats && cats.length > 0) {
          const names = cats.map((r: any) => r.name).filter(Boolean);
          setDbCategories(names);
          return;
        }
      } catch {}
      try {
        const { data } = await supabase.from('categories').select('name');
        if (data && data.length > 0) {
          const names = data.map((r: any) => r.name).filter(Boolean);
          setDbCategories(names);
        }
      } catch (e) {
        console.error('Failed to fetch categories:', e);
      }
    };
    void fetchDbCategories();
  }, []);

  const [showContactModal, setShowContactModal] = useState(false);

  return (
    <div className="min-h-screen text-slate-900 dark:text-slate-100 flex flex-col bg-white dark:bg-slate-950 font-sans transition-colors duration-200">
      <Header
        onAdmin={onAdmin}
        detectedCity={detectedCity}
        onCityChange={(newCity: string) => {
          setDetectedCity(newCity);
          setUserCity(newCity);
        }}
      />
      <main className="flex-1 flex flex-col">
        {route.name === 'home' && <Home detectedCity={detectedCity} dbCategories={dbCategories} />}
        {(route.name === 'search' || route.name === 'city' || route.name === 'category') && (
          <SearchPage route={route as any} dbCategories={dbCategories} />
        )}
        {route.name === 'business' && <BusinessPage slug={route.slug} />}
        {route.name === 'products' && (
          <ProductsExplorerPage
            categoryFilter={(route as any).category}
            cityFilter={(route as any).city}
            searchQuery={(route as any).q}
          />
        )}
        {route.name === 'product_detail' && <ProductDetailPage slug={route.slug} />}
        {route.name === 'blogs' && (
          <BlogsExplorerPage categoryFilter={(route as any).category} searchQuery={(route as any).q} />
        )}
        {route.name === 'blog' && <BlogDetailPage slug={route.slug} />}
      </main>
      <Footer onOpenContact={() => setShowContactModal(true)} />
    </div>
  );
}

function Header({ onAdmin, detectedCity, onCityChange }: { onAdmin: () => void; detectedCity?: string; onCityChange?: (newCity: string) => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [menuItems, setMenuItems] = useState<any[]>([]);

  // Logged-in user & Profile menu states
  const [currentUser, setCurrentUser] = useState<{ email: string; name: string } | null>(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [ownedCount, setOwnedCount] = useState(0);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passError, setPassError] = useState('');
  const [passSuccess, setPassSuccess] = useState('');
  const [passLoading, setPassLoading] = useState(false);

  const cityPickerRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (cityPickerRef.current && !cityPickerRef.current.contains(e.target as Node)) {
        setCityDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchOwnedCount = async (email: string) => {
    try {
      const { count } = await supabase.from('listings').select('id', { count: 'exact', head: true }).or(`email.eq.${email},contact_email.eq.${email}`);
      if (typeof count === 'number') {
        setOwnedCount(count);
      } else {
        const localListings = JSON.parse(localStorage.getItem('localhub_owner_listings') || '[]');
        setOwnedCount(localListings.length);
      }
    } catch {
      const localListings = JSON.parse(localStorage.getItem('localhub_owner_listings') || '[]');
      setOwnedCount(localListings.length);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      // 1. Check Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const u = {
          email: session.user.email || '',
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User'
        };
        setCurrentUser(u);
        fetchOwnedCount(u.email);
        return;
      }

      // 2. Check local owner user
      const localOwner = localStorage.getItem('owner_user');
      if (localOwner) {
        try {
          const parsed = JSON.parse(localOwner);
          if (parsed && parsed.email) {
            setCurrentUser({ email: parsed.email, name: parsed.name || parsed.email.split('@')[0] });
            fetchOwnedCount(parsed.email);
            return;
          }
        } catch {}
      }

      setCurrentUser(null);
      setOwnedCount(0);
    };

    void checkAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const u = {
          email: session.user.email || '',
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User'
        };
        setCurrentUser(u);
        fetchOwnedCount(u.email);
      } else {
        const localOwner = localStorage.getItem('owner_user');
        if (!localOwner) {
          setCurrentUser(null);
          setOwnedCount(0);
        }
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPassError('');
    setPassSuccess('');

    if (!newPassword || newPassword.length < 6) {
      setPassError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPassError('Passwords do not match.');
      return;
    }

    setPassLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setPassError(error.message);
      } else {
        setPassSuccess('Password updated successfully!');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setShowChangePasswordModal(false), 2000);
      }
    } catch (err: any) {
      setPassError(err.message || 'Failed to update password.');
    } finally {
      setPassLoading(false);
    }
  };

  const popularCities = useMemo(() => {
    const defaultList = ['Chennai', 'Bengaluru', 'Mumbai', 'Delhi NCR', 'Hyderabad', 'Pune', 'Kolkata', 'Coimbatore', 'Ahmedabad', 'Kochi', 'Jaipur', 'Madurai', 'Trichy', 'Salem'];
    if (!citySearch.trim()) return defaultList;
    return TOP_CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase()));
  }, [citySearch]);

  const handleSelectCity = (chosenCity: string) => {
    setUserCity(chosenCity);
    onCityChange?.(chosenCity);
    setCityDropdownOpen(false);
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem('localhub_menu_config');
      if (saved) {
        setMenuItems(JSON.parse(saved));
      }
    } catch {}

    void (async () => {
      try {
        const { data } = await supabase.from('settings').select('value').eq('key', 'navigation_menu').maybeSingle();
        if (data && data.value && Array.isArray(data.value)) {
          setMenuItems(data.value);
        }
      } catch {}
    })();
  }, []);

  const defaultNav = [
    { id: 'm1', label: 'Home', path: '/', children: [] },
    { id: 'm2', label: 'Explore Directory', path: '/search', children: [
      { id: 'm2_1', label: 'Cafés in City', path: '/cat/Cafe' },
      { id: 'm2_2', label: 'Restaurants', path: '/cat/Restaurant' },
      { id: 'm2_3', label: 'Home Services', path: '/cat/Home%20Services' },
      { id: 'm2_4', label: 'Automotive & Repairs', path: '/cat/Automotive' }
    ] },
    { id: 'm4', label: 'Top Blogs', path: '/blogs', children: [] }
  ];

  const navList = menuItems.length > 0 ? menuItems.filter(m => m.is_header !== false) : defaultNav;

  const formatNavHref = (p: string) => {
    if (!p) return '/';
    let clean = p.startsWith('#') ? p.slice(1) : p;
    if (!clean.startsWith('/')) clean = '/' + clean;
    return clean;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-[1200] w-full bg-white border-b border-slate-200 shadow-sm transition-all duration-300">
      <div className="max-w-container-max mx-auto w-full px-6 py-3.5 flex justify-between items-center">
        <div className="flex items-center gap-6">
          <a {...linkProps('/')} className="flex items-center gap-2 group py-1" aria-label="Zha Search Home">
            <img src="/zha-logo.webp" alt="Zha Search Logo" width="160" height="44" className="h-10 md:h-11 w-auto object-contain rounded-lg shadow-xs hover:opacity-95 transition-opacity" />
          </a>
          
          {/* Dynamic Navigation Menu Items */}
          <nav className="hidden lg:flex items-center gap-1 text-sm font-semibold text-on-surface-variant">
            {navList.map((item) => {
              const hasSubs = item.children && item.children.length > 0;
              return (
                <div key={item.id} className="relative group">
                  <a
                    className="hover:text-primary transition-all duration-200 px-3 py-2 rounded-xl flex items-center gap-1 text-slate-800 font-extrabold"
                    {...linkProps(formatNavHref(item.path))}
                    onMouseEnter={() => hasSubs && setActiveDropdown(item.id)}
                  >
                    <span>{item.label}</span>
                    {hasSubs && <span className="material-symbols-outlined text-sm">expand_more</span>}
                  </a>

                  {/* Dropdown Menu for Subheaders */}
                  {hasSubs && (
                    <div className="absolute top-full left-0 hidden group-hover:flex flex-col bg-white border border-slate-200 rounded-2xl shadow-xl p-2 w-56 text-xs font-bold text-slate-800 space-y-1 z-50 animate-fade-in">
                      {item.children.map((sub: any) => (
                        <a
                          key={sub.id}
                          className="hover:bg-emerald-50 hover:text-emerald-700 px-3 py-2 rounded-xl block font-bold text-slate-700 transition-colors"
                          {...linkProps(formatNavHref(sub.path))}
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
        
        {/* Header Right Actions */}
        <div className="flex items-center gap-2.5">
          {/* Free Listing CTA Button */}
          <a
            href="/owner"
            className="group relative overflow-hidden bg-gradient-to-r from-emerald-700 via-teal-600 to-emerald-700 bg-[length:200%_auto] hover:bg-[position:right_center] text-white font-extrabold text-xs px-4 md:px-5 py-2.5 rounded-full shadow-lg shadow-emerald-900/30 hover:shadow-emerald-700/50 transition-all duration-500 transform hover:-translate-y-0.5 active:translate-y-0 flex items-center gap-1.5 cursor-pointer shrink-0 border border-emerald-400/30"
          >
            <span className="material-symbols-outlined text-base animate-pulse text-amber-300">add_business</span>
            <span className="tracking-wide font-black">Free Listing</span>
          </a>

          {/* User Profile Avatar & Dropdown OR Sign In Button */}
          {currentUser ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs pl-1.5 pr-3 py-1 rounded-full shadow-md transition-all cursor-pointer border border-slate-700/80"
                title="Account Menu"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-emerald-600 to-teal-400 text-white font-black text-xs flex items-center justify-center shadow-inner uppercase">
                  {currentUser.name.charAt(0) || currentUser.email.charAt(0) || 'U'}
                </div>
                <span className="max-w-[90px] truncate hidden md:inline-block text-slate-100 font-extrabold">{currentUser.name}</span>
                <span className="material-symbols-outlined text-slate-400 text-sm">expand_more</span>
              </button>

              {userDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl p-2.5 z-50 animate-fade-in text-slate-800">
                  {/* User Profile Header */}
                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl mb-2 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-700 text-white font-black text-sm flex items-center justify-center uppercase shrink-0 shadow-sm">
                      {currentUser.name.charAt(0) || 'U'}
                    </div>
                    <div className="min-w-0">
                      <div className="font-extrabold text-xs text-slate-900 truncate">{currentUser.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{currentUser.email}</div>
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-700 bg-emerald-100/80 px-1.5 py-0.5 rounded mt-1">
                        <span className="material-symbols-outlined text-[10px]">verified</span> Verified Owner
                      </span>
                    </div>
                  </div>

                  {/* Profile Menu Items */}
                  <div className="space-y-0.5 text-xs font-bold text-slate-700">
                    <a
                      href="/owner"
                      onClick={() => setUserDropdownOpen(false)}
                      className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 hover:text-emerald-800 rounded-xl flex items-center justify-between transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="material-symbols-outlined text-emerald-600 text-base">storefront</span>
                        <span>My Businesses</span>
                      </div>
                      <span className="bg-emerald-100 text-emerald-800 font-extrabold text-[10px] px-2 py-0.5 rounded-full">({ownedCount})</span>
                    </a>

                    <a
                      href="/owner"
                      onClick={() => setUserDropdownOpen(false)}
                      className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 hover:text-emerald-800 rounded-xl flex items-center gap-2.5 transition-colors"
                    >
                      <span className="material-symbols-outlined text-teal-600 text-base">add_circle</span>
                      <span>Register New Business</span>
                    </a>

                    <a
                      href="/owner"
                      onClick={() => setUserDropdownOpen(false)}
                      className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 hover:text-emerald-800 rounded-xl flex items-center gap-2.5 transition-colors"
                    >
                      <span className="material-symbols-outlined text-indigo-600 text-base">event</span>
                      <span>Create & Manage Events</span>
                    </a>

                    <button
                      onClick={() => {
                        setUserDropdownOpen(false);
                        setShowChangePasswordModal(true);
                      }}
                      className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 hover:text-emerald-800 rounded-xl flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <span className="material-symbols-outlined text-amber-600 text-base">lock_reset</span>
                      <span>Change Password</span>
                    </button>
                  </div>

                  {/* Sign Out Divider */}
                  <div className="border-t border-slate-100 my-1.5 pt-1.5">
                    <button
                      onClick={async () => {
                        setUserDropdownOpen(false);
                        try {
                          await supabase.auth.signOut();
                        } catch {}
                        localStorage.removeItem('owner_user');
                        setCurrentUser(null);
                        setOwnedCount(0);
                        window.location.reload();
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 font-bold text-xs rounded-xl flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <span className="material-symbols-outlined text-base">logout</span>
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <a
              href="/owner"
              className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 font-bold text-xs px-3.5 py-2 rounded-full transition-all flex items-center gap-1.5 shadow-xs cursor-pointer shrink-0"
            >
              <span className="material-symbols-outlined text-base text-slate-600">account_circle</span>
              <span className="hidden sm:inline">Sign In</span>
            </a>
          )}
          
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden p-2 text-on-surface-variant hover:bg-surface-container rounded-full transition-colors cursor-pointer shrink-0"
          >
            <span className="material-symbols-outlined">{menuOpen ? 'close' : 'menu'}</span>
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-200 text-slate-800 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-600 text-2xl">lock_reset</span>
                <h3 className="font-extrabold text-base text-slate-900">Change Account Password</h3>
              </div>
              <button
                onClick={() => setShowChangePasswordModal(false)}
                className="text-slate-400 hover:text-slate-700 font-bold text-base cursor-pointer"
              >
                ✕
              </button>
            </div>

            {passError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-bold rounded-xl flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">warning</span>
                <span>{passError}</span>
              </div>
            )}

            {passSuccess && (
              <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold rounded-xl flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">check_circle</span>
                <span>{passSuccess}</span>
              </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">New Password (min 6 characters)</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:border-emerald-600"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:border-emerald-600"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowChangePasswordModal(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs py-2.5 rounded-xl transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passLoading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs py-2.5 rounded-xl transition cursor-pointer shadow-md disabled:opacity-50"
                >
                  {passLoading ? 'Updating...' : 'Save Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Drawer Menu */}
      {menuOpen && (
        <div className="lg:hidden mx-4 mb-3 bg-white/95 border border-outline-variant/30 rounded-2xl shadow-2xl p-4 flex flex-col gap-2 font-semibold text-sm text-on-surface z-50 relative animate-fade-in">
          {navList.map((item) => (
            <div key={item.id} className="space-y-1">
              <a
                {...linkProps(formatNavHref(item.path))}
                onClick={(e) => {
                  setMenuOpen(false);
                  linkProps(formatNavHref(item.path)).onClick(e as any);
                }}
                className="hover:text-primary hover:bg-surface-container transition-all px-4 py-2 rounded-xl block font-extrabold text-slate-900"
              >
                {item.label}
              </a>
              {item.children && item.children.length > 0 && (
                <div className="pl-6 space-y-1">
                  {item.children.map((sub: any) => (
                    <a
                      key={sub.id}
                      {...linkProps(formatNavHref(sub.path))}
                      onClick={(e) => {
                        setMenuOpen(false);
                        linkProps(formatNavHref(sub.path)).onClick(e as any);
                      }}
                      className="text-xs text-slate-600 hover:text-emerald-700 block py-1.5 px-3 rounded-lg font-bold"
                    >
                      • {sub.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}

function ContactModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [userLocation, setUserLocation] = useState<{ city: string; ip: string }>({ city: 'Detecting...', ip: '' });
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    // Fetch Categories for dropdown
    void supabase.from('listings').select('category').limit(1000).then(({ data }) => {
      if (data) {
        const cats = Array.from(new Set(data.map(d => d.category).filter(Boolean) as string[])).sort();
        setAllCategories(cats);
      }
    });

    // Auto-detect City & IP automatically
    void detectVisitorCity().then(geo => {
      setUserLocation({
        city: geo.city || 'Chennai',
        ip: geo.ip || ''
      });
    });
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredCategories = allCategories.filter(c => c.toLowerCase().includes(categorySearch.toLowerCase()));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!name.trim()) {
      setErrorMsg('Please enter your full name.');
      return;
    }
    if (!phone.trim() || phone.trim().length < 8) {
      setErrorMsg('Please enter a valid phone number.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        phone: phone.trim(),
        category: selectedCategory || categorySearch || 'General Enquiry',
        city: userLocation.city || 'Chennai',
        source: `mobile_contact_modal (IP: ${userLocation.ip || 'unknown'})`,
        created_at: new Date().toISOString()
      };

      void leadsApi.createLead({
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        service_needed: selectedCategory || categorySearch || 'General Enquiry',
        city: userLocation.city || 'Chennai'
      });
      void analyticsApi.recordEvent({
        event_type: 'enquiry',
        city: userLocation.city || 'Chennai',
        category: selectedCategory || categorySearch || 'General Enquiry',
        meta: payload
      });

      setSuccessMsg('Thank you! Your enquiry has been received. Our team will contact you shortly.');
      setTimeout(() => {
        setSuccessMsg('');
        setName('');
        setPhone('');
        setSelectedCategory('');
        setCategorySearch('');
        onClose();
      }, 2500);
    } catch (err: any) {
      setSuccessMsg('Enquiry received! We will get in touch soon.');
      setTimeout(() => {
        onClose();
      }, 2000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden relative text-slate-900">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-slate-900 via-emerald-950 to-slate-900 p-5 text-white flex justify-between items-center">
          <div>
            <h3 className="font-extrabold text-base flex items-center gap-2">
              <span className="material-symbols-outlined text-emerald-400">support_agent</span>
              Get Instant Call Back & Quote
            </h3>
            <p className="text-[11px] text-slate-300 font-medium mt-0.5">Connect with verified local services & experts</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs font-bold flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">error</span>
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-bold flex items-center gap-2">
              <span className="material-symbols-outlined text-sm text-emerald-600">check_circle</span>
              <span>{successMsg}</span>
            </div>
          )}

          {/* Service / Category Dropdown */}
          <div className="relative">
            <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1">
              Select Service / Category
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                search
              </span>
              <input
                type="text"
                placeholder="Type to search service (e.g. Cleaning, Dentists)..."
                value={selectedCategory || categorySearch}
                onChange={(e) => {
                  setSelectedCategory('');
                  setCategorySearch(e.target.value);
                  setShowCategoryDropdown(true);
                }}
                onFocus={() => setShowCategoryDropdown(true)}
                className="w-full pl-9 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-600 focus:bg-white transition text-slate-900"
              />
              {categorySearch && (
                <button
                  type="button"
                  onClick={() => { setCategorySearch(''); setSelectedCategory(''); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <span className="material-symbols-outlined text-sm">cancel</span>
                </button>
              )}
            </div>

            {/* Dropdown list */}
            {showCategoryDropdown && filteredCategories.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-2xl z-50 p-1">
                {filteredCategories.map((cat) => (
                  <div
                    key={cat}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setCategorySearch(cat);
                      setShowCategoryDropdown(false);
                    }}
                    className="px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-emerald-50 hover:text-emerald-900 rounded-lg cursor-pointer flex items-center justify-between transition"
                  >
                    <span>{cat}</span>
                    <span className="material-symbols-outlined text-xs text-slate-300">chevron_right</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1">
              Your Full Name *
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                person
              </span>
              <input
                type="text"
                placeholder="Enter your name..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-600 focus:bg-white transition text-slate-900"
              />
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1">
              Mobile Number *
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                call
              </span>
              <input
                type="tel"
                placeholder="Enter 10-digit mobile number..."
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold outline-none focus:border-emerald-600 focus:bg-white transition text-slate-900"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-extrabold text-xs h-11 rounded-xl shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer transition transform active:scale-98 mt-2"
          >
            {submitting ? (
              <span>Submitting Enquiry...</span>
            ) : (
              <>
                <span className="material-symbols-outlined text-sm">send</span>
                <span>Submit Quick Enquiry</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function Footer({ onOpenContact }: { onOpenContact?: () => void }) {
  return (
    <footer className="bg-surface-container-low w-full py-16 px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 gap-12 max-w-container-max mx-auto border-t border-outline-variant/30 pb-24 md:pb-16 mt-24">
      <div className="col-span-1 md:col-span-2">
        <a {...linkProps('/')} className="flex items-center gap-2 group py-1 mb-4 inline-block" aria-label="Zha Search Home">
            <img src="/zha-logo.webp" alt="Zha Search Logo" width="160" height="44" className="h-10 w-auto object-contain rounded-lg" />
          </a>
          <p className="font-body-md text-on-surface-variant max-w-md text-sm">India's premier verified local business directory and e-commerce discovery platform. Connect with verified shops, businesses, and on-demand services across India.</p>
        {/* Social Media Links */}
        <div className="flex items-center gap-3 mt-6">
          {/* Facebook */}
          <a
            href="https://www.facebook.com/zhasearch"
            target="_blank"
            rel="noopener noreferrer"
            title="Facebook"
            className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-[#1877F2] hover:text-white text-slate-700 dark:text-slate-300 flex items-center justify-center transition-all duration-300 shadow-sm hover:scale-110"
          >
            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>

          {/* Instagram */}
          <a
            href="https://www.instagram.com/zhasearch"
            target="_blank"
            rel="noopener noreferrer"
            title="Instagram"
            className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-gradient-to-tr hover:from-amber-500 hover:via-rose-500 hover:to-purple-600 hover:text-white text-slate-700 dark:text-slate-300 flex items-center justify-center transition-all duration-300 shadow-sm hover:scale-110"
          >
            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>

          {/* X (Twitter) */}
          <a
            href="https://x.com/zhasearch"
            target="_blank"
            rel="noopener noreferrer"
            title="X (Twitter)"
            className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-black hover:text-white text-slate-700 dark:text-slate-300 flex items-center justify-center transition-all duration-300 shadow-sm hover:scale-110"
          >
            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>

          {/* YouTube */}
          <a
            href="https://www.youtube.com/@zhasearch"
            target="_blank"
            rel="noopener noreferrer"
            title="YouTube"
            className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-[#FF0000] hover:text-white text-slate-700 dark:text-slate-300 flex items-center justify-center transition-all duration-300 shadow-sm hover:scale-110"
          >
            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
        </div>

        <p className="font-label-sm text-xs text-on-surface-variant mt-8">© 2026 Zha Search. Premium Business Directory.</p>
      </div>
      <div className="flex flex-col gap-3 text-sm">
        <h5 className="font-display font-bold text-on-background mb-2">Explore</h5>
        <a className="text-on-surface-variant hover:text-primary transition-colors" {...linkProps('/')}>Home</a>
        <a className="text-on-surface-variant hover:text-primary transition-colors" {...linkProps('/search')}>All Listings</a>
        <a className="text-on-surface-variant hover:text-primary transition-colors" href="/sitemap.xml">Sitemap</a>
        <a className="text-on-surface-variant hover:text-primary transition-colors" href="/rss.xml">RSS Feed</a>
      </div>
      <div className="flex flex-col gap-3 text-sm">
        <h5 className="font-display font-bold text-on-background mb-2">Support</h5>
        <a className="text-on-surface-variant hover:text-primary transition-colors" {...linkProps('/')}>Partner with us</a>
        <button onClick={onOpenContact} className="text-on-surface-variant hover:text-primary transition-colors text-left cursor-pointer">Contact Support</button>
        <a className="text-on-surface-variant hover:text-primary transition-colors" {...linkProps('/')}>Privacy Policy</a>
        <a className="text-on-surface-variant hover:text-primary transition-colors" {...linkProps('/')}>Terms of Service</a>
      </div>

      {/* Mobile-Only Bottom Sticky Quick Action Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 py-2.5 px-6 flex items-center justify-around shadow-2xl">
        <a
          {...linkProps('/')}
          className="flex flex-col items-center gap-0.5 text-[10px] font-bold text-slate-300 hover:text-white transition cursor-pointer"
        >
          <span className="material-symbols-outlined text-lg text-emerald-400">home</span>
          <span>Home</span>
        </a>

        <a
          href="/owner"
          className="relative overflow-hidden bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 bg-[length:200%_auto] animate-gradient-x text-white font-black text-xs px-4 py-2 rounded-full shadow-lg shadow-emerald-500/30 flex items-center gap-1.5 transform active:scale-95 transition cursor-pointer"
        >
          <span className="material-symbols-outlined text-sm animate-pulse text-amber-300">add_business</span>
          <span>Free Listing</span>
          <span className="text-xs">🚀</span>
        </a>

        <button
          onClick={onOpenContact}
          className="flex flex-col items-center gap-0.5 text-[10px] font-bold text-slate-300 hover:text-white transition cursor-pointer"
        >
          <span className="material-symbols-outlined text-lg text-teal-400">support_agent</span>
          <span>Contact</span>
        </button>
      </div>
    </footer>
  );
}

function HeroSearch({ initialQ = '', initialCity = '', initialCategory = '' }: { initialQ?: string; initialCity?: string; initialCategory?: string }) {
  const [q, setQ] = useState(initialQ);
  const [city, setCity] = useState(initialCity);
  const [category, setCategory] = useState(initialCategory);
  const [cities, setCities] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    void supabase.from('listings').select('city, category').limit(500).then(({ data }) => {
      const rows = (data as { city: string | null; category: string | null }[]) ?? [];
      setCities(Array.from(new Set(rows.map((r) => r.city).filter(Boolean) as string[])).sort());
      setCategories(Array.from(new Set(rows.map((r) => r.category).filter(Boolean) as string[])).sort());
    });
  }, []);

  const submit = () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (city) params.set('city', city);
    if (category) params.set('category', category);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="w-full bg-white/[0.04] ring-1 ring-white/10 rounded-2xl p-2 flex flex-col sm:flex-row gap-2 shadow-[0_20px_60px_-20px_rgba(6,182,212,0.35)]">
      <div className="flex-1 flex items-center gap-2 px-3 py-2">
        <Search className="h-4 w-4 text-ink-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="What are you looking for?"
          className="w-full bg-transparent outline-none text-sm text-white placeholder-ink-500"
        />
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-l border-white/5">
        <MapPin className="h-4 w-4 text-ink-400" />
        <select value={city} onChange={(e) => setCity(e.target.value)} className="bg-transparent text-sm text-white outline-none min-w-32">
          <option value="" className="bg-ink-800">Any city</option>
          {cities.map((c) => <option key={c} value={c} className="bg-ink-800">{c}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-l border-white/5">
        <Tag className="h-4 w-4 text-ink-400" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-transparent text-sm text-white outline-none min-w-32">
          <option value="" className="bg-ink-800">Any category</option>
          {categories.map((c) => <option key={c} value={c} className="bg-ink-800">{c}</option>)}
        </select>
      </div>
      <button
        onClick={submit}
        className="rounded-xl px-5 py-3 bg-brand-500 hover:bg-brand-400 text-ink-950 font-medium text-sm shadow-lg transition"
      >
        Search
      </button>
    </div>
  );
}

const categoryIcons: Record<string, string> = {
  'Restaurant': 'restaurant',
  'Cafe': 'local_cafe',
  'Home Services': 'home_repair_service',
  'Healthcare': 'local_hospital',
  'Beauty & Spa': 'spa',
  'Automotive': 'directions_car',
  'Tech Support': 'computer',
  'Real Estate': 'real_estate_agent',
};

const categoryImages: Record<string, string> = {
  "Car Wash": "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?auto=format&fit=crop&w=600&q=80",
  "Wine Shops": "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=600&q=80",
  "Photo Studios": "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=600&q=80",
  "Educational Consultants": "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=600&q=80",
  "Restaurant": "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=600&q=80",
  "Cafe": "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=600&q=80",
  "Home Services": "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=600&q=80",
  "Healthcare": "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=600&q=80",
  "Beauty & Spa": "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=600&q=80",
  "Automotive": "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=600&q=80",
  "Tech Support": "https://images.unsplash.com/photo-1600132806370-bf17e65e942f?auto=format&fit=crop&w=600&q=80",
  "Real Estate": "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=600&q=80",
  "Schools": "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=600&q=80",
  "Packers & Movers": "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80",
  "Default": "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80"
};

const DIVERSE_CATEGORY_POOLS: Record<string, string[]> = {
  // 1. Bakeries, Cake Shops & Pastries
  "Bakery": [
    "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1555507036-ab1f4038808a?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1517433670267-08bbd4be890f?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1586985289688-ca3cf47d3e6e?auto=format&fit=crop&w=600&q=80"
  ],
  // 2. Amusement, Theme Parks & Adventure
  "Amusement": [
    "https://images.unsplash.com/photo-1505778276668-26b3ff7af103?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1534447677768-be436bb09401?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1576086213369-97a306d36557?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?auto=format&fit=crop&w=600&q=80"
  ],
  // 3. Printing Services & Digital Press
  "Printing": [
    "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?auto=format&fit=crop&w=600&q=80"
  ],
  // 4. Hotels & Resorts
  "Hotel": [
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=600&q=80"
  ],
  // 5. Restaurants & Food
  "Restaurant": [
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=600&q=80"
  ],
  // 6. Cafes & Coffee
  "Cafe": [
    "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=600&q=80"
  ],
  // 7. Dentists & Dental Clinics
  "Dentist": [
    "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=600&q=80"
  ],
  // 8. Healthcare, Hospitals & Clinics
  "Healthcare": [
    "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=600&q=80"
  ],
  // 9. Eye Clinics & Opticians
  "Eye": [
    "https://images.unsplash.com/photo-1574258495973-f010dfbb5371?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1511499767150-a48a237f0083?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1591076482161-42ce6da69f67?auto=format&fit=crop&w=600&q=80"
  ],
  // 10. Pharmacies & Chemists
  "Pharmacy": [
    "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1586015555751-63c25b306915?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?auto=format&fit=crop&w=600&q=80"
  ],
  // 11. Real Estate & Builders
  "Real Estate": [
    "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=600&q=80"
  ],
  // 12. Interior Designers & Architects
  "Interior": [
    "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=600&q=80"
  ],
  // 13. AC Dealers & HVAC Services
  "AC Dealers": [
    "https://images.unsplash.com/photo-1625602812206-5ec545ca1231?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1585771724684-38269d6639fd?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=600&q=80"
  ],
  // 14. Electricians
  "Electricians": [
    "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=600&q=80"
  ],
  // 15. Plumbers
  "Plumbers": [
    "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1585771724684-38269d6639fd?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1556909172-54557c7e4fb7?auto=format&fit=crop&w=600&q=80"
  ],
  // 16. Painters
  "Painters": [
    "https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?auto=format&fit=crop&w=600&q=80"
  ],
  // 17. Carpenters
  "Carpenters": [
    "https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=600&q=80"
  ],
  // 18. Car Repair, Garages & Mechanics
  "Automotive": [
    "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?auto=format&fit=crop&w=600&q=80"
  ],
  // 19. Car Wash & Detailing
  "Car Wash": [
    "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?auto=format&fit=crop&w=600&q=80"
  ],
  // 20. Beauty Parlours, Salons & Spas
  "Beauty": [
    "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&w=600&q=80"
  ],
  // 21. Gyms & Fitness Centers
  "Gym": [
    "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?auto=format&fit=crop&w=600&q=80"
  ],
  // 22. Schools, Colleges & Coaching
  "Schools": [
    "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=600&q=80"
  ],
  // 23. Advocates, Legal & Chartered Accountants
  "Legal": [
    "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=600&q=80"
  ],
  // 24. Banquet Halls, Marriage Halls & Events
  "Events": [
    "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&w=600&q=80"
  ],
  // 25. Jewellery Showrooms
  "Jewellery": [
    "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=600&q=80"
  ],
  // 26. Clothing, Sarees & Boutiques
  "Clothing": [
    "https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1523381294911-8d3cead13475?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?auto=format&fit=crop&w=600&q=80"
  ],
  // 27. Electronic Stores & Mobile Repair
  "Electronics": [
    "https://images.unsplash.com/photo-1550009158-9ebf69173e03?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1563770660941-20978e870e26?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1597872200969-2b65d56bd16b?auto=format&fit=crop&w=600&q=80"
  ],
  // 28. Packers & Movers
  "Packers": [
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=600&q=80"
  ],
  // 29. Pet Clinics & Animal Care
  "Pet": [
    "https://images.unsplash.com/photo-1576201836106-db1758fd1c97?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&w=600&q=80"
  ],
  // 30. General Stores, Supermarkets & Grocery
  "Supermarket": [
    "https://images.unsplash.com/photo-1578916171728-46686eac8d58?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1534723452862-4c874018d66d?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1608686207856-001b95cf60ca?auto=format&fit=crop&w=600&q=80"
  ],
  // 31. Home Services, Maids & Housekeeping
  "Home Services": [
    "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?auto=format&fit=crop&w=600&q=80"
  ],
  // 32. Photography & Studios
  "Photo Studios": [
    "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=600&q=80"
  ],
  // 33. Wine & Liquor
  "Wine Shops": [
    "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1563227812-0ea4c22e6cc8?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1527061011665-3652c757a4d4?auto=format&fit=crop&w=600&q=80"
  ],
  // 34. Cinema & Theatres
  "Cinema": [
    "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=600&q=80"
  ],
  // 35. Tourism & Attractions
  "Tourism": [
    "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1506461883276-594a12b11cf3?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&w=600&q=80"
  ],
  // 36. Martial Arts, Karate & Fitness
  "Martial Arts": [
    "https://images.unsplash.com/photo-1555597673-b21d5c935865?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&w=600&q=80"
  ],
  // 37. Tyres & Automobiles
  "Tyres": [
    "https://images.unsplash.com/photo-1578844251758-2f71da64c96f?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&w=600&q=80"
  ],
  "Tempo": [
    "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&w=800&q=80"
  ],
  // 38. Water Purifier & RO Services
  "Water Purifier": [
    "https://images.unsplash.com/photo-1548839140-29a749e1bc4e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1585829365295-ab7cd400c167?auto=format&fit=crop&w=600&q=80"
  ],
  // 39. Default Clean Modern Business Building & Office Front
  "Default": [
    "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80"
  ]
};

export function getCategorySpecificImage(cat?: string | null, seedString: string = ''): string {
  const lower = (cat || '').toLowerCase();
  let key = 'Default';

  // 1. Bakeries & Pastries
  if (lower.includes('baker') || lower.includes('cake') || lower.includes('pastry') || lower.includes('sweet shop') || lower.includes('cookie') || lower.includes('bread')) key = 'Bakery';
  // 2. Amusement, Theme Parks & Adventure
  else if (lower.includes('amusement') || lower.includes('theme park') || lower.includes('adventure') || lower.includes('water park') || lower.includes('arcade') || lower.includes('gaming') || lower.includes('bowling')) key = 'Amusement';
  // 3. Cinema, Movies & Theatres
  else if (lower.includes('cinema') || lower.includes('theatre') || lower.includes('theater') || lower.includes('rajhans') || lower.includes('multiplex') || lower.includes('film') || lower.includes('movie')) key = 'Cinema';
  // 4. Tourist Attractions & Sightseeing
  else if (lower.includes('tourist') || lower.includes('attraction') || lower.includes('sightsee') || lower.includes('monument') || lower.includes('heritage') || lower.includes('temple') || lower.includes('fort')) key = 'Tourism';
  // 5. Printing Services & Xerox
  else if (lower.includes('print') || lower.includes('xerox') || lower.includes('flex') || lower.includes('digital press') || lower.includes('offset') || lower.includes('signage') || lower.includes('banner')) key = 'Printing';
  // 6. Dentists
  else if (lower.includes('dent') || lower.includes('tooth') || lower.includes('orthodont')) key = 'Dentist';
  // 7. Eye Clinics & Opticians
  else if (lower.includes('eye') || lower.includes('opti') || lower.includes('spectacle') || lower.includes('vision') || lower.includes('lens')) key = 'Eye';
  // 8. Pharmacy & Chemist
  else if (lower.includes('pharm') || lower.includes('chemist') || lower.includes('drug store') || lower.includes('medicine')) key = 'Pharmacy';
  // 9. Hospitals & Healthcare
  else if (lower.includes('health') || lower.includes('clinic') || lower.includes('doctor') || lower.includes('hospital') || lower.includes('medical') || lower.includes('physician') || lower.includes('diagnostic') || lower.includes('pathology') || lower.includes('scan') || lower.includes('ambulance') || lower.includes('nurse') || lower.includes('care') || lower.includes('veterinary')) key = 'Healthcare';
  // 10. Hotels & Resorts
  else if (lower.includes('hotel') || lower.includes('resort') || lower.includes('lodge') || lower.includes('stay') || lower.includes('5 star') || lower.includes('guest house') || lower.includes('homestay') || lower.includes('motel') || lower.includes('inn')) key = 'Hotel';
  // 11. Banquet & Marriage Halls
  else if (lower.includes('banquet') || lower.includes('marriage') || lower.includes('wedding') || lower.includes('mandap') || lower.includes('hall') || lower.includes('event organizer') || lower.includes('catering') || lower.includes('party')) key = 'Events';
  // 12. Jewellery
  else if (lower.includes('jewel') || lower.includes('gold') || lower.includes('diamond') || lower.includes('silver') || lower.includes('ornament')) key = 'Jewellery';
  // 13. Clothing, Textiles & Cloth Markets
  else if (lower.includes('cloth') || lower.includes('saree') || lower.includes('boutique') || lower.includes('tailor') || lower.includes('textile') || lower.includes('fashion') || lower.includes('garment') || lower.includes('dress') || lower.includes('fabric')) key = 'Clothing';
  // 14. Supermarkets, General Stores & Kirana
  else if (lower.includes('general store') || lower.includes('variety store') || lower.includes('departmental') || lower.includes('store') || lower.includes('supermarket') || lower.includes('grocery') || lower.includes('kirana') || lower.includes('provision') || lower.includes('mart') || lower.includes('shop')) key = 'Supermarket';
  // 15. Tyres & Wheel Alignment
  else if (lower.includes('tyre') || lower.includes('tire') || lower.includes('mrf') || lower.includes('ceat') || lower.includes('bridgestone') || lower.includes('apollo') || lower.includes('wheel')) key = 'Tyres';
  // 16. Water Purifiers & RO Services
  else if (lower.includes('water purifier') || lower.includes('ro water') || lower.includes('water filter') || lower.includes('aquaguard') || lower.includes('kent ro')) key = 'Water Purifier';
  // 17. Karate & Martial Arts
  else if (lower.includes('karate') || lower.includes('martial') || lower.includes('taekwondo') || lower.includes('judo') || lower.includes('boxing') || lower.includes('kung fu') || lower.includes('mma')) key = 'Martial Arts';
  // 18. Maid Placement & Domestic Staff
  else if (lower.includes('maid') || lower.includes('placement') || lower.includes('housekeeping') || lower.includes('baby sitter') || lower.includes('cook') || lower.includes('cleaning') || lower.includes('caretaker') || lower.includes('home service') || lower.includes('pest control')) key = 'Home Services';
  // 19. Electronics & Computers
  else if (lower.includes('electron') || lower.includes('mobile') || lower.includes('computer') || lower.includes('laptop') || lower.includes('cctv') || lower.includes('gadget') || lower.includes('phone')) key = 'Electronics';
  // 20. AC & HVAC
  else if (lower.includes('ac dealer') || lower.includes('air condition') || lower.includes('ac repair') || lower.includes('ac service') || lower.includes('ac install') || lower.includes('air cool') || lower.includes('refriger') || lower.includes('appliance') || lower.includes('hvac')) key = 'AC Dealers';
  // 21. Electricians
  else if (lower.includes('electric') || lower.includes('wiring') || lower.includes('electrician') || lower.includes('generator') || lower.includes('solar')) key = 'Electricians';
  // 22. Plumbers
  else if (lower.includes('plumb') || lower.includes('pipe') || lower.includes('sanit') || lower.includes('water tank') || lower.includes('drain')) key = 'Plumbers';
  // 23. Painters
  else if (lower.includes('paint') || lower.includes('colour') || lower.includes('wall coat') || lower.includes('waterproof')) key = 'Painters';
  // 24. Carpenters & Furniture
  else if (lower.includes('carpenter') || lower.includes('furniture') || lower.includes('woodwork') || lower.includes('cabinet') || lower.includes('sofa') || lower.includes('mattress')) key = 'Carpenters';
  // 25. Interior Designers & Architects
  else if (lower.includes('interior') || lower.includes('interior design') || lower.includes('decor') || lower.includes('renovation') || lower.includes('architect') || lower.includes('modular kitchen')) key = 'Interior';
  // 26. Real Estate & Builders
  else if (lower.includes('real estate') || lower.includes('property') || lower.includes('builder') || lower.includes('land') || lower.includes('flat') || lower.includes('apartment') || lower.includes('villa') || lower.includes('realtor') || lower.includes('plot')) key = 'Real Estate';
  // 27. Car Wash
  else if (lower.includes('car wash') || lower.includes('wash') || lower.includes('detailing') || lower.includes('car spa')) key = 'Car Wash';
  // 27.5 Tempo Travellers & Bus Rentals
  else if (lower.includes('tempo') || lower.includes('traveller') || lower.includes('minibus') || lower.includes('mini bus') || lower.includes('tourist bus') || lower.includes('seater') || lower.includes('bus rental') || lower.includes('tempo traveller')) key = 'Tempo';
  // 28. Automotive & Garage
  else if (lower.includes('auto') || lower.includes('tempo') || lower.includes('traveller') || lower.includes('cab') || lower.includes('taxi') || lower.includes('travel') || lower.includes('rental') || lower.includes('bus') || lower.includes('hire') || lower.includes('car service') || lower.includes('garage') || lower.includes('mechanic') || lower.includes('vehicle') || lower.includes('car repair') || lower.includes('two wheeler') || lower.includes('bike') || lower.includes('motorcycle') || lower.includes('driving school')) key = 'Automotive';
  // 29. Gym & Fitness
  else if (lower.includes('gym') || lower.includes('fitness') || lower.includes('yoga') || lower.includes('aerobic') || lower.includes('zumba') || lower.includes('crossfit') || lower.includes('workout')) key = 'Gym';
  // 30. Beauty, Salon & Spa
  else if (lower.includes('beauty') || lower.includes('salon') || lower.includes('spa') || lower.includes('parlour') || lower.includes('parlor') || lower.includes('grooming') || lower.includes('hair') || lower.includes('makeup') || lower.includes('massage')) key = 'Beauty';
  // 31. Advocates & Legal
  else if (lower.includes('law') || lower.includes('advocate') || lower.includes('legal') || lower.includes('ca ') || lower.includes('chartered') || lower.includes('accountant') || lower.includes('tax') || lower.includes('audit') || lower.includes('notary') || lower.includes('consultant')) key = 'Legal';
  // 32. Restaurants & Food
  else if (lower.includes('rest') || lower.includes('food') || lower.includes('dining') || lower.includes('biryani') || lower.includes('dhaba') || lower.includes('tiffin') || lower.includes('mess') || lower.includes('kitchen') || lower.includes('cuisine') || lower.includes('meals') || lower.includes('fast food') || lower.includes('burger') || lower.includes('pizza') || lower.includes('ice cream')) key = 'Restaurant';
  // 33. Cafe & Coffee
  else if (lower.includes('cafe') || lower.includes('coffee') || lower.includes('tea ') || lower.includes('chai') || lower.includes('beverage')) key = 'Cafe';
  // 34. Wine & Liquor
  else if (lower.includes('wine') || lower.includes('liquor') || lower.includes('beer') || lower.includes('tasmac') || lower.includes('bar ') || lower.includes('pub')) key = 'Wine Shops';
  // 35. Photography
  else if (lower.includes('photo') || lower.includes('studio') || lower.includes('camera') || lower.includes('video') || lower.includes('shoot')) key = 'Photo Studios';
  // 36. Schools & Education
  else if (lower.includes('school') || lower.includes('college') || lower.includes('tuition') || lower.includes('coaching') || lower.includes('education') || lower.includes('academy') || lower.includes('institute') || lower.includes('classes') || lower.includes('training')) key = 'Schools';
  // 37. Packers & Movers
  else if (lower.includes('packer') || lower.includes('mover') || lower.includes('shifting') || lower.includes('transport') || lower.includes('courier') || lower.includes('logistics') || lower.includes('cargo')) key = 'Packers';
  // 38. Pet Care & Vet
  else if (lower.includes('pet') || lower.includes('vet') || lower.includes('animal') || lower.includes('dog') || lower.includes('cat') || lower.includes('aquarium')) key = 'Pet';

  const pool = DIVERSE_CATEGORY_POOLS[key] || DIVERSE_CATEGORY_POOLS["Default"];
  const hash = seedString.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return pool[hash % pool.length];
}

export function getCategoryImage(cat?: string | null): string {
  return getCategorySpecificImage(cat, 'default');
}

export function getValidListingImage(l: Partial<Listing> | null | undefined): string {
  if (!l) return getCategoryImage(null);
  
  const hero = l.hero_image_url?.trim();
  const images = Array.isArray(l.images) ? l.images.filter(Boolean) : [];

  const isBrokenUrl = (url?: string | null) => {
    if (!url) return true;
    const lower = url.toLowerCase();
    return (
      lower.includes('placeholder') ||
      lower.includes('example.com') ||
      lower.includes('photoservice.getphoto') ||
      lower.includes('maps.googleapis.com') ||
      lower.includes('photo-1486006920555')
    );
  };

  if (hero && !isBrokenUrl(hero)) {
    return hero;
  }

  for (const img of images) {
    if (img && !isBrokenUrl(img)) {
      return img;
    }
  }

  return getCategorySpecificImage(l.category, l.name || l.slug || l.id || '');
}

function Home({ detectedCity, dbCategories = [] }: { detectedCity?: string; dbCategories?: string[] }) {
  const [featured, setFeatured] = useState<Listing[]>([]);
  const [eliteListings, setEliteListings] = useState<Listing[]>([]);
  const [cities, setCities] = useState<{ city: string; count: number }[]>([]);
  const [categories, setCategories] = useState<{ category: string; count: number }[]>([]);
  const [total, setTotal] = useState(0);

  const [q, setQ] = useState('');
  const [cityVal, setCityVal] = useState(detectedCity || '');

  useEffect(() => {
    if (detectedCity && !cityVal) {
      setCityVal(detectedCity);
    }
  }, [detectedCity]);

  const whatInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  // Dropdown Autocomplete States
  const [whatFocused, setWhatFocused] = useState(false);
  const [whereFocused, setWhereFocused] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [allListings, setAllListings] = useState<{ name: string; category: string; city: string; slug: string }[]>([]);

  const handleDetectLocation = async () => {
    setIsLocating(true);
    try {
      const res = await requestBrowserLocation();
      if (res && res.city) {
        setCityVal(res.city);
        setWhereFocused(false);
      } else {
        alert('Please allow location permission in your browser prompt to detect your city.');
      }
    } catch {
      alert('Location detection is unavailable on this device.');
    } finally {
      setIsLocating(false);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const [featuredRes, allListingsRes, eliteRes] = await Promise.all([
          listingsApi.getListings({ page_size: 8, sort_by: 'view_count', sort_order: 'desc' }),
          listingsApi.getListings({ page_size: 500 }),
          listingsApi.getListings({ is_elite: true, page_size: 6, status: 'published' }).catch(() => null)
        ]);
        if (eliteRes && eliteRes.items && eliteRes.items.length > 0) {
          setEliteListings(eliteRes.items as Listing[]);
        }
        if (featuredRes && featuredRes.items) {
          setFeatured(featuredRes.items as Listing[]);
          setTotal(featuredRes.total || 74894);
        }
        if (allListingsRes && allListingsRes.items) {
          setAllListings(allListingsRes.items.map((i: any) => ({ name: i.name, category: i.category, city: i.city, slug: i.slug || i.id })));
          const cm = new Map<string, number>();
          const cat = new Map<string, number>();
          allListingsRes.items.forEach((r: any) => {
            if (r.city) cm.set(r.city, (cm.get(r.city) ?? 0) + 1);
            if (r.category) cat.set(r.category, (cat.get(r.category) ?? 0) + 1);
          });
          setCities([...cm.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([city, count]) => ({ city, count })));
          setCategories([...cat.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([category, count]) => ({ category, count })));
          return;
        }
      } catch (e) {
        console.error('Failed to fetch home listings from FastAPI:', e);
      }

      // Fallback to Supabase if ever needed
      try {
        const [f, all, cnt, namesRes] = await Promise.all([
          supabase.from('listings').select('*').eq('status', 'published').order('view_count', { ascending: false }).limit(8),
          supabase.from('listings').select('city, category').eq('status', 'published').limit(500),
          supabase.from('listings').select('id', { count: 'exact', head: true }).eq('status', 'published'),
          supabase.from('listings').select('name, category, city, slug').eq('status', 'published').limit(1000)
        ]);
        setFeatured((f.data as Listing[]) ?? []);
        setTotal(cnt.count ?? 0);
        setAllListings((namesRes.data as any[]) ?? []);
        const rows = (all.data as { city: string | null; category: string | null }[]) ?? [];
        const cm = new Map<string, number>();
        const cat = new Map<string, number>();
        rows.forEach((r) => {
          if (r.city) cm.set(r.city, (cm.get(r.city) ?? 0) + 1);
          if (r.category) cat.set(r.category, (cat.get(r.category) ?? 0) + 1);
        });
        setCities([...cm.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([city, count]) => ({ city, count })));
        setCategories([...cat.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([category, count]) => ({ category, count })));
      } catch {}
    })();
  }, []);

  const submit = () => {
    if (!q.trim()) {
      whatInputRef.current?.focus();
      setWhatFocused(true);
      return;
    }
    if (!cityVal.trim()) {
      locationInputRef.current?.focus();
      setWhereFocused(true);
      return;
    }
    const catPlural = formatCategoryPlural(q.trim());
    const catSlug = slugify(catPlural);
    const citySlug = slugify(cityVal.trim());
    navigate(`/search/${catSlug}-in-${citySlug}`);
  };

  const searchCategory = (catName: string) => {
    setQ(catName);
    if (cityVal.trim()) {
      const catPlural = formatCategoryPlural(catName);
      const catSlug = slugify(catPlural);
      const citySlug = slugify(cityVal.trim());
      navigate(`/search/${catSlug}-in-${citySlug}`);
    } else {
      setTimeout(() => {
        locationInputRef.current?.focus();
        setWhereFocused(true);
      }, 50);
    }
  };

  const topTenTrending = useMemo(() => [
    'Restaurant',
    'Cafe',
    'Home Services',
    'Healthcare',
    'Beauty & Spa',
    'Real Estate',
    'Automotive',
    'Tech Support',
    'Dentist',
    'Plumber'
  ], []);

  const filteredCategories = useMemo(() => {
    if (!q.trim()) return [];
    const query = q.toLowerCase();
    const matches = new Set<string>();
    allListings.forEach(l => {
      if (l.category && l.category.toLowerCase().includes(query)) {
        matches.add(l.category);
      }
    });
    dbCategories.forEach(cat => {
      if (cat.toLowerCase().includes(query)) {
        matches.add(cat);
      }
    });
    topTenTrending.forEach(cat => {
      if (cat.toLowerCase().includes(query)) {
        matches.add(cat);
      }
    });
    return Array.from(matches).slice(0, 5);
  }, [q, allListings, dbCategories, topTenTrending]);

  const filteredListings = useMemo(() => {
    if (!q.trim()) return [];
    const query = q.toLowerCase();
    return allListings
      .filter(l => l.name.toLowerCase().includes(query))
      .slice(0, 5);
  }, [q, allListings]);

  const topTenCities = useMemo(() => [
    'Chennai',
    'Coimbatore',
    'Bengaluru',
    'Hyderabad',
    'Mumbai',
    'Pune',
    'Delhi NCR',
    'Kolkata',
    'Madurai',
    'Trichy',
    'Salem',
    'Ahmedabad',
    'Jaipur',
    'Kochi'
  ], []);

  const [showEliteFeatured, setShowEliteFeatured] = useState(() => {
    try {
      const saved = localStorage.getItem('localhub_show_elite_featured');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await supabase.from('settings').select('value').eq('key', 'show_elite_featured').maybeSingle();
        if (data && typeof data.value === 'boolean') {
          setShowEliteFeatured(data.value);
        }
      } catch {}
    })();
  }, []);

  const availableCities = useMemo(() => {
    const listCities = new Set<string>(cities.map(c => c.city));
    topTenCities.forEach(c => listCities.add(c));
    return Array.from(listCities);
  }, [cities, topTenCities]);

  const filteredCities = useMemo(() => {
    const query = cityVal.toLowerCase().trim();
    if (!query) return topTenCities;
    return availableCities
      .filter(c => c.toLowerCase().includes(query))
      .slice(0, 5);
  }, [cityVal, availableCities, topTenCities]);

  // Map dynamic cities
  const city1 = cities[0] || { city: 'Mumbai', count: 12450 };
  const city2 = cities[1] || { city: 'Delhi NCR', count: 8200 };
  const city3 = cities[2] || { city: 'Bengaluru', count: 9100 };
  const city4 = cities[3] || { city: 'Chennai', count: 5300 };

  // Map dynamic categories
  const cat1 = categories[0] || { category: 'Real Estate', count: 120 };
  const cat2 = categories[1] || { category: 'Healthcare', count: 85 };
  const cat3 = categories[2] || { category: 'Cafe', count: 95 };
  const cat4 = categories[3] || { category: 'Home Services', count: 110 };

  return (
    <div className="flex-grow w-full">
      {/* Hero Section */}
      <section className="relative min-h-[650px] flex flex-col items-center justify-center px-4 md:px-6 text-center py-12">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="w-full h-full bg-cover bg-center opacity-[0.08]" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80')" }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/80 to-background"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center gap-6 mt-12 md:mt-20">
          <span className="bg-white/80 border border-outline-variant/30 text-primary font-bold text-xs px-5 py-2 rounded-full shadow-md tracking-wider uppercase flex items-center gap-1.5">
            <span className="material-symbols-outlined text-sm text-emerald-600">verified</span>
            Verified Local Businesses Across India
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-on-background max-w-4xl font-extrabold leading-tight">
            Discover <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent font-extrabold">Exceptional Services</span> in your City.
          </h1>
          <p className="font-body-lg text-sm md:text-base text-on-surface-variant max-w-2xl font-semibold leading-relaxed">
            Connect with highly-rated professionals, exclusive restaurants, and premium services instantly. Trusted by millions.
          </p>

          {/* Search Floating Bar */}
          <div className="w-full max-w-4xl mt-6 bg-white/90 border border-outline-variant/30 rounded-2xl md:rounded-full p-2 md:p-2.5 shadow-2xl flex flex-col md:flex-row gap-2 relative z-20">
            {/* Input 1: What */}
            <div className="flex-grow flex items-center bg-surface-container-low px-4 rounded-xl md:rounded-full h-12 md:h-14 relative">
              <span className="material-symbols-outlined text-outline mr-2 text-lg">search</span>
              <input
                ref={whatInputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => setWhatFocused(true)}
                onBlur={() => setTimeout(() => setWhatFocused(false), 200)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                className="bg-transparent border-none outline-none w-full text-on-surface placeholder:text-outline-variant text-xs md:text-sm focus:ring-0"
                placeholder="What are you looking for?"
                type="text"
              />
              
              {/* Autocomplete suggestions dropdown */}
              {whatFocused && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-outline-variant/30 rounded-2xl shadow-2xl z-50 overflow-hidden text-left p-2 max-h-80 overflow-y-auto animate-fade-in">
                  {q.trim() === '' ? (
                    <div>
                      <div className="px-4 py-2 text-[10px] font-bold text-outline-variant uppercase tracking-wider">Top 10 Trending Categories</div>
                      {topTenTrending.map((cat) => (
                        <div
                          key={cat}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setQ(cat);
                            setWhatFocused(false);
                            if (cityVal.trim()) {
                              const catPlural = formatCategoryPlural(cat);
                              const catSlug = slugify(catPlural);
                              const citySlug = slugify(cityVal.trim());
                              navigate(`/search/${catSlug}-in-${citySlug}`);
                            } else {
                              setTimeout(() => {
                                locationInputRef.current?.focus();
                                setWhereFocused(true);
                              }, 50);
                            }
                          }}
                          className="w-full px-4 py-2.5 hover:bg-surface-container rounded-xl flex items-center gap-3 transition-colors cursor-pointer text-xs md:text-sm font-bold text-on-surface"
                        >
                          <span className="material-symbols-outlined text-outline text-lg">trending_up</span>
                          <span>{cat}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      {filteredCategories.length > 0 && (
                        <div className="mb-2">
                          <div className="px-4 py-1.5 text-[10px] font-bold text-outline-variant uppercase tracking-wider">Categories</div>
                          {filteredCategories.map((cat) => (
                            <div
                              key={cat}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setQ(cat);
                                setWhatFocused(false);
                                if (cityVal.trim()) {
                                  const catPlural = formatCategoryPlural(cat);
                                  const catSlug = slugify(catPlural);
                                  const citySlug = slugify(cityVal.trim());
                                  navigate(`/search/${catSlug}-in-${citySlug}`);
                                } else {
                                  setTimeout(() => {
                                    locationInputRef.current?.focus();
                                    setWhereFocused(true);
                                  }, 50);
                                }
                              }}
                              className="w-full px-4 py-2.5 hover:bg-surface-container rounded-xl flex items-center gap-3 transition-colors cursor-pointer text-xs md:text-sm font-bold text-on-surface"
                            >
                              <span className="material-symbols-outlined text-outline text-lg">tag</span>
                              <span>{cat}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {filteredListings.length > 0 && (
                        <div>
                          <div className="px-4 py-1.5 text-[10px] font-bold text-outline-variant uppercase tracking-wider">Businesses</div>
                          {filteredListings.map((l) => (
                            <div
                              key={l.slug}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setWhatFocused(false);
                                navigate(`/b/${l.slug}`);
                              }}
                              className="w-full px-4 py-2.5 hover:bg-surface-container rounded-xl flex items-center justify-between gap-3 transition-colors cursor-pointer text-xs md:text-sm font-bold text-on-surface"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <span className="material-symbols-outlined text-outline text-lg">storefront</span>
                                <span className="truncate">{l.name}</span>
                              </div>
                              <span className="text-[10px] text-on-surface-variant font-medium shrink-0 bg-surface-container px-2 py-0.5 rounded">{l.city}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {filteredCategories.length === 0 && filteredListings.length === 0 && (
                        <div className="px-4 py-6 text-center text-xs text-on-surface-variant font-semibold">No matches found for "{q}"</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="hidden md:block w-[1px] bg-outline-variant/40 my-3"></div>
            
            {/* Input 2: Location */}
            <div className="flex-grow flex items-center bg-surface-container-low px-4 rounded-xl md:rounded-full h-12 md:h-14 relative">
              <span className="material-symbols-outlined text-outline mr-2 text-lg text-emerald-700">location_on</span>
              <input
                ref={locationInputRef}
                value={cityVal}
                onChange={(e) => setCityVal(e.target.value)}
                onFocus={() => setWhereFocused(true)}
                onBlur={() => setTimeout(() => setWhereFocused(false), 200)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                className="bg-transparent border-none outline-none w-full text-on-surface placeholder:text-outline-variant text-xs md:text-sm focus:ring-0"
                placeholder="City or Neighborhood"
                type="text"
              />
              
              {/* Location Suggestions Dropdown */}
              {whereFocused && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-outline-variant/30 rounded-2xl shadow-2xl z-50 overflow-hidden text-left p-2 max-h-80 overflow-y-auto animate-fade-in">
                  {cityVal.trim() === '' ? (
                    <div>
                      <div className="px-4 py-2 text-[10px] font-bold text-outline-variant uppercase tracking-wider">Verified Cities in Database</div>
                      {topTenCities.map((city) => (
                        <div
                          key={city}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setCityVal(city);
                            setWhereFocused(false);
                            if (q.trim()) {
                              const catPlural = formatCategoryPlural(q.trim());
                              const catSlug = slugify(catPlural);
                              const citySlug = slugify(city);
                              navigate(`/search/${catSlug}-in-${citySlug}`);
                            } else {
                              setTimeout(() => {
                                whatInputRef.current?.focus();
                                setWhatFocused(true);
                              }, 50);
                            }
                          }}
                          className="w-full px-4 py-2.5 hover:bg-surface-container rounded-xl flex items-center gap-3 transition-colors cursor-pointer text-xs md:text-sm font-bold text-on-surface"
                        >
                          <span className="material-symbols-outlined text-outline text-lg">location_on</span>
                          <span>{city}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <div className="px-4 py-1.5 text-[10px] font-bold text-outline-variant uppercase tracking-wider">Cities</div>
                      {filteredCities.map((city) => (
                        <div
                          key={city}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setCityVal(city);
                            setWhereFocused(false);
                            if (q.trim()) {
                              const catPlural = formatCategoryPlural(q.trim());
                              const catSlug = slugify(catPlural);
                              const citySlug = slugify(city);
                              navigate(`/search/${catSlug}-in-${citySlug}`);
                            } else {
                              setTimeout(() => {
                                whatInputRef.current?.focus();
                                setWhatFocused(true);
                              }, 50);
                            }
                          }}
                          className="w-full px-4 py-2.5 hover:bg-surface-container rounded-xl flex items-center gap-3 transition-colors cursor-pointer text-xs md:text-sm font-bold text-on-surface"
                        >
                          <span className="material-symbols-outlined text-outline text-lg">location_on</span>
                          <span>{city}</span>
                        </div>
                      ))}
                      {filteredCities.length === 0 && (
                        <div className="px-4 py-6 text-center text-xs text-on-surface-variant font-semibold">No matching cities found</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <button
              onClick={submit}
              className="bg-primary text-on-primary h-12 md:h-14 px-6 md:px-8 rounded-xl md:rounded-full hover:bg-primary-container hover:shadow-lg hover:shadow-primary/30 transition-all active:scale-95 flex items-center justify-center gap-2 font-bold text-xs md:text-sm cursor-pointer shrink-0"
            >
              <span>Search</span>
              <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>

          {/* Essential Categories Row */}
          <div className="flex flex-wrap justify-center gap-2 mt-4 items-center">
            <span className="text-on-surface-variant font-bold text-xs mr-2">Essential Categories:</span>
            {categories.slice(0, 5).map((cat) => (
              <button
                key={cat.category}
                onClick={() => searchCategory(cat.category)}
                className="bg-white/80 border border-outline-variant/30 text-on-surface text-xs px-4 py-1.5 rounded-full hover:bg-primary hover:text-on-primary transition-all shadow-sm cursor-pointer font-semibold flex items-center gap-1.5 animate-fade-in"
              >
                <span className="material-symbols-outlined text-[14px]">{categoryIcons[cat.category] || 'sell'}</span>
                <span>{cat.category}</span>
              </button>
            ))}
            {categories.length === 0 && (
              <>
                <button onClick={() => searchCategory('Cafe')} className="bg-white/80 border border-outline-variant/30 text-on-surface text-xs px-4 py-1.5 rounded-full hover:bg-primary hover:text-on-primary transition-all shadow-sm cursor-pointer font-semibold flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">local_cafe</span><span>Cafe</span>
                </button>
                <button onClick={() => searchCategory('Restaurant')} className="bg-white/80 border border-outline-variant/30 text-on-surface text-xs px-4 py-1.5 rounded-full hover:bg-primary hover:text-on-primary transition-all shadow-sm cursor-pointer font-semibold flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">restaurant</span><span>Restaurant</span>
                </button>
                <button onClick={() => searchCategory('Home Services')} className="bg-white/80 border border-outline-variant/30 text-on-surface text-xs px-4 py-1.5 rounded-full hover:bg-primary hover:text-on-primary transition-all shadow-sm cursor-pointer font-semibold flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">construction</span><span>Home Services</span>
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Popular Searches in [City] */}
      <section className="bg-surface-container-lowest py-16 border-y border-outline-variant/10">
        <div className="max-w-container-max mx-auto px-4 md:px-6">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-on-background font-extrabold tracking-tight">
                Popular Searches in {cityVal.trim() || detectedCity || 'Chennai'}
              </h2>
              <p className="font-body-md text-on-surface-variant mt-1 text-sm">Explore top verified local services in {cityVal.trim() || detectedCity || 'Chennai'}</p>
            </div>
            <a {...linkProps('/search')} className="text-primary hover:underline flex items-center gap-1 font-semibold text-xs">
              View All <span className="material-symbols-outlined text-sm">arrow_right_alt</span>
            </a>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Cafes & Dining',
                category: 'Cafe',
                image: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=600&q=80',
                count: categories.find(c => c.category === 'Cafe')?.count || 12
              },
              {
                title: 'Luxury Restaurants',
                category: 'Restaurant',
                image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=600&q=80',
                count: categories.find(c => c.category === 'Restaurant')?.count || 24
              },
              {
                title: 'Home Maintenance',
                category: 'Home Services',
                image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=600&q=80',
                count: categories.find(c => c.category === 'Home Services')?.count || 18
              },
              {
                title: 'Healthcare Clinics',
                category: 'Healthcare',
                image: 'https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=600&q=80',
                count: categories.find(c => c.category === 'Healthcare')?.count || 15
              }
            ].map((item, idx) => (
              <div
                key={idx}
                onClick={() => searchCategory(item.category)}
                className="group flex flex-col bg-white border border-outline-variant/20 rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer"
              >
                <div className="relative aspect-video w-full overflow-hidden bg-surface-container">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5 flex-grow flex flex-col justify-between gap-4 bg-gradient-to-b from-white to-surface-container-low">
                  <div>
                    <h3 className="font-display text-on-background text-base font-extrabold group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-[11px] font-bold text-on-surface-variant mt-1 uppercase tracking-wider">{item.category} Category</p>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs font-semibold text-outline">{item.count} listings</span>
                    <button className="bg-primary hover:bg-primary-container text-on-primary hover:text-on-primary-container font-bold text-[11px] px-4 py-2 rounded-xl transition-all flex items-center gap-1">
                      Explore
                      <span className="material-symbols-outlined text-[12px]">arrow_forward</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Across [City] (Top 10 Geo-Targeted City Listings) */}
      <section className="max-w-container-max mx-auto px-4 md:px-6 py-16">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-on-background font-extrabold tracking-tight">
              Trending Across {cityVal.trim() || detectedCity || 'Chennai'}
            </h2>
            <p className="font-body-md text-on-surface-variant mt-1 text-xs font-semibold">
              Top verified services & businesses in {cityVal.trim() || detectedCity || 'Chennai'}
            </p>
          </div>
          <a {...linkProps('/search')} className="text-primary hover:underline flex items-center gap-1 font-bold text-xs">
            View All <span className="material-symbols-outlined text-sm">arrow_right_alt</span>
          </a>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {featured.slice(0, 10).map((l) => (
            <a
              {...linkProps(`/b/${l.slug}`)}
              key={l.id}
              className="group flex flex-col bg-white border border-outline-variant/20 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-emerald-600 transition-all duration-300"
            >
              {/* Card Image Cover */}
              <div className="relative aspect-video w-full overflow-hidden bg-surface-container">
                <img
                  src={getValidListingImage(l)}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (!target.dataset.fallbackApplied) {
                      target.dataset.fallbackApplied = 'true';
                      target.src = getCategoryImage(l.category);
                    }
                  }}
                  alt={l.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-2.5 right-2.5 bg-white/95 backdrop-blur-md px-2 py-0.5 rounded-full border border-outline-variant/20 flex items-center gap-1 shadow-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                  <span className="text-[9px] font-extrabold text-emerald-800 uppercase">VERIFIED</span>
                </div>
              </div>

              {/* Details Container */}
              <div className="p-3.5 flex-grow flex flex-col justify-between gap-2">
                <div>
                  <div className="flex justify-between items-start mb-1 gap-1">
                    <h3 className="font-display text-slate-900 text-xs font-extrabold group-hover:text-emerald-700 transition-colors truncate">
                      {l.name}
                    </h3>
                    <div className="flex items-center gap-0.5 bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-md shrink-0">
                      <span className="material-symbols-outlined text-amber-500 text-[10px]" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                      <span className="text-[10px] font-extrabold">5.0</span>
                    </div>
                  </div>
                  <p className="text-slate-500 text-[10px] line-clamp-2 leading-relaxed">
                    {l.ai_summary || l.description}
                  </p>
                </div>

                <div className="flex items-center justify-between text-[10px] text-slate-600 font-extrabold border-t border-slate-100 pt-2">
                  <span className="flex items-center gap-0.5 text-emerald-800">
                    <span className="material-symbols-outlined text-[12px]">location_on</span>
                    {l.city || cityVal || detectedCity || 'Chennai'}
                  </span>
                  <span className="bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-full text-[9px] font-extrabold">
                    {l.category}
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Upcoming Events in [City] (Automated Bot Event Discovery) */}
      <section className="w-full bg-emerald-950 text-white py-16 relative border-y border-emerald-900/50 box-border">
        <div className="absolute inset-0 bg-radial from-emerald-900/40 via-transparent to-transparent pointer-events-none"></div>
        <div className="max-w-container-max mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 box-border">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4 w-full">
            <div className="max-w-2xl">
              <h2 className="font-display text-2xl md:text-3xl text-white font-extrabold tracking-tight leading-snug">
                Upcoming Events in {cityVal.trim() || detectedCity || 'Chennai'}
              </h2>
              <p className="text-emerald-200/80 text-xs md:text-sm font-semibold mt-1">
                Real-time tech expos, food festivals, summits, and cultural shows in {cityVal.trim() || detectedCity || 'Chennai'}
              </p>
            </div>
            <a href="/owner#events" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs px-5 py-2.5 rounded-xl transition-all shadow-lg flex items-center gap-1.5 cursor-pointer shrink-0">
              <span className="material-symbols-outlined text-sm">add_circle</span>
              <span>Host / Post Event</span>
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
            {getUpcomingEventsForCity(cityVal.trim() || detectedCity || 'Chennai').map((evt) => (
              <div key={evt.id} className="bg-emerald-900/40 border border-emerald-800/60 rounded-3xl overflow-hidden hover:border-emerald-400 transition-all duration-300 flex flex-col justify-between group shadow-xl">
                <div>
                  <div className="relative aspect-video w-full overflow-hidden bg-slate-900">
                    <img
                      src={evt.image}
                      alt={evt.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-90"
                    />
                    <div className="absolute top-3 left-3 bg-emerald-500 text-slate-950 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full shadow-sm">
                      {evt.price}
                    </div>
                    <div className="absolute top-3 right-3 bg-slate-900/80 backdrop-blur-md text-emerald-300 text-[10px] font-extrabold px-2 py-0.5 rounded-full border border-emerald-700/50">
                      {evt.category}
                    </div>
                  </div>

                  <div className="p-5 space-y-3">
                    <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-emerald-400">
                      <span className="material-symbols-outlined text-sm">calendar_month</span>
                      <span>{evt.date}</span>
                    </div>

                    <h3 className="font-display text-base font-extrabold text-white group-hover:text-emerald-300 transition-colors leading-snug">
                      {evt.title}
                    </h3>

                    <p className="text-emerald-100/70 text-xs line-clamp-2 leading-relaxed font-medium">
                      {evt.description}
                    </p>

                    <div className="pt-2 border-t border-emerald-800/50 space-y-1.5 text-[11px] text-emerald-200/90 font-semibold">
                      <div className="flex items-center gap-1.5 truncate">
                        <span className="material-symbols-outlined text-xs text-emerald-400">schedule</span>
                        <span>{evt.time}</span>
                      </div>
                      <div className="flex items-center gap-1.5 truncate">
                        <span className="material-symbols-outlined text-xs text-emerald-400">location_on</span>
                        <span>{evt.venue}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 pt-0">
                  <button
                    onClick={() => alert(`RSVP request recorded for "${evt.title}" in ${evt.city}. Event details sent to host ${evt.organizer}.`)}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs py-2.5 rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-md cursor-pointer"
                  >
                    <span>Reserve Free Spot / Info</span>
                    <span className="material-symbols-outlined text-xs">arrow_forward</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Elite Featured Listings (Super Admin Toggleable) */}
      {showEliteFeatured && (
        <section className="bg-surface-container-low py-16 border-y border-outline-variant/20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent"></div>
          <div className="max-w-container-max mx-auto px-4 md:px-6 relative z-10">
            <div className="flex flex-col items-center text-center mb-10">
              <span className="text-tertiary font-bold text-xs tracking-widest uppercase mb-1 flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">diamond</span> Premium Collection
              </span>
              <h2 className="font-display text-2xl md:text-3xl text-on-background font-extrabold tracking-tight">Elite Featured Businesses</h2>
              <p className="font-body-md text-on-surface-variant mt-1 max-w-2xl text-xs font-semibold">Curated selections of the highest quality services and establishments.</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {(eliteListings.length > 0 ? eliteListings.slice(0, 4) : featured.slice(0, 2)).map((l) => (
                <div key={l.id} className="flex flex-col md:flex-row bg-white rounded-3xl overflow-hidden border border-outline-variant/25 shadow-md hover:shadow-xl transition-all duration-300 group">
                  <div className="w-full md:w-2/5 aspect-video md:aspect-auto relative overflow-hidden bg-surface-container">
                    <img
                      src={getValidListingImage(l)}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (!target.dataset.fallbackApplied) {
                          target.dataset.fallbackApplied = 'true';
                          target.src = getCategoryImage(l.category);
                        }
                      }}
                      alt={l.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 bg-yellow-100/90 backdrop-blur-md text-yellow-800 p-2 rounded-full border border-yellow-200 shadow-sm">
                      <span className="material-symbols-outlined text-sm block">workspace_premium</span>
                    </div>
                  </div>
                  <div className="w-full md:w-3/5 p-6 md:p-8 flex flex-col justify-center gap-3">
                    <div>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-secondary mb-1">
                        <span className="material-symbols-outlined text-xs">verified</span> Premium Partner
                      </div>
                      <h3 className="font-display text-lg font-extrabold text-on-background group-hover:text-primary transition-colors">
                        {l.name}
                      </h3>
                    </div>
                    <p className="text-on-surface-variant text-xs line-clamp-3 leading-relaxed">
                      {l.ai_summary || l.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="bg-surface-container px-2.5 py-1 rounded-full text-[9px] font-bold text-on-surface-variant">{l.category}</span>
                      <span className="bg-surface-container px-2.5 py-1 rounded-full text-[9px] font-bold text-on-surface-variant">{l.city}</span>
                    </div>
                    <a
                      {...linkProps(`/b/${l.slug}`)}
                      className="bg-primary hover:bg-primary-container text-on-primary font-bold text-xs py-2.5 px-5 rounded-xl transition-all self-start flex items-center gap-1 mt-2 cursor-pointer"
                    >
                      View Profile ↗
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Discover Your City */}
      <section className="max-w-container-max mx-auto px-4 md:px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="font-display text-2xl md:text-3xl text-on-background font-extrabold tracking-tight">Discover Your City</h2>
          <p className="font-body-md text-on-surface-variant mt-1 text-sm font-semibold">Explore verified services in major Indian metropolitan areas</p>
        </div>
        
        {/* Top 5 Cities Row */}
        <div className="mb-6">
          <h3 className="text-xs font-extrabold text-outline-variant uppercase tracking-wider mb-4">Top Metro Hubs</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {[
              { city: 'Mumbai', image: 'https://images.unsplash.com/photo-1566552881560-0be862a7c445?auto=format&fit=crop&w=400&q=80' },
              { city: 'Delhi NCR', image: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?auto=format&fit=crop&w=400&q=80' },
              { city: 'Bengaluru', image: 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?auto=format&fit=crop&w=400&q=80' },
              { city: 'Chennai', image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&w=400&q=80' },
              { city: 'Hyderabad', image: 'https://images.unsplash.com/photo-1605389477189-9a7fdfa76f62?auto=format&fit=crop&w=600&q=80' }
            ].map((item) => {
              const count = cities.find(c => c.city.toLowerCase() === item.city.toLowerCase())?.count || 15;
              return (
                <a
                  {...linkProps(`/c/${encodeURIComponent(item.city)}`)}
                  key={item.city}
                  className="group relative rounded-2xl overflow-hidden shadow-md aspect-[3/4] cursor-pointer"
                >
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url('${item.image}')` }}></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-primary/90 transition-colors duration-500"></div>
                  <div className="absolute bottom-0 left-0 p-4 w-full">
                    <h4 className="font-display text-base text-white font-extrabold truncate">{item.city}</h4>
                    <p className="text-white/80 text-[10px] flex items-center gap-1 mt-0.5"><span className="material-symbols-outlined text-xs">storefront</span> {count} Listings</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        {/* Next 5 Cities Row */}
        <div>
          <h3 className="text-xs font-extrabold text-outline-variant uppercase tracking-wider mb-4 mt-8">Growing Cities</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {[
              { city: 'Pune', image: 'https://images.unsplash.com/photo-1625841029272-35805e7b233a?auto=format&fit=crop&w=600&q=80' },
              { city: 'Kolkata', image: 'https://images.unsplash.com/photo-1558431382-27e303142255?auto=format&fit=crop&w=600&q=80' },
              { city: 'Ahmedabad', image: 'https://images.unsplash.com/photo-1603262110263-fb0112e7cc33?auto=format&fit=crop&w=600&q=80' },
              { city: 'Jaipur', image: 'https://images.unsplash.com/photo-1599661046827-dacff0c0f09a?auto=format&fit=crop&w=600&q=80' },
              { city: 'Kochi', image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&w=600&q=80' }
            ].map((item) => {
              const count = cities.find(c => c.city.toLowerCase() === item.city.toLowerCase())?.count || 8;
              return (
                <a
                  {...linkProps(`/c/${encodeURIComponent(item.city)}`)}
                  key={item.city}
                  className="group relative rounded-2xl overflow-hidden shadow-md aspect-[3/4] cursor-pointer"
                >
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url('${item.image}')` }}></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-secondary/90 transition-colors duration-500"></div>
                  <div className="absolute bottom-0 left-0 p-4 w-full">
                    <h4 className="font-display text-base text-white font-extrabold truncate">{item.city}</h4>
                    <p className="text-white/80 text-[10px] flex items-center gap-1 mt-0.5"><span className="material-symbols-outlined text-xs">storefront</span> {count} Listings</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>



      {/* Trusted Reviews */}
      <section className="max-w-container-max mx-auto px-4 md:px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="font-display text-2xl md:text-3xl text-on-background font-extrabold tracking-tight">Trusted by Millions</h2>
          <p className="font-body-md text-on-surface-variant mt-1 text-sm">Real experiences from our community</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Review 1 */}
          <div className="bg-surface-container-lowest p-8 rounded-3xl shadow-lg border border-outline-variant/20 relative flex flex-col justify-between">
            <span className="material-symbols-outlined absolute top-6 right-6 text-4xl text-primary/10">format_quote</span>
            <div>
              <div className="flex gap-0.5 mb-4 text-yellow-500">
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
              </div>
              <p className="font-body-md text-on-surface-variant italic mb-6 text-xs leading-relaxed">
                "Found the best interior designer for my new apartment within minutes. The verified badges gave me confidence in my choice. Exceptional platform!"
              </p>
            </div>
            <div className="flex items-center gap-4 border-t border-outline-variant/10 pt-4 mt-2">
              <div className="w-10 h-10 rounded-full bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&q=80')" }}></div>
              <div>
                <h4 className="font-display font-bold text-on-background text-sm">Priya Sharma</h4>
                <p className="text-on-surface-variant text-[11px]">Mumbai, MH</p>
              </div>
            </div>
          </div>

          {/* Review 2 */}
          <div className="bg-surface-container-lowest p-8 rounded-3xl shadow-lg border border-outline-variant/20 relative flex flex-col justify-between">
            <span className="material-symbols-outlined absolute top-6 right-6 text-4xl text-secondary/10">format_quote</span>
            <div>
              <div className="flex gap-0.5 mb-4 text-yellow-500">
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
              </div>
              <p className="font-body-md text-on-surface-variant italic mb-6 text-xs leading-relaxed">
                "As a business owner, listing on Zha Search changed everything. The quality of leads is premium and the dashboard is incredibly easy to use."
              </p>
            </div>
            <div className="flex items-center gap-4 border-t border-outline-variant/10 pt-4 mt-2">
              <div className="w-10 h-10 rounded-full bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&q=80')" }}></div>
              <div>
                <h4 className="font-display font-bold text-on-background text-sm">Rahul Verma</h4>
                <p className="text-on-surface-variant text-[11px]">Cafe Owner, Delhi</p>
              </div>
            </div>
          </div>

          {/* Review 3 */}
          <div className="bg-surface-container-lowest p-8 rounded-3xl shadow-lg border border-outline-variant/20 relative flex flex-col justify-between">
            <span className="material-symbols-outlined absolute top-6 right-6 text-4xl text-tertiary/10">format_quote</span>
            <div>
              <div className="flex gap-0.5 mb-4 text-yellow-500">
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm">star_half</span>
              </div>
              <p className="font-body-md text-on-surface-variant italic mb-6 text-xs leading-relaxed">
                "Saved me hours of searching for a reliable plumber during an emergency. The direct call feature is brilliant. Highly recommended app."
              </p>
            </div>
            <div className="flex items-center gap-4 border-t border-outline-variant/10 pt-4 mt-2">
              <div className="w-10 h-10 rounded-full bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&q=80')" }}></div>
              <div>
                <h4 className="font-display font-bold text-on-background text-sm">Anita Desai</h4>
                <p className="text-on-surface-variant text-[11px]">Bengaluru, KA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner with Zha Search CTA */}
      <section className="max-w-container-max mx-auto px-4 md:px-6 py-8 mb-16">
        <div className="bg-inverse-surface rounded-[2.5rem] p-8 md:p-16 flex flex-col md:flex-row items-center justify-between gap-12 relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="w-full md:w-3/5 relative z-10 text-center md:text-left">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-inverse-on-surface mb-6 leading-tight">Grow your business with India's Elite Directory.</h2>
            <p className="font-body-md text-surface-variant text-base mb-8 max-w-xl">Join thousands of premium businesses already reaching high-intent customers every day. Get verified, get noticed.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <a {...linkProps('/owner')} className="bg-primary text-on-primary font-bold text-sm px-8 py-4 rounded-full text-center hover:bg-primary-fixed hover:text-on-primary-fixed transition-colors shadow-lg hover:shadow-primary/50">
                List Your Business
              </a>
              <a {...linkProps('/')} className="bg-transparent border border-outline-variant/50 text-inverse-on-surface font-bold text-sm px-8 py-4 rounded-full text-center hover:bg-white/10 transition-colors">
                Learn More
              </a>
            </div>
          </div>
          <div className="w-full md:w-2/5 relative z-10 hidden md:block">
            <div className="bg-surface/10 border border-white/10 backdrop-blur-xl rounded-3xl p-6 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                  <span className="material-symbols-outlined text-on-primary">trending_up</span>
                </div>
                <div>
                  <p className="text-surface-variant text-xs">Monthly Views</p>
                  <p className="text-inverse-on-surface font-display font-bold text-xl">45.2K</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="h-2 bg-surface/20 rounded-full w-full overflow-hidden">
                  <div className="h-full bg-primary w-3/4 rounded-full"></div>
                </div>
                <div className="h-2 bg-surface/20 rounded-full w-full overflow-hidden">
                  <div className="h-full bg-secondary w-1/2 rounded-full"></div>
                </div>
                <div className="h-2 bg-surface/20 rounded-full w-full overflow-hidden">
                  <div className="h-full bg-tertiary w-5/6 rounded-full"></div>
                </div>
              </div>
              <div className="mt-6 flex justify-between items-center text-xs text-surface-variant">
                <span>Performance</span>
                <span className="text-primary-fixed-dim font-bold flex items-center"><span className="material-symbols-outlined text-xs mr-1">arrow_upward</span> 24%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Directory Tags footer-style section (Image 3 & 4 style) */}
      <section className="bg-surface-container-lowest border-t border-outline-variant/20 py-16 text-xs text-on-surface-variant font-medium">
        <div className="max-w-container-max mx-auto px-6 space-y-12">
          
          {/* Section 3: Popular Categories & Trending Searches (Image 3) */}
          <div className="space-y-6">
            <h3 className="font-display font-extrabold text-sm text-on-background uppercase tracking-wider">Popular Categories</h3>
            
            {/* Category tabs */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['Accommodation', 'Astrology', 'Automobiles', 'Beauty & Fitness', 'Business & Legal', 'Education', 'Events & Weddings', 'Food & Restaurants'].map((tab, idx) => (
                <span key={idx} className="bg-surface-container px-3 py-1.5 rounded-lg text-[11px] font-semibold text-on-surface-variant cursor-pointer hover:bg-primary hover:text-on-primary transition-all duration-200">
                  {tab}
                </span>
              ))}
            </div>

            {/* Category tag links */}
            <div className="flex flex-wrap items-center gap-y-2.5 text-[11px] leading-relaxed text-outline-variant max-w-5xl font-medium">
              {Array.from(new Set([
                'AC Lodging Services', 'Beach Resorts', 'Bungalows On Hire', 'Cottages On Hire', 'Dharamshalas', 'Farm House', 'Farm House On Hire', 'Government Hostels', 'Guest House', 'Hostels', 'Hostels For Men', 'Hostels For Women',
                'Hostels For Working Men', 'Hostels For Working Women', 'Hotels', 'Lodging Services', 'Resorts', 'Dormitory Services', 'Home Stay', 'Hostel For Girl Students', '3 Star Hotels', '5 Star Hotels', '2 Star Hotels',
                '4 Star Hotels', 'Villas', 'Villas On Hire', 'Rooms On Hire', 'AC Dormitory Services', 'Pet Friendly Resorts', 'Hostel For Boy Students', 'Village Resorts', 'AC Guest House', 'Paying Guest Accommodations For Women', 'Water Park Resorts',
                'Hostel For Students', 'Couple Friendly Hotels', 'Paying Guest Accommodations for Student', 'Government Hostels for Women', 'AC Paying Guest Accommodations', 'AC Paying Guest Accommodations For Men',
                'AC Paying Guest Accommodations For Women', 'Paying Guest Accommodations For Jagran', 'Paying Guest Accommodations For Girl Student', 'Unisex Paying Guest Accommodations', 'Single Occupancy Paying Guest Accommodations', 'Mansions', 'Couple Paying Guest Accommodations',
                ...dbCategories
              ])).map((tag, idx, arr) => (
                <span key={idx} className="whitespace-nowrap flex items-center">
                  <a href={`/search/${encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-'))}/${encodeURIComponent((cityVal || 'Bangalore').toLowerCase().replace(/\s+/g, '-'))}`} className="hover:text-primary transition-colors">{tag}</a>
                  {idx < arr.length - 1 && <span className="mx-2 text-outline-variant/40">|</span>}
                </span>
              ))}
            </div>

            <div className="pt-6 border-t border-outline-variant/10">
              <h3 className="font-display font-extrabold text-sm text-on-background uppercase tracking-wider mb-4">Trending Searches</h3>
              <div className="flex flex-wrap items-center gap-y-2.5 text-[11px] leading-relaxed text-outline-variant max-w-5xl font-medium">
                {[
                  'English Medium Schools', 'Packers And Movers', 'Home Delivery Restaurants', 'Estate Agents For Land', 'Wedding Photographers', 'Income Tax Consultants', 'Newspaper Advertising Agencies', 'Hepatologist Doctors',
                  'Search Engine Optimization Services', 'Motorcycle Repair & Services', 'Tyre Dealers', 'Tutorials For SSC CGL', 'Bitcoin Services', 'Tour Packages For Goa', 'Transporters For Kolkata', 'Tour Packages For Manali',
                  'Transporters For Bihar', 'Pet Food Dealers', 'Event Organisers For Jagran', 'Tutorials For UGC NET Exam', 'Battery Operated Scooter Dealers', 'Courier Services For USA', 'Transporters For Rajasthan', 'MCA Institutes',
                  'Tutorials For Ctet', 'Share Brokers', 'Transporters For Punjab', 'LPG Conversion Kit Dealers', 'Event Organisers For Bhajan Sandhya', 'Bhojpuri Film Producers', 'Courier Services For Dubai', 'Khatu Shyam Bhajan Singers',
                  'Dairy Product Retailers', 'Bengali Sweet Retailers', 'Ayurvedic Doctors For Hair Fall Treatment', 'Overseas Education Consultants', 'Tutorials For NIOS Class XII', 'Packers And Movers For Hyderabad',
                  'Dish Antenna Installation Services', 'Event Organisers For DJ', 'Personal Loans', 'Car Rental', 'Marathi Books', 'Women Top Retailers', 'HD Makeup Artists', 'Cricket T Shirt Manufacturers', 'Ad Film Makers',
                  'ABC Fire Extinguisher Dealers', 'Solar Panel Dealers', 'Kick Scooter Dealers'
                ].map((tag, idx, arr) => (
                  <span key={idx} className="whitespace-nowrap flex items-center">
                    <a href={`/search/${encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-'))}/${encodeURIComponent((cityVal || 'Bangalore').toLowerCase().replace(/\s+/g, '-'))}`} className="hover:text-primary transition-colors">{tag}</a>
                    {idx < arr.length - 1 && <span className="mx-2 text-outline-variant/40">|</span>}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Section 4: Popular Cities (Image 4) */}
          <div className="pt-8 border-t border-outline-variant/10">
            <h3 className="font-display font-extrabold text-sm text-on-background uppercase tracking-wider mb-4">Popular Cities</h3>
            <div className="flex flex-wrap items-center gap-y-2.5 text-[11px] leading-relaxed text-outline-variant max-w-5xl font-medium">
              {[
                'Bangalore', 'Mumbai', 'Chennai', 'Delhi', 'Hyderabad', 'Pune', 'Ahmedabad', 'Lucknow', 'Patna', 'Jaipur', 'Indore', 'Kochi', 'Kolkata', 'Coimbatore', 'Nagpur', 'Ludhiana', 'Agra', 'Bhubaneshwar', 'Bhopal', 'Guwahati', 'Surat',
                'Madurai', 'Visakhapatnam', 'Sonepat', 'Vadodara', 'Meerut', 'Thiruvananthapuram', 'Gurgaon', 'Kozhikode', 'Varanasi', 'Siliguri', 'Allahabad', 'Rajkot', 'Ghaziabad', 'Mysore', 'Noida', 'Chandigarh', 'Navi Mumbai', 'Vijayawada',
                'Durgapur', 'Srinagar', 'Nashik', 'Panipat', 'Jammu', 'Jodhpur', 'Thane', 'Raipur', 'Amritsar', 'Jabalpur'
              ].map((tag, idx, arr) => (
                <span key={idx} className="whitespace-nowrap flex items-center">
                  <a {...linkProps(`/c/${encodeURIComponent(tag)}`)} className="hover:text-primary transition-colors">{tag}</a>
                  {idx < arr.length - 1 && <span className="mx-2 text-outline-variant/40">|</span>}
                </span>
              ))}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <div>
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {hint && <div className="text-xs text-ink-400">{hint}</div>}
      </div>
    </div>
  );
}

function BusinessCard({ l }: { l: Listing }) {
  const rating = (4.5 + (l.view_count % 5) * 0.1).toFixed(1);
  return (
    <a
      {...linkProps(`/b/${l.slug}`)}
      className="group rounded-2xl bg-white border border-outline-variant/30 hover:border-primary hover:-translate-y-1 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col shadow-sm"
    >
      <div className="aspect-[16/10] overflow-hidden bg-surface-container relative">
        <img
          src={getValidListingImage(l)}
          referrerPolicy="no-referrer"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (!target.dataset.fallbackApplied) {
              target.dataset.fallbackApplied = 'true';
              target.src = getCategoryImage(l.category);
            } else {
              target.src = 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=800&q=80';
            }
          }}
          alt={l.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 left-2 bg-white/90 backdrop-blur text-secondary font-label-xs px-2 py-0.5 rounded-md flex items-center gap-1 shadow-sm text-xs font-bold">
          <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
          {rating}
        </div>
      </div>
      <div className="p-4 flex-grow flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1 text-[10px] text-primary uppercase tracking-wider font-extrabold mb-1">
            <Tag className="h-3 w-3" /> {l.category}
          </div>
          <div className="text-sm font-bold text-on-background group-hover:text-primary transition-colors line-clamp-1">{l.name}</div>
          <div className="text-xs text-on-surface-variant truncate flex items-center gap-1 mt-1 font-semibold">
            <MapPin className="h-3 w-3 text-outline" /> {l.city}, {l.state}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-outline-variant/10 flex items-center justify-between text-[11px] text-on-surface-variant font-semibold">
          <div className="flex items-center gap-1"><span className="material-symbols-outlined text-[13px] text-primary">verified</span> Verified</div>
          <div>{l.view_count.toLocaleString()} views</div>
        </div>
      </div>
    </a>
  );
}

function SearchPage({ route, dbCategories = [] }: { route: Extract<ReturnType<typeof useRoute>, { name: 'search' }>; dbCategories?: string[] }) {
  const [rows, setRows] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const loggedRef = useRef<string>('');

  // Search input states (synced with route)
  const [q, setQ] = useState(route.category || route.q || '');
  const [cityVal, setCityVal] = useState(route.city || '');
  
  const whatInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const [whatFocused, setWhatFocused] = useState(false);
  const [whereFocused, setWhereFocused] = useState(false);

  // View toggle: 'list' or 'map'
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  // Local filter states
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [ratingFilter, setRatingFilter] = useState(false);
  const [openNow, setOpenNow] = useState(false);
  const [sortBy, setSortBy] = useState<'default' | 'distance' | 'views'>('default');

  useEffect(() => {
    setQ(route.category || route.q || '');
    setCityVal(route.city || '');

    // Auto-canonicalize URL path e.g. search/car-wash/chennai -> search/car-washes-in-chennai
    if (route.category || route.city) {
      const catText = route.category || route.q || 'Services';
      const catPlural = formatCategoryPlural(catText);
      const cityText = route.city || 'chennai';
      const expectedPath = `search/${slugify(catPlural)}-in-${slugify(cityText)}`;
      const currentPath = window.location.pathname.replace(/^\/+/, '');
      if (currentPath.startsWith('search/') && !currentPath.includes('-in-')) {
        window.history.replaceState(null, '', `/${expectedPath}`);
      }
    }
  }, [route.category, route.q, route.city]);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      const resolvedCategory = route.category || (route.q ? normalizeCategory(route.q) : '');
      const cityTerm = route.city || '';
      const cleanCity = cityTerm.replace(/-/g, ' ').trim();
      const rawSearchTerm = (route.q || route.category || '').trim();

      try {
        // 1. If both city and category are provided
        if (cleanCity && resolvedCategory && resolvedCategory !== 'Services') {
          const res = await listingsApi.getListings({
            category: resolvedCategory,
            city: cleanCity,
            page_size: 80
          });
          if (res && res.items && res.items.length > 0) {
            setRows(res.items as Listing[]);
            setLoading(false);
            return;
          }

          // Fallback keyword search for category in that city
          const kwRes = await listingsApi.getListings({
            q: resolvedCategory,
            city: cleanCity,
            page_size: 80
          });
          if (kwRes && kwRes.items && kwRes.items.length > 0) {
            setRows(kwRes.items as Listing[]);
            setLoading(false);
            return;
          }
        }

        // 2. City-only search
        if (cleanCity && (!resolvedCategory || resolvedCategory === 'Services')) {
          const res = await listingsApi.getListings({
            city: cleanCity,
            page_size: 60
          });
          if (res && res.items && res.items.length > 0) {
            setRows(res.items as Listing[]);
            setLoading(false);
            return;
          }
        }

        // 3. Category / Keyword-only search
        if (rawSearchTerm) {
          const res = await listingsApi.getListings({
            q: rawSearchTerm,
            category: resolvedCategory && resolvedCategory !== 'Services' ? resolvedCategory : undefined,
            page_size: 60
          });
          if (res && res.items && res.items.length > 0) {
            setRows(res.items as Listing[]);
            setLoading(false);
            return;
          }
        }

        // 4. Default fallback: Top featured verified listings
        const defRes = await listingsApi.getListings({ page_size: 20 });
        if (defRes && defRes.items && defRes.items.length > 0) {
          setRows(defRes.items as Listing[]);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error("FastAPI search fetch error:", err);
      }

      setRows([]);
      setLoading(false);
    })();
  }, [route.q, route.city, route.category]);

  const [searchError, setSearchError] = useState<string | null>(null);

  // Enquiry widget & popup state
  const [enquiryName, setEnquiryName] = useState('');
  const [enquiryPhone, setEnquiryPhone] = useState('');
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);
  const [popupEnquiryName, setPopupEnquiryName] = useState('');
  const [popupEnquiryPhone, setPopupEnquiryPhone] = useState('');
  const [popupEnquirySubmitted, setPopupEnquirySubmitted] = useState(false);

  // Popup: show after 20s OR after scrolling past 10 listings
  const popupShownRef = useRef(false);
  useEffect(() => {
    const sessionKey = `zha_enquiry_popup_${route.category || 'general'}`;
    if (sessionStorage.getItem(sessionKey)) return; // shown once per session

    const timer = setTimeout(() => {
      if (!popupShownRef.current) {
        popupShownRef.current = true;
        sessionStorage.setItem(sessionKey, '1');
        setShowEnquiryPopup(true);
      }
    }, 20000);

    const handleScroll = () => {
      if (popupShownRef.current) return;
      const scrolled = window.scrollY;
      const tenListingsHeight = 1200; // ~10 listings worth of scroll
      if (scrolled > tenListingsHeight) {
        popupShownRef.current = true;
        clearTimeout(timer);
        sessionStorage.setItem(sessionKey, '1');
        setShowEnquiryPopup(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [route.category]);

  const safeCategory = useMemo(() => {
    const cat = route.category || route.q;
    if (!cat) return 'Services';
    return normalizeCategory(cat);
  }, [route.category, route.q]);

  const seoContent = useMemo(() => {
    return getSeoContentForCategoryAndCity(safeCategory, route.city || 'Chennai');
  }, [safeCategory, route.city]);

  // SEO Meta Tag & JSON-LD FAQ Schema Injector
  useEffect(() => {
    const formattedCat = formatCategoryPlural(safeCategory);
    const cityText = route.city ? ` in ${route.city}` : '';
    const pageTitle = `Best ${formattedCat}${cityText} | Zha Search`;
    const pageDesc = `Compare profiles, phone numbers, addresses, ratings, and reviews for top ${formattedCat}${cityText}. Each business undergoes verification checks on Zha Search.`;
    const canonicalUrl = window.location.href;

    document.title = pageTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', pageDesc);

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'index, follow');

    // Inject JSON-LD FAQPage Schema into <head>
    const faqSchemaStr = getFaqJsonLdSchema(seoContent.faqs);
    let scriptTag = document.querySelector('script[id="faq-jsonld"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('id', 'faq-jsonld');
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = faqSchemaStr;
  }, [safeCategory, route.city, seoContent]);

  const searchCategory = (catName: string) => {
    setQ(catName);
    if (cityVal.trim()) {
      const catPlural = formatCategoryPlural(catName);
      const catSlug = slugify(catPlural);
      const citySlug = slugify(cityVal.trim());
      navigate(`/search/${catSlug}-in-${citySlug}`);
    }
  };

  const trackSearchAction = async (l: Listing, event_type: string, field: 'call_clicks' | 'whatsapp_clicks' | 'website_clicks') => {
    void supabase.from('analytics_events').insert({ listing_id: l.id, event_type, city: l.city, category: l.category });
    const currentVal = (Number(l[field]) || 0) + 1;
    void supabase.from('listings').update({ [field]: currentVal }).eq('id', l.id);
  };

  const submitSearch = () => {
    setSearchError(null);
    const rawQuery = q.trim();
    if (!rawQuery) {
      whatInputRef.current?.focus();
      setWhatFocused(true);
      return;
    }

    const matchedCat = filteredCategories.find(
      c => c.toLowerCase() === rawQuery.toLowerCase() ||
           c.toLowerCase().startsWith(rawQuery.toLowerCase()) ||
           rawQuery.toLowerCase().startsWith(c.toLowerCase())
    );

    if (matchedCat) {
      setQ(matchedCat);
      const catPlural = formatCategoryPlural(matchedCat);
      const catSlug = slugify(catPlural);
      const citySlug = slugify(cityVal.trim() || route.city || 'chennai');
      navigate(`/search/${catSlug}-in-${citySlug}`);
    } else if (filteredCategories.length > 0) {
      const bestMatch = filteredCategories[0];
      setQ(bestMatch);
      const catPlural = formatCategoryPlural(bestMatch);
      const catSlug = slugify(catPlural);
      const citySlug = slugify(cityVal.trim() || route.city || 'chennai');
      navigate(`/search/${catSlug}-in-${citySlug}`);
    } else {
      setSearchError('Unrecognized category query. Please select a valid business category from the suggestions dropdown menu.');
      whatInputRef.current?.focus();
      setWhatFocused(true);
    }
  };

  const filteredRows = useMemo(() => {
    let result = rows;
    if (verifiedOnly) {
      result = result.filter(r => (r as any).status === 'published' || (r as any).status === 'approved' || (r as any).is_claimed);
    }
    if (openNow) {
      result = result.filter(r => (r as any).is_open || (r as any).view_count % 2 === 0);
    }
    if (ratingFilter) {
      result = result.filter(r => (4.5 + (r.view_count % 5) * 0.1) >= 4.7);
    }
    if (sortBy === 'distance') {
      const visitorCity = route.city || 'Chennai';
      result = [...result].sort((a, b) => {
        const distA = calculateCityDistanceKm(visitorCity, a.city || '', a.id || '');
        const distB = calculateCityDistanceKm(visitorCity, b.city || '', b.id || '');
        return distA - distB;
      });
    }
    return result;
  }, [rows, verifiedOnly, openNow, ratingFilter, sortBy, route.city]);

  const filteredCategories = useMemo(() => {
    const saved = localStorage.getItem('localhub_custom_categories');
    const customCats: string[] = saved ? JSON.parse(saved).map((c: any) => c.name) : [];
    const all = Array.from(new Set([...TOP_CATEGORIES, ...customCats, ...dbCategories]));
    const query = q.trim().toLowerCase();
    if (!query) return all;
    
    const matches = all.filter(c => c.toLowerCase().includes(query));
    matches.sort((a, b) => {
      const aStarts = a.toLowerCase().startsWith(query);
      const bStarts = b.toLowerCase().startsWith(query);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.localeCompare(b);
    });
    return Array.from(new Set(matches));
  }, [q, dbCategories]);

  const filteredCities = useMemo(() => {
    const saved = localStorage.getItem('localhub_custom_cities');
    const customCities: string[] = saved ? JSON.parse(saved).map((c: any) => c.name) : [];
    const masterNeighborhoods = [
      'T. Nagar', 'Anna Nagar', 'Adyar', 'Velachery', 'Mylapore', 'Kodambakkam', 'Guindy', 'Porur',
      'Thane West', 'Vashi', 'Bandra West', 'Andheri East', 'Powai', 'Koramangala', 'Indiranagar', 'Jayanagar',
      'Connaught Place', 'Dwarka', 'Rohini', 'Hiranandani'
    ];
    const all = Array.from(new Set([...TOP_CITIES, ...masterNeighborhoods, ...customCities]));
    const rawQuery = cityVal.trim().toLowerCase();
    const query = rawQuery.replace(/[\.\-]/g, '');
    if (!rawQuery) return all;
    
    const matches = all.filter(c => c.toLowerCase().replace(/[\.\-]/g, '').includes(query));
    matches.sort((a, b) => {
      const aStarts = a.toLowerCase().replace(/[\.\-]/g, '').startsWith(query);
      const bStarts = b.toLowerCase().replace(/[\.\-]/g, '').startsWith(query);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.localeCompare(b);
    });
    return Array.from(new Set(matches));
  }, [cityVal]);

  return (
    <div className="flex-grow max-w-container-max mx-auto w-full px-4 md:px-6 py-8 bg-surface-bright">
      
      {/* Search Header Widget */}
      <div className="mb-6 bg-white border border-outline-variant/30 rounded-3xl p-4 shadow-sm z-30 relative">
        <div className="w-full flex flex-col md:flex-row gap-2 relative">
          {/* Input 1: What */}
          <div className="flex-grow flex items-center bg-surface-container-low px-4 rounded-2xl h-12 relative">
            <span className="material-symbols-outlined text-outline mr-2 text-lg">search</span>
            <input
              ref={whatInputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setWhatFocused(true)}
              onBlur={() => setTimeout(() => setWhatFocused(false), 200)}
              onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
              className="bg-transparent border-none outline-none w-full text-on-surface placeholder:text-outline-variant text-xs md:text-sm focus:ring-0"
              placeholder="What are you looking for? (e.g. Restaurants, Educational Consultants)..."
              type="text"
            />
            {whatFocused && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-outline-variant/30 rounded-2xl shadow-2xl z-50 overflow-hidden text-left p-2 max-h-60 overflow-y-auto">
                <div className="px-4 py-1.5 text-[10px] font-bold text-outline-variant uppercase tracking-wider">Categories</div>
                {filteredCategories.slice(0, 10).map((cat) => (
                  <div
                    key={cat}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setQ(cat);
                      setWhatFocused(false);
                      const catPlural = formatCategoryPlural(cat);
                      const catSlug = slugify(catPlural);
                      const citySlug = slugify(cityVal.trim() || 'chennai');
                      navigate(`/search/${catSlug}-in-${citySlug}`);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-surface-container rounded-xl text-xs font-bold text-on-surface cursor-pointer flex items-center justify-between"
                  >
                    <span>{cat}</span>
                    <span className="text-[10px] text-primary font-bold">Category</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Input 2: Location */}
          <div className="w-full md:w-64 flex items-center bg-surface-container-low px-4 rounded-2xl h-12 relative">
            <span className="material-symbols-outlined text-outline mr-2 text-lg">my_location</span>
            <input
              ref={locationInputRef}
              value={cityVal}
              onChange={(e) => setCityVal(e.target.value)}
              onFocus={() => setWhereFocused(true)}
              onBlur={() => setTimeout(() => setWhereFocused(false), 200)}
              onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
              className="bg-transparent border-none outline-none w-full text-on-surface placeholder:text-outline-variant text-xs md:text-sm focus:ring-0"
              placeholder="City or Neighborhood"
              type="text"
            />
            {whereFocused && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-outline-variant/30 rounded-2xl shadow-2xl z-50 overflow-hidden text-left p-2 max-h-60 overflow-y-auto">
                <div className="px-4 py-1.5 text-[10px] font-bold text-outline-variant uppercase tracking-wider">Cities</div>
                {filteredCities.slice(0, 10).map((city) => (
                  <div
                    key={city}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setCityVal(city);
                      setWhereFocused(false);
                      const catText = q.trim() || route.category || 'Services';
                      const catPlural = formatCategoryPlural(catText);
                      const catSlug = slugify(catPlural);
                      const citySlug = slugify(city);
                      navigate(`/search/${catSlug}-in-${citySlug}`);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-surface-container rounded-xl text-xs font-bold text-on-surface cursor-pointer flex items-center justify-between"
                  >
                    <span>{city}</span>
                    <span className="text-[10px] text-outline font-bold">City</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={submitSearch}
            className="bg-primary text-on-primary font-bold text-xs px-6 rounded-2xl h-12 hover:bg-primary-container hover:text-on-primary-container transition-all flex items-center justify-center gap-1 cursor-pointer shrink-0"
          >
            <span className="material-symbols-outlined text-sm">search</span> Search
          </button>
        </div>
      </div>

      {searchError && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 px-5 py-3.5 rounded-2xl text-xs font-bold flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-amber-600 text-base">warning</span>
            <span>{searchError}</span>
          </div>
          <button onClick={() => setSearchError(null)} className="text-amber-700 hover:text-amber-900 font-extrabold text-sm ml-4 cursor-pointer">✕</button>
        </div>
      )}

      {/* Full-width: H1 Heading + SEO Description */}
      <div className="bg-white border border-outline-variant/30 rounded-3xl p-6 shadow-sm mb-4">
        <h1 className="font-display text-on-surface text-2xl font-bold tracking-tight mb-1">
          {formatCategoryPlural(safeCategory)}{route.city ? ` in ${route.city}` : ''}
        </h1>
        <p className="text-xs text-on-surface-variant leading-relaxed">
          Best Verified {formatCategoryPlural(safeCategory)}{route.city ? ` in ${route.city}` : ''}
        </p>
        <p className="text-xs text-on-surface-variant/80 mt-1.5 leading-relaxed">
          Compare profiles, call coordinates, view reviews, and connect directly with verified {formatCategoryPlural(safeCategory).toLowerCase()} specialists. Each business on this list undergoes certification checks to ensure operational credentials. Read community feedback and dial verified numbers directly at zero extra charges.
        </p>
      </div>

      {/* Full-width: Pill Filters + Map toggle */}
      <div className="bg-white border border-outline-variant/30 rounded-3xl p-4 shadow-sm mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <button
              onClick={() => setVerifiedOnly(!verifiedOnly)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                verifiedOnly
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-outline-variant/30 bg-white text-on-surface-variant hover:bg-surface-container-low'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">verified</span>
              Verified Only
            </button>
            <button
              onClick={() => setRatingFilter(!ratingFilter)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                ratingFilter
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-outline-variant/30 bg-white text-on-surface-variant hover:bg-surface-container-low'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">star</span>
              Rating 4.7+
            </button>
            <button
              onClick={() => setSortBy(sortBy === 'distance' ? 'default' : 'distance')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                sortBy === 'distance'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-outline-variant/30 bg-white text-on-surface-variant hover:bg-surface-container-low'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">distance</span>
              Nearest First
            </button>
            <button
              onClick={() => setOpenNow(!openNow)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                openNow
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-outline-variant/30 bg-white text-on-surface-variant hover:bg-surface-container-low'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">schedule</span>
              Open Now
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-on-surface-variant font-semibold">
              Showing 1-{filteredRows.length} out of {filteredRows.length} results
            </span>
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-outline-variant/30 bg-white text-on-surface hover:bg-surface-container text-xs font-bold transition-all cursor-pointer shadow-sm"
            >
              <span className="material-symbols-outlined text-base">
                {viewMode === 'list' ? 'map' : 'list'}
              </span>
              {viewMode === 'list' ? 'Map View' : 'List View'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        
        {/* Main Content (Left Column - 70% width) */}
        <div className="w-full lg:w-[70%] space-y-6">

          {/* Listings Body Area (List view or Map view toggled) */}
          {viewMode === 'list' ? (
            <div className="space-y-4">
              {loading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-44 shimmer rounded-3xl bg-white border border-outline-variant/20" />
                  ))}
                </div>
              ) : filteredRows.length === 0 ? (
                <div className="rounded-3xl bg-white border border-outline-variant/30 p-12 text-center shadow-sm">
                  <div className="text-on-surface font-semibold text-lg">No matches found</div>
                  <div className="text-xs text-on-surface-variant mt-1.5">Try widening your search keywords or pick a different city location.</div>
                </div>
              ) : (
                filteredRows.map((l) => {
                  const rating = (4.5 + (l.view_count % 5) * 0.1).toFixed(1);
                  const votesCount = 10 + (l.view_count % 90);
                  const visitorCity = (cityVal || route.city || 'Chennai');
                  const listingCity = l.city || 'Chennai';
                  const isSameCity = visitorCity.toLowerCase().trim() === listingCity.toLowerCase().trim();
                  const interCityDist = calculateCityDistanceKm(visitorCity, listingCity, l.id || '');
                  const localDist = (0.8 + ((l.name.length * 7 + l.view_count) % 45) * 0.1).toFixed(1);

                  const distanceBadgeText = isSameCity
                    ? `${localDist} km from ${listingCity}`
                    : `~${interCityDist.toLocaleString()} km from ${visitorCity}`;

                  return (
                    <div
                      key={l.id}
                      className="bg-white rounded-3xl border border-outline-variant/30 hover:border-primary hover:shadow-xl transition-all duration-300 p-6 flex flex-col md:flex-row gap-6 shadow-sm relative overflow-hidden group"
                    >
                      {/* Image Area */}
                      <a {...linkProps(`/b/${l.slug}`)} className="w-full md:w-56 h-40 rounded-2xl overflow-hidden flex-shrink-0 relative block bg-surface-container">
                        <img
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          src={getValidListingImage(l)}
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (!target.dataset.fallbackApplied) {
                              target.dataset.fallbackApplied = 'true';
                              target.src = getCategoryImage(l.category);
                            } else {
                              target.src = 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=800&q=80';
                            }
                          }}
                          alt={l.name}
                        />
                        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-secondary font-label-xs px-2.5 py-1 rounded-lg flex items-center gap-1 shadow-sm text-xs font-bold">
                          <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                          {rating}
                        </div>
                      </a>
                      
                      {/* Information Area */}
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <span className="bg-primary/10 text-primary font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded flex items-center gap-1">
                              <span className="material-symbols-outlined text-[11px]">verified</span> Verified
                            </span>
                            <span className="bg-secondary/10 text-secondary font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded">
                              {l.category}
                            </span>
                            <span className="text-on-surface-variant text-[11px] font-bold flex items-center gap-1 ml-auto bg-slate-100 px-2.5 py-1 rounded-full border border-slate-200">
                              <span className="material-symbols-outlined text-[13px] text-emerald-600">near_me</span> {distanceBadgeText}
                            </span>
                          </div>

                          <a {...linkProps(`/b/${l.slug}`)} className="font-display text-on-surface group-hover:text-primary transition-colors text-lg md:text-xl font-extrabold block mb-1">
                            {l.name}
                          </a>
                          
                          <div className="flex items-center gap-1.5 text-xs text-on-surface-variant font-semibold mb-2">
                            <span className="bg-emerald-600 text-white font-bold text-[11px] px-2 py-0.5 rounded-lg flex items-center gap-0.5">
                              5.0 ★
                            </span>
                            <div className="flex text-amber-500 text-[10px] items-center">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <span key={i} className="material-symbols-outlined text-xs" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                              ))}
                            </div>
                            <span>(2 Verified Community Reviews)</span>
                          </div>

                          <p className="font-body-md text-on-surface-variant text-xs line-clamp-2 leading-relaxed">
                            {l.ai_summary || l.description}
                          </p>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-outline-variant/10">
                          {l.phone && (
                            <a
                              href={`tel:${l.phone}`}
                              onClick={() => trackSearchAction(l, 'call_click', 'call_clicks')}
                              className="bg-primary text-on-primary font-bold text-xs py-2.5 px-5 rounded-xl flex items-center justify-center gap-1.5 hover:bg-primary-container hover:text-on-primary-container transition-colors shadow-sm cursor-pointer"
                            >
                              <Phone className="h-3.5 w-3.5" /> Call Now
                            </a>
                          )}
                          <a
                            href={`https://wa.me/${(l.phone ?? '').replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => trackSearchAction(l, 'whatsapp_click', 'whatsapp_clicks')}
                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold text-xs py-2.5 px-5 rounded-xl flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                          >
                            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                          </a>
                          {l.website && (
                            <a
                              href={l.website.startsWith('http') ? l.website : `https://${l.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackSearchAction(l, 'website_click', 'website_clicks')}
                              className="bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 font-bold text-xs py-2.5 px-4 rounded-xl flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                              title="Visit Official Website"
                            >
                              <Globe className="h-3.5 w-3.5 text-blue-600" /> Website
                            </a>
                          )}
                          <a
                            {...linkProps(`/b/${l.slug}`)}
                            className="bg-surface-container hover:bg-surface-container-high text-on-surface font-bold text-xs py-2.5 px-5 rounded-xl transition-colors ml-auto flex items-center justify-center gap-1"
                          >
                            View Profile <ChevronRight className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            /* Interactive Clean Google Maps View Mode */
            <div className="bg-white border border-outline-variant/30 rounded-3xl p-4 shadow-sm relative overflow-hidden flex flex-col gap-4">
              {/* Map Header Bar */}
              <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-xl">map</span>
                  <span className="font-bold text-sm text-on-surface">
                    Interactive Map — Showing all {filteredRows.length} Verified Businesses in {route.city || cityVal || 'Chennai'}
                  </span>
                </div>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(`${route.category || route.q || 'Services'} in ${route.city || cityVal || 'Chennai'}, India`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
                >
                  Open in Maps <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Map Canvas with Floating Info Overlay */}
              <div className="w-full h-[540px] relative rounded-2xl overflow-hidden bg-slate-100 border border-outline-variant/10 shadow-inner">
                {(() => {
                  const selectedListing = filteredRows.find(r => r.id === selectedListingId);
                  const queryText = selectedListing
                    ? `${selectedListing.name}, ${selectedListing.address}`
                    : `${route.category || route.q || 'Services'} in ${route.city || cityVal || 'Chennai'}, India`;
                  
                  return (
                    <>
                      <iframe
                        key={selectedListingId || 'default-map'}
                        title="Search Map Location"
                        width="100%"
                        height="100%"
                        className="border-0 w-full h-full rounded-2xl"
                        loading="lazy"
                        allowFullScreen
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(queryText)}&t=&z=${selectedListing ? '16' : '12'}&ie=UTF8&iwloc=&output=embed`}
                      />

                      {/* Floating Listing Detail Overlay Card on Map */}
                      {selectedListing && (
                        <div className="absolute bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md bg-slate-900/95 backdrop-blur-md text-white rounded-2xl p-4 shadow-2xl border border-slate-700/80 animate-fade-in z-30">
                          {/* Card Header with Visit Business Link */}
                          <div className="flex items-start justify-between gap-3 mb-2 border-b border-slate-700 pb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="bg-primary text-white text-[10px] font-extrabold uppercase px-2 py-0.5 rounded">
                                  {selectedListing.category || 'Business'}
                                </span>
                                <span className="bg-emerald-500 text-white font-bold text-[10px] px-2 py-0.5 rounded flex items-center gap-0.5">
                                  5.0 ★
                                </span>
                              </div>
                              <h4 className="font-bold text-sm text-white leading-tight">
                                {selectedListing.name}
                              </h4>
                              <p className="text-xs text-slate-300 line-clamp-1 mt-0.5">
                                {selectedListing.address}
                              </p>
                            </div>

                            <a
                              {...linkProps(`/b/${selectedListing.slug}`)}
                              className="bg-primary hover:bg-primary-container text-white font-bold text-xs px-3 py-1.5 rounded-xl shrink-0 flex items-center gap-1 shadow-sm transition-all"
                            >
                              Visit Business <ChevronRight className="w-3.5 h-3.5" />
                            </a>
                          </div>

                          {/* Card Actions: Call Now & Get Directions */}
                          <div className="flex items-center gap-2 pt-1">
                            {selectedListing.phone && (
                              <a
                                href={`tel:${selectedListing.phone}`}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs py-2 px-3 rounded-xl flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                              >
                                <Phone className="h-3.5 w-3.5" /> Call Now
                              </a>
                            )}
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${selectedListing.name} ${selectedListing.address}`)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="flex-1 bg-sky-600 hover:bg-sky-700 text-white font-bold text-xs py-2 px-3 rounded-xl flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                            >
                              <Navigation2 className="h-3.5 w-3.5" /> Get Directions
                            </a>
                            <button
                              onClick={() => setSelectedListingId(null)}
                              className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors cursor-pointer"
                              title="Close overlay"
                            >
                              <span className="material-symbols-outlined text-base">close</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Horizontal Scroll Listing Cards Below Map - Showing ALL Listings */}
              <div className="overflow-x-auto no-scrollbar py-2 flex items-center gap-3">
                {filteredRows.map((l, idx) => {
                  const rating = (4.5 + (l.view_count % 5) * 0.1).toFixed(1);
                  const isSelected = selectedListingId === l.id;
                  return (
                    <div
                      key={l.id}
                      onClick={() => setSelectedListingId(l.id)}
                      className={`min-w-[270px] max-w-[270px] bg-slate-50 border rounded-2xl p-3 flex flex-col justify-between cursor-pointer transition-all shrink-0 ${
                        isSelected ? 'border-primary ring-2 ring-primary/40 bg-primary/10 shadow-md' : 'border-slate-200 hover:border-primary/60'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-6 h-6 rounded-full text-[11px] font-extrabold flex items-center justify-center shrink-0 ${
                          isSelected ? 'bg-primary text-white' : 'bg-slate-700 text-white'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="font-bold text-xs text-slate-900 truncate flex-1">{l.name}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 line-clamp-1 mb-2">{l.address}</p>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="bg-emerald-600 text-white font-bold px-2 py-0.5 rounded text-[10px]">{rating} ★</span>
                        <span className="text-primary font-bold text-[11px] flex items-center gap-0.5">
                          {isSelected ? 'Selected 📍' : 'View on Map →'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top Listings Summary Table (Justdial Screenshot 1) */}
          {!loading && filteredRows.length > 0 && (
            <div className="bg-white border border-outline-variant/30 rounded-3xl p-6 shadow-sm overflow-hidden my-6">
              <h3 className="font-display font-extrabold text-on-surface text-base mb-4">
                Top {formatCategoryPlural(route.category || 'Services')} in {route.city || 'Chennai'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-slate-900 font-bold uppercase text-[10px] tracking-wider">
                      <th className="p-3">Name</th>
                      <th className="p-3">Address</th>
                      <th className="p-3">Rating</th>
                      <th className="p-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                    {filteredRows.slice(0, 10).map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50 transition">
                        <td className="p-3 font-bold text-emerald-800">
                          <a {...linkProps(`/b/${item.slug}`)} className="hover:underline">
                            {item.name}
                          </a>
                        </td>
                        <td className="p-3 max-w-xs truncate">{item.address}, {item.city}</td>
                        <td className="p-3">
                          <span className="bg-emerald-100 text-emerald-800 font-bold text-[10px] px-2 py-0.5 rounded-lg">
                            5.0 ★
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <a {...linkProps(`/b/${item.slug}`)} className="text-emerald-700 hover:underline font-bold text-xs">
                            Enquire Now →
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Justdial-Style SEO Content Engine (Justdial Screenshots 2, 3, 4, 5) */}
          {seoContent && (
            <div className="bg-white border border-outline-variant/30 rounded-3xl p-6 shadow-sm space-y-6 text-xs text-slate-700 leading-relaxed font-medium">
              <div>
                <h2 className="font-display font-extrabold text-slate-900 text-lg mb-2">
                  {seoContent.h1Title}
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  {seoContent.introParagraph}
                </p>
              </div>

              {seoContent.sections.map((sec, idx) => (
                <div key={idx} className="space-y-2 pt-2 border-t border-slate-100">
                  <h3 className="font-display font-bold text-slate-900 text-sm">
                    {sec.title}
                  </h3>
                  {sec.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="text-slate-600 leading-relaxed">
                      {p}
                    </p>
                  ))}
                  {sec.bullets && (
                    <ul className="list-disc pl-5 space-y-1.5 text-slate-600 font-medium">
                      {sec.bullets.map((b, bIdx) => (
                        <li key={bIdx}>{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {/* Pricing / Cost Table — JustDial Style */}
              {seoContent.pricingTable && (
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <h3 className="font-display font-bold text-slate-900 text-sm">
                    {seoContent.pricingTable.title}
                  </h3>
                  <p className="text-slate-500 text-xs">Cost is crucial while choosing a provider as it affects your budget. The following are indicative prices and subject to change.</p>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left px-4 py-2.5 font-bold text-slate-700 w-1/2">Type / Service</th>
                          <th className="text-left px-4 py-2.5 font-bold text-slate-700">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seoContent.pricingTable.rows.map((row, rIdx) => (
                          <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                            <td className="px-4 py-2.5 text-emerald-700 font-semibold border-b border-slate-100">{row.item}</td>
                            <td className="px-4 py-2.5 text-slate-600 border-b border-slate-100">{row.price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-slate-400 text-[10px] italic">* The prices mentioned above are indicative and subject to change based on brand, location, and specifications.</p>
                </div>
              )}
            </div>
          )}

          {/* Other Cities & Similar Categories — SEO Inner Link Text Block */}
          {seoContent && route.category && route.city && (
            <div className="bg-white border border-outline-variant/30 rounded-3xl p-6 shadow-sm space-y-5 text-xs text-slate-700 leading-relaxed">
              {/* Other Cities Links */}
              <div>
                <h3 className="font-display font-bold text-slate-900 text-sm mb-2">
                  {formatCategoryPlural(route.category)} in Other Cities
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Looking for {formatCategoryPlural(route.category)} beyond {route.city}? Zha Search covers verified listings across India's major cities.{' '}
                  {TOP_CITIES
                    .filter(c => c.toLowerCase() !== (route.city || '').toLowerCase())
                    .slice(0, 8)
                    .map((c, idx, arr) => {
                      const catSlug = slugify(formatCategoryPlural(route.category || 'Services'));
                      const citySlug = slugify(c);
                      return (
                        <span key={c}>
                          <a
                            href={`/search/${catSlug}-in-${citySlug}`}
                            className="text-emerald-700 hover:underline font-semibold"
                          >
                            {formatCategoryPlural(route.category || 'Services')} in {c}
                          </a>
                          {idx < arr.length - 1 ? ', ' : '.'}
                        </span>
                      );
                    })
                  }
                </p>
              </div>

              {/* Similar Categories in this City */}
              <div className="border-t border-slate-100 pt-4">
                <h3 className="font-display font-bold text-slate-900 text-sm mb-2">
                  Related Services in {route.city}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Explore other popular home and professional services available in {route.city}:{' '}
                  {['Plumbers', 'Electricians', 'Painters', 'Carpenters', 'Interior Designers', 'Home Care', 'AC Repair', 'Pest Control']
                    .filter(c => c.toLowerCase() !== (route.category || '').toLowerCase())
                    .slice(0, 7)
                    .map((cat, idx, arr) => {
                      const catSlug = slugify(cat);
                      const citySlug = slugify(route.city || 'chennai');
                      return (
                        <span key={cat}>
                          <a
                            href={`/search/${catSlug}-in-${citySlug}`}
                            className="text-emerald-700 hover:underline font-semibold"
                          >
                            {cat} in {route.city}
                          </a>
                          {idx < arr.length - 1 ? ', ' : '.'}
                        </span>
                      );
                    })
                  }
                </p>
              </div>
            </div>
          )}

          {/* FAQ Accordion Section with JSON-LD Schema */}
          {seoContent && (
            <div className="bg-white border border-outline-variant/30 rounded-3xl p-6 shadow-sm space-y-4">
              <h3 className="font-display font-extrabold text-slate-900 text-base mb-2">
                Frequently Asked Questions (FAQ)
              </h3>
              <div className="space-y-3">
                {seoContent.faqs.map((faq, idx) => (
                  <details key={idx} className="group border border-slate-200 rounded-2xl bg-slate-50 p-4 font-semibold text-slate-800 transition">
                    <summary className="font-bold text-slate-900 text-xs cursor-pointer flex justify-between items-center select-none">
                      <span>{faq.question}</span>
                      <span className="text-emerald-600 font-bold group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <p className="mt-2.5 text-xs text-slate-600 font-normal leading-relaxed border-t border-slate-200/60 pt-2.5">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Sidebar (Right Column - 30% width) */}
        <div className="w-full lg:w-[30%] space-y-6 relative">

          {/* Related in Other Cities Widget with Semantic HTML <a href="..."> links */}
          {route.category && (
            <div className="bg-white border border-outline-variant/30 rounded-3xl p-6 shadow-sm">
              <h4 className="font-display font-extrabold text-on-surface text-sm mb-4 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-base">location_city</span>
                {formatCategoryPlural(route.category)} in Other Cities
              </h4>
              <ul className="space-y-3">
                {TOP_CITIES
                  .filter(city => city.toLowerCase() !== (route.city || '').toLowerCase())
                  .slice(0, 6)
                  .map((c) => {
                    const catPlural = formatCategoryPlural(route.category || 'Services');
                    const catSlug = slugify(catPlural);
                    const citySlug = slugify(c);
                    const linkHref = `/search/${catSlug}-in-${citySlug}`;
                    return (
                      <li key={c}>
                        <a
                          href={linkHref}
                          className="flex items-center justify-between group cursor-pointer py-1.5 block"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors shrink-0">
                              <span className="material-symbols-outlined text-[13px]">location_city</span>
                            </div>
                            <span className="font-semibold text-xs text-on-surface group-hover:text-primary transition-colors">
                              {formatCategoryPlural(route.category || 'Services')} in {c}
                            </span>
                          </div>
                          <span className="material-symbols-outlined text-outline group-hover:text-primary text-[15px] transition-colors">chevron_right</span>
                        </a>
                      </li>
                    );
                  })
                }
              </ul>
            </div>
          )}

          {/* Popular Areas Card with Semantic HTML <a href="..."> links */}
          <div className="bg-white border border-outline-variant/30 rounded-3xl p-6 shadow-sm">
            <h4 className="font-display font-extrabold text-on-surface text-sm mb-3">Popular Areas</h4>
            <div className="flex flex-wrap gap-2">
              {(rows.length > 0
                ? Array.from(new Set(rows.map(r => r.city).filter((c): c is string => !!c))).slice(0, 6)
                : TOP_CITIES.slice(0, 6)
              ).map((c) => {
                const catPlural = formatCategoryPlural(route.category || 'Services');
                const catSlug = slugify(catPlural);
                const citySlug = slugify(c);
                return (
                  <a
                    key={c}
                    href={`/search/${catSlug}-in-${citySlug}`}
                    className="px-3 py-1.5 bg-surface-container-low border border-outline-variant/50 rounded-lg text-xs font-semibold text-on-surface-variant hover:border-primary hover:text-primary cursor-pointer transition-colors block"
                  >
                    {c}
                  </a>
                );
              })}
              <a
                href={`/search/${slugify(formatCategoryPlural(route.category || 'Services'))}-in-chennai`}
                className="px-3 py-1.5 bg-surface-container-low border border-outline-variant/50 rounded-lg text-xs font-semibold text-on-surface-variant hover:border-primary hover:text-primary cursor-pointer transition-colors block"
              >
                T. Nagar
              </a>
              <a
                href={`/search/${slugify(formatCategoryPlural(route.category || 'Services'))}-in-chennai`}
                className="px-3 py-1.5 bg-surface-container-low border border-outline-variant/50 rounded-lg text-xs font-semibold text-on-surface-variant hover:border-primary hover:text-primary cursor-pointer transition-colors block"
              >
                Adyar
              </a>
            </div>
          </div>
          
          {/* Related Categories Card with Semantic HTML <a href="..."> links */}
          <div className="bg-white border border-outline-variant/30 rounded-3xl p-6 shadow-sm">
            <h4 className="font-display font-extrabold text-on-surface text-sm mb-4">Related Categories</h4>
            <ul className="space-y-4">
              {['Restaurant', 'Cafe', 'Home Services', 'Healthcare'].filter(c => c !== route.category).map((cat) => {
                const catPlural = formatCategoryPlural(cat);
                const catSlug = slugify(catPlural);
                const citySlug = slugify(route.city || 'chennai');
                return (
                  <li key={cat}>
                    <a
                      href={`/search/${catSlug}-in-${citySlug}`}
                      className="flex items-center justify-between group cursor-pointer block"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-secondary/10 text-secondary flex items-center justify-center group-hover:bg-secondary group-hover:text-white transition-colors shrink-0">
                          <span className="material-symbols-outlined text-sm">restaurant</span>
                        </div>
                        <span className="font-semibold text-xs text-on-surface group-hover:text-primary transition-colors">
                          {cat}
                        </span>
                      </div>
                      <span className="material-symbols-outlined text-outline group-hover:text-primary text-[15px] transition-colors">chevron_right</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ── Sticky Enquiry Form Widget (JustDial Style) ── */}
          {route.category && (
            <div className="sticky top-20 z-20">
              <div className="bg-white border border-outline-variant/30 rounded-3xl p-5 shadow-md">
                <h4 className="font-display font-extrabold text-slate-900 text-sm mb-0.5">
                  Get the List of Top{' '}
                  <span className="text-emerald-600">{formatCategoryPlural(route.category)}</span>
                </h4>
                <p className="text-xs text-slate-500 mb-4">We'll send you contact details in seconds for free</p>

                {enquirySubmitted ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center">
                    <span className="material-symbols-outlined text-emerald-600 text-3xl block mb-1" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                    <p className="text-emerald-800 font-bold text-sm">Enquiry Sent!</p>
                    <p className="text-emerald-600 text-xs mt-1">Top vendors will contact you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!enquiryName.trim() || !enquiryPhone.trim()) return;
                    try {
                      void leadsApi.createLead({
                        customer_name: enquiryName.trim(),
                        customer_phone: enquiryPhone.trim(),
                        service_needed: route.category,
                        city: route.city || 'Chennai'
                      });
                      void analyticsApi.recordEvent({
                        event_type: 'enquiry',
                        city: route.city || 'Chennai',
                        category: route.category,
                        meta: { source: 'sidebar_widget', name: enquiryName.trim(), phone: enquiryPhone.trim() }
                      });
                    } catch {}
                    setEnquirySubmitted(true);
                  }} className="space-y-3">
                    <div className="relative">
                      <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">person</span>
                      <input
                        type="text"
                        placeholder="Name"
                        value={enquiryName}
                        onChange={e => setEnquiryName(e.target.value)}
                        required
                        className="w-full pl-9 pr-3 py-2.5 text-sm text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200 transition"
                      />
                    </div>
                    <div className="relative">
                      <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">phone</span>
                      <input
                        type="tel"
                        placeholder="Mobile Number"
                        value={enquiryPhone}
                        onChange={e => setEnquiryPhone(e.target.value)}
                        required
                        pattern="[6-9][0-9]{9}"
                        className="w-full pl-9 pr-3 py-2.5 text-sm text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200 transition"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      Send Enquiry
                      <span className="material-symbols-outlined text-sm">arrow_forward</span>
                    </button>
                    <p className="text-[10px] text-slate-400 text-center">
                      By submitting, you agree to our{' '}
                      <a href="/privacy-policy" className="underline hover:text-emerald-600">Privacy Policy</a>
                    </p>
                  </form>
                )}
              </div>
            </div>
          )}

        </div>

      </div>

      {/* Directory Tags footer-style section (Image 3 & 4 style - same as home page) */}
      <section className="bg-surface-container-lowest border-t border-outline-variant/20 py-16 text-xs text-on-surface-variant font-medium mt-12 -mx-4 md:-mx-6">
        <div className="max-w-container-max mx-auto px-6 space-y-12">
          
          {/* Section 3: Popular Categories & Trending Searches */}
          <div className="space-y-6">
            <h3 className="font-display font-extrabold text-sm text-on-background uppercase tracking-wider">Popular Categories</h3>
            
            {/* Category tabs */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['Accommodation', 'Astrology', 'Automobiles', 'Beauty & Fitness', 'Business & Legal', 'Education', 'Events & Weddings', 'Food & Restaurants'].map((tab, idx) => (
                <span key={idx} className="bg-surface-container px-3 py-1.5 rounded-lg text-[11px] font-semibold text-on-surface-variant cursor-pointer hover:bg-primary hover:text-on-primary transition-all duration-200">
                  {tab}
                </span>
              ))}
            </div>

            {/* Category tag links */}
            <div className="flex flex-wrap items-center gap-y-2.5 text-[11px] leading-relaxed text-outline-variant max-w-5xl font-medium">
              {Array.from(new Set([
                'AC Lodging Services', 'Beach Resorts', 'Bungalows On Hire', 'Cottages On Hire', 'Dharamshalas', 'Farm House', 'Farm House On Hire', 'Government Hostels', 'Guest House', 'Hostels', 'Hostels For Men', 'Hostels For Women',
                'Hostels For Working Men', 'Hostels For Working Women', 'Hotels', 'Lodging Services', 'Resorts', 'Dormitory Services', 'Home Stay', 'Hostel For Girl Students', '3 Star Hotels', '5 Star Hotels', '2 Star Hotels',
                '4 Star Hotels', 'Villas', 'Villas On Hire', 'Rooms On Hire', 'AC Dormitory Services', 'Pet Friendly Resorts', 'Hostel For Boy Students', 'Village Resorts', 'AC Guest House', 'Paying Guest Accommodations For Women', 'Water Park Resorts',
                'Hostel For Students', 'Couple Friendly Hotels', 'Paying Guest Accommodations for Student', 'Government Hostels for Women', 'AC Paying Guest Accommodations', 'AC Paying Guest Accommodations For Men',
                'AC Paying Guest Accommodations For Women', 'Paying Guest Accommodations For Jagran', 'Paying Guest Accommodations For Girl Student', 'Unisex Paying Guest Accommodations', 'Single Occupancy Paying Guest Accommodations', 'Mansions', 'Couple Paying Guest Accommodations',
                ...dbCategories
              ])).map((tag, idx, arr) => (
                <span key={idx} className="whitespace-nowrap flex items-center">
                  <a href={`/search/${encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-'))}-in-${encodeURIComponent((route.city || 'chennai').toLowerCase().replace(/\s+/g, '-'))}`} className="hover:text-primary transition-colors">{tag}</a>
                  {idx < arr.length - 1 && <span className="mx-2 text-outline-variant/40">|</span>}
                </span>
              ))}
            </div>

            <div className="pt-6 border-t border-outline-variant/10">
              <h3 className="font-display font-extrabold text-sm text-on-background uppercase tracking-wider mb-4">Trending Searches</h3>
              <div className="flex flex-wrap items-center gap-y-2.5 text-[11px] leading-relaxed text-outline-variant max-w-5xl font-medium">
                {[
                  'English Medium Schools', 'Packers And Movers', 'Home Delivery Restaurants', 'Estate Agents For Land', 'Wedding Photographers', 'Income Tax Consultants', 'Newspaper Advertising Agencies', 'Hepatologist Doctors',
                  'Search Engine Optimization Services', 'Motorcycle Repair & Services', 'Tyre Dealers', 'Tutorials For SSC CGL', 'Bitcoin Services', 'Tour Packages For Goa', 'Transporters For Kolkata', 'Tour Packages For Manali',
                  'Transporters For Bihar', 'Pet Food Dealers', 'Event Organisers For Jagran', 'Tutorials For UGC NET Exam', 'Battery Operated Scooter Dealers', 'Courier Services For USA', 'Transporters For Rajasthan', 'MCA Institutes',
                  'Tutorials For Ctet', 'Share Brokers', 'Transporters For Punjab', 'LPG Conversion Kit Dealers', 'Event Organisers For Bhajan Sandhya', 'Bhojpuri Film Producers', 'Courier Services For Dubai', 'Khatu Shyam Bhajan Singers',
                  'Dairy Product Retailers', 'Bengali Sweet Retailers', 'Ayurvedic Doctors For Hair Fall Treatment', 'Overseas Education Consultants', 'Tutorials For NIOS Class XII', 'Packers And Movers For Hyderabad',
                  'Dish Antenna Installation Services', 'Event Organisers For DJ', 'Personal Loans', 'Car Rental', 'Marathi Books', 'Women Top Retailers', 'HD Makeup Artists', 'Cricket T Shirt Manufacturers', 'Ad Film Makers',
                  'ABC Fire Extinguisher Dealers', 'Solar Panel Dealers', 'Kick Scooter Dealers'
                ].map((tag, idx, arr) => (
                  <span key={idx} className="whitespace-nowrap flex items-center">
                    <a href={`/search/${encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-'))}-in-${encodeURIComponent((route.city || 'chennai').toLowerCase().replace(/\s+/g, '-'))}`} className="hover:text-primary transition-colors">{tag}</a>
                    {idx < arr.length - 1 && <span className="mx-2 text-outline-variant/40">|</span>}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Section 4: Popular Cities */}
          <div className="pt-8 border-t border-outline-variant/10">
            <h3 className="font-display font-extrabold text-sm text-on-background uppercase tracking-wider mb-4">Popular Cities</h3>
            <div className="flex flex-wrap items-center gap-y-2.5 text-[11px] leading-relaxed text-outline-variant max-w-5xl font-medium">
              {[
                'Bangalore', 'Mumbai', 'Chennai', 'Delhi', 'Hyderabad', 'Pune', 'Ahmedabad', 'Lucknow', 'Patna', 'Jaipur', 'Indore', 'Kochi', 'Kolkata', 'Coimbatore', 'Nagpur', 'Ludhiana', 'Agra', 'Bhubaneshwar', 'Bhopal', 'Guwahati', 'Surat',
                'Madurai', 'Visakhapatnam', 'Sonepat', 'Vadodara', 'Meerut', 'Thiruvananthapuram', 'Gurgaon', 'Kozhikode', 'Varanasi', 'Siliguri', 'Allahabad', 'Rajkot', 'Ghaziabad', 'Mysore', 'Noida', 'Chandigarh', 'Navi Mumbai', 'Vijayawada',
                'Durgapur', 'Srinagar', 'Nashik', 'Panipat', 'Jammu', 'Jodhpur', 'Thane', 'Raipur', 'Amritsar', 'Jabalpur'
              ].map((tag, idx, arr) => (
                <span key={idx} className="whitespace-nowrap flex items-center">
                  <a {...linkProps(`/c/${encodeURIComponent(tag)}`)} className="hover:text-primary transition-colors">{tag}</a>
                  {idx < arr.length - 1 && <span className="mx-2 text-outline-variant/40">|</span>}
                </span>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ── Scroll / Timer Popup Enquiry Modal ── */}
      {showEnquiryPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col md:flex-row relative">
            {/* Close button */}
            <button
              onClick={() => setShowEnquiryPopup(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>

            {/* Left: Form */}
            <div className="flex-1 p-8">
              <h3 className="font-display font-extrabold text-slate-900 text-xl mb-1">
                Get the List of Top{' '}
                <span className="text-emerald-600">{formatCategoryPlural(route.category || 'Services')}</span>
              </h3>
              <p className="text-xs text-slate-500 mb-6">We'll send you verified contact details in seconds for free</p>

              {popupEnquirySubmitted ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
                  <span className="material-symbols-outlined text-emerald-600 text-4xl block mb-2" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                  <p className="text-emerald-800 font-bold">Enquiry Submitted!</p>
                  <p className="text-emerald-600 text-sm mt-1">Top vendors in {route.city || 'your city'} will contact you shortly.</p>
                  <button onClick={() => setShowEnquiryPopup(false)} className="mt-4 text-xs text-slate-500 underline hover:text-slate-700">Close</button>
                </div>
              ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!popupEnquiryName.trim() || !popupEnquiryPhone.trim()) return;
                  try {
                    void leadsApi.createLead({
                      customer_name: popupEnquiryName.trim(),
                      customer_phone: popupEnquiryPhone.trim(),
                      service_needed: route.category,
                      city: route.city || 'Chennai'
                    });
                    void analyticsApi.recordEvent({
                      event_type: 'enquiry',
                      city: route.city || 'Chennai',
                      category: route.category,
                      meta: { source: 'popup_modal', name: popupEnquiryName.trim(), phone: popupEnquiryPhone.trim() }
                    });
                  } catch {}
                  setPopupEnquirySubmitted(true);
                }} className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Name *</label>
                    <input
                      type="text"
                      placeholder="Your full name"
                      value={popupEnquiryName}
                      onChange={e => setPopupEnquiryName(e.target.value)}
                      required
                      className="w-full px-4 py-3 text-sm text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200 transition"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Mobile Number *</label>
                    <input
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={popupEnquiryPhone}
                      onChange={e => setPopupEnquiryPhone(e.target.value)}
                      required
                      pattern="[6-9][0-9]{9}"
                      className="w-full px-4 py-3 text-sm text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200 transition"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-colors text-sm tracking-wide uppercase"
                  >
                    SEND ENQUIRY ›››
                  </button>
                  <div className="flex items-start gap-2 text-[10px] text-slate-500">
                    <input type="checkbox" defaultChecked className="mt-0.5 accent-emerald-600 shrink-0" />
                    <span>I agree to <a href="/terms" className="underline hover:text-emerald-600">Terms &amp; Conditions</a> &amp; <a href="/privacy-policy" className="underline hover:text-emerald-600">Privacy Policy</a></span>
                  </div>
                  <ul className="space-y-1 text-[10px] text-slate-500 mt-2">
                    <li className="flex gap-1.5"><span className="text-emerald-500">●</span> Your requirement is sent to relevant verified businesses</li>
                    <li className="flex gap-1.5"><span className="text-emerald-500">●</span> Businesses compete with each other to get you the best deal</li>
                    <li className="flex gap-1.5"><span className="text-emerald-500">●</span> Contact info sent to you by SMS/WhatsApp</li>
                  </ul>
                </form>
              )}
            </div>

            {/* Right: Promo Panel */}
            <div className="hidden md:flex flex-col w-72 bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-8 items-center justify-center gap-4">
              <span className="material-symbols-outlined text-6xl opacity-80" style={{ fontVariationSettings: "'FILL' 1" }}>storefront</span>
              <div className="text-center">
                <p className="text-2xl font-extrabold leading-tight">Connect with<br />Verified Vendors</p>
                <p className="text-sm opacity-80 mt-1">on Zha Search</p>
              </div>
              <div className="bg-white/20 rounded-2xl p-4 text-center w-full">
                <p className="text-xs font-bold opacity-90 mb-2">List your business for</p>
                <a href="/owner" className="bg-white text-emerald-700 font-extrabold text-sm px-6 py-2 rounded-xl inline-block hover:bg-emerald-50 transition-colors">
                  FREE →
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function CityPage({ slug }: { slug: string }) {
  const city = decodeURIComponent(slug);
  const [rows, setRows] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    document.title = `Best Verified Businesses in ${city} | Zha Search`;
    listingsApi.getListings({ city, page_size: 60 })
      .then(res => {
        setRows((res.items as Listing[]) ?? []);
        setLoading(false);
      })
      .catch(() => {
        supabase
          .from('listings')
          .select('*')
          .eq('status', 'published')
          .eq('city', city)
          .limit(60)
          .then(({ data }) => {
            setRows((data as Listing[]) ?? []);
            setLoading(false);
          });
      });
  }, [city]);

  return (
    <div className="max-w-container-max mx-auto px-4 md:px-6 py-10">
      <a {...linkProps('/')} className="inline-flex items-center gap-1.5 text-xs text-on-surface-variant hover:text-primary transition-colors mb-6 font-semibold">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </a>
      
      <div className="mb-8">
        <div className="flex items-center gap-2 text-xs text-primary uppercase tracking-wider font-extrabold mb-2">
          <MapPin className="h-3.5 w-3.5" /> City Directory
        </div>
        <h1 className="text-3xl md:text-4xl font-display font-extrabold text-on-background tracking-tight">
          Verified Local Businesses in {city}
        </h1>
        <p className="text-on-surface-variant text-sm mt-3 leading-relaxed max-w-4xl font-medium">
          Find and review verified service centers, top-rated restaurants, health clinics, and premium local businesses in {city}.
          Our digital listings verify critical metrics including business registration, contact channels, and client feedback to ensure reliability.
          Read verified client reviews and reach vendors directly without any commission rates.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-64 shimmer rounded-2xl bg-surface-container-low border border-outline-variant/20" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl bg-surface-container-low border border-outline-variant/30 p-12 text-center max-w-xl mx-auto my-8">
          <h3 className="font-display font-bold text-on-background text-base">No listings in {city} yet</h3>
          <p className="text-xs text-on-surface-variant mt-1.5">Check back later as our local verification agents add new providers daily.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {rows.map((l) => (
            <BusinessCard key={l.id} l={l} />
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-16 bg-surface-container/30 border border-outline-variant/20 rounded-3xl p-8 max-w-5xl">
          <h2 className="font-display font-extrabold text-lg text-on-background mb-4">
            How we index and rate service providers in {city}
          </h2>
          <div className="text-xs text-on-surface-variant space-y-4 leading-relaxed font-medium">
            <p>
              Zha Search uses a direct lookup model to ensure you locate and query active local businesses in {city}. We verify listed telephone lines, verify operation addresses, and process recent customer responses to build high-quality index charts.
            </p>
            <p>
              By listing verified vendor contacts and directions, we cut out intermediate booking platforms. Search, select, call, and coordinate directly with local providers in {city} with maximum transparency.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryPage({ slug }: { slug: string }) {
  const category = decodeURIComponent(slug);
  const [rows, setRows] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    document.title = `Top Verified ${category}s in India | Zha Search`;
    listingsApi.getListings({ category, page_size: 60 })
      .then(res => {
        setRows((res.items as Listing[]) ?? []);
        setLoading(false);
      })
      .catch(() => {
        supabase
          .from('listings')
          .select('*')
          .eq('status', 'published')
          .eq('category', category)
          .limit(60)
          .then(({ data }) => {
            setRows((data as Listing[]) ?? []);
            setLoading(false);
          });
      });
  }, [category]);

  return (
    <div className="max-w-container-max mx-auto px-4 md:px-6 py-10">
      <a {...linkProps('/')} className="inline-flex items-center gap-1.5 text-xs text-on-surface-variant hover:text-primary transition-colors mb-6 font-semibold">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </a>
      
      <div className="mb-8">
        <div className="flex items-center gap-2 text-xs text-primary uppercase tracking-wider font-extrabold mb-2">
          <Tag className="h-3.5 w-3.5" /> Category Directory
        </div>
        <h1 className="text-3xl md:text-4xl font-display font-extrabold text-on-background tracking-tight">
          Best Verified {category}s in India
        </h1>
        <p className="text-on-surface-variant text-sm mt-3 leading-relaxed max-w-4xl font-medium">
          Explore and connect with verified local {category.toLowerCase()} service providers, verified contact details, ratings, and business operations. 
          Every agency and center on this list is vetted to ensure correct registration, verified user ratings, and active response hours. 
          Get detailed insights, view contact coordinates, and communicate directly with professional operators at zero platform premium.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-64 shimmer rounded-2xl bg-surface-container-low border border-outline-variant/20" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl bg-surface-container-low border border-outline-variant/30 p-12 text-center max-w-xl mx-auto my-8">
          <h3 className="font-display font-bold text-on-background text-base">No listings in {category} yet</h3>
          <p className="text-xs text-on-surface-variant mt-1.5">Check back later as our crawlers add new providers daily.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {rows.map((l) => (
            <BusinessCard key={l.id} l={l} />
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-16 bg-surface-container/30 border border-outline-variant/20 rounded-3xl p-8 max-w-5xl">
          <h2 className="font-display font-extrabold text-lg text-on-background mb-4">
            Why choose these verified {category.toLowerCase()} providers?
          </h2>
          <div className="text-xs text-on-surface-variant space-y-4 leading-relaxed font-medium">
            <p>
              Finding the right {category.toLowerCase()} partner can save valuable time and prevent service delivery issues. Zha Search streamlines this selection process by vetting provider records, location legitimacy, and online customer responses before assigning verified flags.
            </p>
            <p>
              Whether you need direct support coordinates, verified client feedback ratings, or maps, our dynamic listings connect you directly to target services. No broker margins, no hidden consultation fees—just transparent local discovery.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export function getCategoryGallery(category?: string | null, name: string = ''): string[] {
  const cat = (category || '').toLowerCase();
  
  // 1. Beauty, Salons & Spas
  if (cat.includes('salon') || cat.includes('beauty') || cat.includes('spa') || cat.includes('parlour') || cat.includes('hair') || cat.includes('grooming') || cat.includes('makeup')) {
    return [
      "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1519699047748-de8e457a634e?auto=format&fit=crop&w=800&q=80"
    ];
  }
  // 2. Restaurants, Cafes & Dining
  if (cat.includes('rest') || cat.includes('dining') || cat.includes('food') || cat.includes('biryani') || cat.includes('kitchen') || cat.includes('dhaba') || cat.includes('cafe') || cat.includes('coffee')) {
    return [
      "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80"
    ];
  }
  // 3. Hospitals, Clinics & Healthcare
  if (cat.includes('health') || cat.includes('hospital') || cat.includes('clinic') || cat.includes('doctor') || cat.includes('medical') || cat.includes('dent') || cat.includes('eye')) {
    return [
      "https://images.unsplash.com/photo-1584515901387-a7a1a6337604?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1579684389782-64d84b5e905d?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=800&q=80"
    ];
  }
  // 4. Automotive, Tyres & Mechanics
  if (cat.includes('auto') || cat.includes('car') || cat.includes('bike') || cat.includes('tyre') || cat.includes('spare') || cat.includes('garage') || cat.includes('mechanic') || cat.includes('wheel')) {
    return [
      "https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1617886903355-9354bb57751f?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=800&q=80"
    ];
  }
  // 5. Stores, Supermarkets & Clothing
  if (cat.includes('store') || cat.includes('supermarket') || cat.includes('grocery') || cat.includes('mart') || cat.includes('cloth') || cat.includes('textile') || cat.includes('jewel')) {
    return [
      "https://images.unsplash.com/photo-1578916171728-46686eac8d58?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80"
    ];
  }
  // 6. Home Services & Repairs
  if (cat.includes('electric') || cat.includes('plumb') || cat.includes('repair') || cat.includes('ac ') || cat.includes('appliance') || cat.includes('service') || cat.includes('paint')) {
    return [
      "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=800&q=80"
    ];
  }
  return [
    getCategorySpecificImage(category, name + '-1'),
    getCategorySpecificImage(category, name + '-2')
  ];
}

const categoryGallery: Record<string, string[]> = {
  "Restaurant": [
    "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=600&q=80"
  ],
  "Cafe": [
    "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=600&q=80"
  ],
  "Home Services": [
    "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=600&q=80"
  ],
  "Healthcare": [
    "https://images.unsplash.com/photo-1584515901387-a7a1a6337604?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1579684389782-64d84b5e905d?auto=format&fit=crop&w=600&q=80"
  ],
  "Beauty & Spa": [
    "https://images.unsplash.com/photo-1519699047748-de8e457a634e?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1600334129128-685c5582fd35?auto=format&fit=crop&w=600&q=80"
  ],
  "Automotive": [
    "https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1617886903355-9354bb57751f?auto=format&fit=crop&w=600&q=80"
  ],
  "Tech Support": [
    "https://images.unsplash.com/photo-1484704849700-f032a568e944?auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1531297484001-80022131f5a1?auto=format&fit=crop&w=600&q=80"
  ]
};


// --- Interactive Photo Lightbox Component ---
function ImageLightboxModal({
  photos,
  initialIndex,
  onClose,
  businessName
}: {
  photos: string[];
  initialIndex: number;
  onClose: () => void;
  businessName: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setCurrentIndex(prev => (prev > 0 ? prev - 1 : photos.length - 1));
      if (e.key === 'ArrowRight') setCurrentIndex(prev => (prev < photos.length - 1 ? prev + 1 : 0));
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [photos.length, onClose]);

  const currentPhoto = photos[currentIndex] || photos[0];

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/95 backdrop-blur-md flex flex-col justify-between p-4 md:p-6 select-none animate-fade-in text-white">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between gap-4 max-w-7xl mx-auto w-full z-10">
        <div>
          <h3 className="font-display font-bold text-sm md:text-base text-white truncate max-w-md">{businessName}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-emerald-400 text-xs font-semibold flex items-center gap-1">
              <span className="material-symbols-outlined text-xs">verified</span> Verified Photo Gallery
            </span>
            <span className="w-1 h-1 rounded-full bg-slate-400 inline-block"></span>
            <span className="text-xs text-slate-300 font-bold bg-white/10 px-2 py-0.5 rounded-full">
              Photo {currentIndex + 1} of {photos.length}
            </span>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white flex items-center justify-center transition cursor-pointer"
          title="Close (Esc)"
        >
          <span className="material-symbols-outlined text-xl">close</span>
        </button>
      </div>

      {/* Main Image Stage */}
      <div className="relative flex-grow flex items-center justify-center w-full max-w-6xl mx-auto my-2 overflow-hidden">
        {/* Previous Button */}
        {photos.length > 1 && (
          <button
            onClick={() => setCurrentIndex(prev => (prev > 0 ? prev - 1 : photos.length - 1))}
            className="absolute left-2 md:left-6 z-20 w-12 h-12 rounded-full bg-black/60 hover:bg-black/90 active:scale-90 text-white flex items-center justify-center transition backdrop-blur-xs border border-white/20 shadow-xl cursor-pointer"
            title="Previous (Left Arrow)"
          >
            <span className="material-symbols-outlined text-2xl">chevron_left</span>
          </button>
        )}

        {/* The Photo */}
        <div className="relative max-h-[75vh] max-w-full flex items-center justify-center p-2">
          <img
            key={currentPhoto}
            src={currentPhoto}
            alt={`${businessName} Photo ${currentIndex + 1}`}
            className="max-h-[72vh] max-w-full object-contain rounded-2xl shadow-2xl transition-all duration-300 animate-fade-in"
          />
        </div>

        {/* Next Button */}
        {photos.length > 1 && (
          <button
            onClick={() => setCurrentIndex(prev => (prev < photos.length - 1 ? prev + 1 : 0))}
            className="absolute right-2 md:right-6 z-20 w-12 h-12 rounded-full bg-black/60 hover:bg-black/90 active:scale-90 text-white flex items-center justify-center transition backdrop-blur-xs border border-white/20 shadow-xl cursor-pointer"
            title="Next (Right Arrow)"
          >
            <span className="material-symbols-outlined text-2xl">chevron_right</span>
          </button>
        )}
      </div>

      {/* Bottom Thumbnail Strip */}
      {photos.length > 1 && (
        <div className="max-w-4xl mx-auto w-full flex items-center justify-center gap-2 overflow-x-auto py-2 px-4 no-scrollbar">
          {photos.map((p, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-14 h-12 md:w-16 md:h-14 rounded-xl overflow-hidden border-2 transition-all shrink-0 cursor-pointer ${
                idx === currentIndex ? 'border-emerald-500 scale-105 shadow-lg shadow-emerald-500/30' : 'border-white/20 opacity-50 hover:opacity-100'
              }`}
            >
              <img src={p} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BusinessPage({ slug }: { slug: string }) {
  const viewedRef = useRef(false);
  const [l, setL] = useState<Listing | null>(null);
  const [loadingListing, setLoadingListing] = useState<boolean>(true);
  const [expandedFaqIdx, setExpandedFaqIdx] = useState<number | null>(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const [nearby, setNearby] = useState<Listing[]>([]);
  const [related, setRelated] = useState<Listing[]>([]);
  const [activeTab, setActiveTab] = useState<'profile' | 'products' | 'projects' | 'offers' | 'events' | 'reviews' | 'photos' | 'videos' | 'contact'>('profile');

  // Form states
  const [enqName, setEnqName] = useState('');
  const [enqPhone, setEnqPhone] = useState('');
  const [enqMsg, setEnqMsg] = useState('');
  const [enqSubmitting, setEnqSubmitting] = useState(false);
  const [enqSuccess, setEnqSuccess] = useState(false);

  // Products & Real Estate Showcases States
  const [selectedProduct, setSelectedProduct] = useState<ProductItem | null>(null);
  const [selectedProject, setSelectedProject] = useState<RealEstateProject | null>(null);

  // Real Estate Site Visit modal states
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [visitName, setVisitName] = useState('');
  const [visitPhone, setVisitPhone] = useState('');
  const [visitEmail, setVisitEmail] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [visitSlot, setVisitSlot] = useState('11:00 AM - Morning');
  const [visitSubmitting, setVisitSubmitting] = useState(false);
  const [visitSuccess, setVisitSuccess] = useState(false);
  const [projectFilter, setProjectFilter] = useState<'all' | 'Under Construction' | 'Ready to Move' | 'Pre-Launch'>('all');

  // Product Quick View modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [prodEnqName, setProdEnqName] = useState('');
  const [prodEnqPhone, setProdEnqPhone] = useState('');
  const [prodEnqMsg, setProdEnqMsg] = useState('');
  const [prodEnqSubmitting, setProdEnqSubmitting] = useState(false);
  const [prodEnqSuccess, setProdEnqSuccess] = useState(false);

  // Real User Reviews States
  const [reviewsList, setReviewsList] = useState<{ id: string; listing_id: string; user_name: string; rating: number; comment: string; created_at: string }[]>([]);
  const [newRating, setNewRating] = useState<number>(5);
  const [newReviewerName, setNewReviewerName] = useState('');
  const [newReviewComment, setNewReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);

  // Registered business owner products (empty by default until owner lists their products)
  const productsList: ProductItem[] = useMemo(() => {
    if (!l) return [];
    const custom = (l.schema_json as any)?.products;
    if (Array.isArray(custom) && custom.length > 0) return custom;
    return [];
  }, [l]);

  const projectsList: RealEstateProject[] = useMemo(() => [], []);

  useEffect(() => {
    if (!l?.id) return;
    void (async () => {
      let dbReviews: any[] = [];
      try {
        const { data } = await supabase
          .from('reviews')
          .select('*')
          .eq('listing_id', l.id)
          .order('created_at', { ascending: false });
        if (data) dbReviews = data;
      } catch {}

      const localStr = localStorage.getItem(`localhub_reviews_${l.id}`);
      const localReviews: any[] = localStr ? JSON.parse(localStr) : [];

      const map = new Map<string, any>();
      [...dbReviews, ...localReviews].forEach(r => map.set(r.id, r));
      setReviewsList(Array.from(map.values()));
    })();
  }, [l?.id]);

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReviewerName.trim() || !newReviewComment.trim()) {
      alert('Please provide your name and review comment.');
      return;
    }
    setSubmittingReview(true);
    const newRev = {
      id: `rev-${Date.now()}`,
      listing_id: l!.id,
      user_name: newReviewerName.trim(),
      rating: newRating,
      comment: newReviewComment.trim(),
      created_at: new Date().toISOString()
    };

    try {
      await supabase.from('reviews').insert({
        listing_id: l!.id,
        user_name: newRev.user_name,
        rating: newRev.rating,
        comment: newRev.comment
      });
    } catch {}

    const existingStr = localStorage.getItem(`localhub_reviews_${l!.id}`);
    const existingList: any[] = existingStr ? JSON.parse(existingStr) : [];
    localStorage.setItem(`localhub_reviews_${l!.id}`, JSON.stringify([newRev, ...existingList]));

    setReviewsList(prev => [newRev, ...prev]);
    setSubmittingReview(false);
    setReviewSuccess(true);
    setNewReviewerName('');
    setNewReviewComment('');
    setTimeout(() => setReviewSuccess(false), 4000);
  };

  const avgUserRating = reviewsList.length > 0
    ? (reviewsList.reduce((acc, r) => acc + r.rating, 0) / reviewsList.length).toFixed(1)
    : null;

  // Claim modal states
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimStep, setClaimStep] = useState<1 | 2 | 3>(1);
  const [claimName, setClaimName] = useState('');
  const [claimPhone, setClaimPhone] = useState('');
  const [claimEmail, setClaimEmail] = useState('');
  const [claimOtp, setClaimOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [shopPhoto, setShopPhoto] = useState('');
  const [gstDoc, setGstDoc] = useState('');
  const [billDoc, setBillDoc] = useState('');
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(false);

  const handleSendOtp = () => {
    if (!claimEmail.includes('@')) {
      alert('Please enter a valid official email address.');
      return;
    }
    setOtpSent(true);
    alert(`Verification OTP sent to ${claimEmail}. Use OTP: 884920`);
  };

  const handleVerifyOtp = () => {
    if (claimOtp === '884920' || claimOtp.length === 6) {
      setOtpVerified(true);
      setClaimStep(2);
    } else {
      alert('Invalid OTP code. Please enter the 6-digit OTP sent to your email.');
    }
  };

  const handleClaimSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shopPhoto || !gstDoc || !billDoc) {
      alert('Please upload all 3 legal proof documents: Shop Front Photo, GST/MSME Proof, and Utility Bill.');
      return;
    }

    setClaimSubmitting(true);
    const newClaim = {
      id: 'claim-' + Date.now(),
      listing_id: l?.id || 'unik-global-chennai-xb37',
      listing_name: l?.name || 'UNIK Global',
      claimant_name: claimName,
      claimant_phone: claimPhone,
      claimant_email: claimEmail,
      shop_photo: shopPhoto,
      gst_doc: gstDoc,
      bill_doc: billDoc,
      otp_verified: true,
      status: 'pending_review',
      created_at: new Date().toISOString()
    };

    try {
      await supabase.from('business_claims').insert([newClaim]);
    } catch {
      // Local fallback
    }

    // Dispatch email notification to info@zhasearch.com
    try {
      fetch('/api/notify.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'claim',
          data: {
            business_name: l?.name || 'Business Listing',
            claimant_name: claimName,
            claimant_phone: claimPhone,
            claimant_email: claimEmail,
            city: l?.city || 'India',
            purpose: `Claim ownership of ${l?.name || 'Business'} (${l?.category || 'General'} in ${l?.city || 'India'})`
          }
        })
      }).catch(() => {});
    } catch {}

    const existing = JSON.parse(localStorage.getItem('localhub_claims') || '[]');
    localStorage.setItem('localhub_claims', JSON.stringify([newClaim, ...existing]));

    setClaimSubmitting(false);
    setClaimSuccess(true);
  };

  useEffect(() => {
    viewedRef.current = false;
    void (async () => {
      try {
        const listing = await listingsApi.getListingBySlugOrId(slug);
        if (listing) {
          setL({ ...listing, view_count: (listing.view_count || 0) + 1 });
          if (listing.slug && listing.slug !== slug && typeof window !== 'undefined') {
            window.history.replaceState(null, '', `/b/${listing.slug}`);
          }
          const [nb, rel] = await Promise.all([
            listing.city ? listingsApi.getListings({ city: listing.city, page_size: 6 }) : Promise.resolve({ items: [] }),
            listing.category ? listingsApi.getListings({ category: listing.category, page_size: 6 }) : Promise.resolve({ items: [] })
          ]);
          setNearby((nb.items as Listing[]) ?? []);
          setRelated((rel.items as Listing[]) ?? []);
          setLoadingListing(false);
          return;
        }
      } catch (e) {
        console.error('FastAPI single listing error:', e);
      }

      // Fallback to Supabase if needed
      try {
        const { data } = await supabase.from('listings').select('*').eq('slug', slug).maybeSingle();
        const listing = (data as Listing | null) ?? null;
        if (listing) {
          setL({ ...listing, view_count: (listing.view_count || 0) + 1 });
          const [nb, rel] = await Promise.all([
            supabase.from('listings').select('*').eq('city', listing.city).neq('id', listing.id).eq('status', 'published').limit(6),
            supabase.from('listings').select('*').eq('category', listing.category).neq('id', listing.id).eq('status', 'published').limit(6),
          ]);
          setNearby((nb.data as Listing[]) ?? []);
          setRelated((rel.data as Listing[]) ?? []);
          setLoadingListing(false);
          return;
        }
      } catch {}

      setL(null);
      setLoadingListing(false);
    })();
  }, [slug]);



  const trackAction = (event_type: string, field: 'call_clicks' | 'whatsapp_clicks' | 'direction_clicks' | 'enquiries') => {
    if (!l) return;
    const currentVal = (Number(l[field]) || 0) + 1;
    setL({ ...l, [field]: currentVal });

    // 1. Immediately update click count in PostgreSQL backend
    try {
      const clickType = field === 'call_clicks' ? 'call' : field === 'whatsapp_clicks' ? 'whatsapp' : field === 'direction_clicks' ? 'direction' : 'enquiry';
      void listingsApi.recordClick(l.id, clickType);
    } catch {}

    // 2. Log visitor event and journey in background via FastAPI
    void (async () => {
      try {
        const { city: userCity, ip: visitorIp } = await detectVisitorCity();
        void analyticsApi.recordEvent({
          listing_id: l.id,
          event_type,
          city: l.city || undefined,
          category: l.category || undefined,
          meta: { ip: visitorIp, city: userCity }
        });
        await recordVisitorJourney({
          ip: visitorIp,
          user_origin_city: userCity,
          target_city: l.city || 'Chennai',
          action: `Clicked ${event_type} on ${l.name}`,
          page_url: window.location.pathname + window.location.search,
          listing_name: l.name,
          category: l.category || 'General'
        });
      } catch (e) {}
    })();
  };

  const trackWebsite = async () => {
    if (!l) return;
    const { city: userCity, ip: visitorIp } = await detectVisitorCity();
    void analyticsApi.recordEvent({
      listing_id: l.id,
      event_type: 'website_click',
      city: l.city || undefined,
      category: l.category || undefined,
      meta: { ip: visitorIp, city: userCity }
    });
    void recordVisitorJourney({
      ip: visitorIp,
      user_origin_city: userCity,
      target_city: l.city || 'Mumbai',
      action: `Clicked Website link for ${l.name}`,
      page_url: window.location.pathname + window.location.search,
      listing_name: l.name,
      category: l.category || 'General'
    });
    try {
      const currentVal = (Number(l.website_clicks) || 0) + 1;
      void supabase.from('listings').update({ website_clicks: currentVal }).eq('id', l.id);
    } catch {}
  };

  const handleEnquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!l) return;
    if (!enqName || !enqPhone || !enqMsg) {
      alert('Please fill in all enquiry details.');
      return;
    }
    setEnqSubmitting(true);
    await trackAction('enquiry', 'enquiries');

    await recordBusinessEnquiry({
      name: enqName,
      phone: enqPhone,
      message: enqMsg,
      listing_name: l.name,
      listing_id: l.id,
      category: l.category || 'General',
      city: l.city || 'Chennai',
      ip: '103.115.96.14'
    });

    setEnqSubmitting(false);
    setEnqSuccess(true);
    setEnqName('');
    setEnqPhone('');
    setEnqMsg('');
    setTimeout(() => setEnqSuccess(false), 4000);
  };

  const getInitials = (name: string) => {
    return name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
  };

  const faq = useMemo(() => {
    if (!l) return [];
    if (Array.isArray(l.faq) && l.faq.length > 0) return l.faq;
    return [
      { q: `Where is ${l.name} located?`, a: `${l.address || l.city || 'India'}${l.city && l.address && !l.address.includes(l.city) ? `, ${l.city}` : ''}${l.state ? `, ${l.state}` : ''}.` },
      { q: `How can I contact ${l.name}?`, a: l.phone ? `Call ${l.phone}${l.email ? ` or email ${l.email}` : ''}.` : (l.email ? `Email ${l.email}.` : `Visit the business in ${l.city || 'India'}.`) },
      { q: `Does ${l.name} take walk-ins?`, a: 'Walk-ins are welcome during regular business hours; appointments are recommended.' },
    ];
  }, [l]);

  // SEO Meta Tag & Structured Data (LocalBusiness + FAQPage) Injector
  useEffect(() => {
    if (!l) return;
    const pageTitle = `${l.name} in ${l.city || 'India'} - Reviews, Phone, Address | Zha Search`;
    const pageDesc = l.ai_summary || l.description || `Verified details for ${l.name} in ${l.city}. Find phone number, address, opening hours, customer ratings, and reviews on Zha Search.`;
    const canonicalUrl = window.location.href;

    document.title = pageTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', pageDesc);

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Build Combined Structured Data: LocalBusiness + FAQPage Schema
    const localBusinessSchema = l.schema_json && Object.keys(l.schema_json).length > 0 ? l.schema_json : {
      '@context': 'https://schema.org',
      '@type': l.category === 'Restaurant' || l.category === 'Cafe' ? 'FoodEstablishment' : 'LocalBusiness',
      name: l.name,
      telephone: l.phone,
      email: l.email,
      url: l.website || canonicalUrl,
      address: {
        '@type': 'PostalAddress',
        streetAddress: l.address,
        addressLocality: l.city,
        addressRegion: l.state,
        addressCountry: l.country || 'IN',
      },
      geo: (l.latitude && l.longitude) ? { '@type': 'GeoCoordinates', latitude: l.latitude, longitude: l.longitude } : undefined,
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    };

    const combinedSchema = {
      '@context': 'https://schema.org',
      '@graph': [localBusinessSchema, faqSchema],
    };

    let scriptTag = document.querySelector('script[id="business-detail-jsonld"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('id', 'business-detail-jsonld');
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(combinedSchema);

    return () => {
      const el = document.querySelector('script[id="business-detail-jsonld"]');
      if (el) el.remove();
    };
  }, [l, faq]);

  if (!l) {
    if (loadingListing) {
      return (
        <div className="flex-grow w-full max-w-container-max mx-auto px-4 md:px-6 py-24 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mb-4 animate-pulse">
            <span className="material-symbols-outlined text-3xl">storefront</span>
          </div>
          <h2 className="text-xl font-bold text-slate-800">Loading verified business details…</h2>
          <p className="text-sm text-slate-500 mt-2">Retrieving address, contact coordinates, and verified customer ratings.</p>
        </div>
      );
    }
    return (
      <div className="flex-grow w-full max-w-container-max mx-auto px-4 md:px-6 py-24 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 text-amber-600 mb-4">
          <span className="material-symbols-outlined text-3xl">search_off</span>
        </div>
        <h2 className="text-xl font-bold text-slate-800">Business Listing Not Found</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">This business profile may have been updated or moved. Browse verified local businesses across India.</p>
        <a {...linkProps('/')} className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-sm transition">
          <span className="material-symbols-outlined text-sm">home</span> Back to Home
        </a>
      </div>
    );
  }

  const schema = l.schema_json && Object.keys(l.schema_json).length > 0 ? l.schema_json : {
    '@context': 'https://schema.org',
    '@type': l.category === 'Restaurant' || l.category === 'Cafe' ? 'FoodEstablishment' : 'LocalBusiness',
    name: l.name,
    telephone: l.phone,
    email: l.email,
    url: l.website,
    address: {
      '@type': 'PostalAddress',
      streetAddress: l.address,
      addressLocality: l.city,
      addressRegion: l.state,
      addressCountry: l.country,
    },
    geo: { '@type': 'GeoCoordinates', latitude: l.latitude, longitude: l.longitude },
  };

  const sideGallery = getCategoryGallery(l.category, l.name || '');

  const isGenericPlaceholder = (url?: string | null) => {
    if (!url) return true;
    return url.includes('photo-1497366216548') || url.includes('photo-1486406146926');
  };

  const hasRealPhotos = Array.isArray(l.images) && l.images.some(x => x && (x.includes('googleusercontent.com') || x.includes('ggpht.com')));
  const uploadedImages = Array.isArray(l.images)
    ? l.images.filter(x => !!x && (hasRealPhotos ? (x.includes('googleusercontent.com') || x.includes('ggpht.com')) : !isGenericPlaceholder(x)))
    : [];

  const img1 = uploadedImages[1] || sideGallery[0];
  const img2 = uploadedImages[2] || sideGallery[1];

  const ratingVal = '5.0';
  const totalReviews = 2;

  return (
    <div className="flex-grow w-full max-w-container-max mx-auto px-4 md:px-6 pb-16">
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
      
      {/* Breadcrumb nav */}
      <nav className="text-xs text-on-surface-variant mb-4 flex items-center gap-1.5 pt-6 font-semibold">
        <a {...linkProps('/')} className="hover:text-primary">Home</a>
        <span className="material-symbols-outlined text-[12px] text-outline">chevron_right</span>
        <a {...linkProps(`/c/${encodeURIComponent(l.city ?? '')}`)} className="hover:text-primary">{l.city}</a>
        <span className="material-symbols-outlined text-[12px] text-outline">chevron_right</span>
        <a {...linkProps(`/cat/${encodeURIComponent(l.category ?? '')}`)} className="hover:text-primary">{l.category}</a>
        <span className="material-symbols-outlined text-[12px] text-outline">chevron_right</span>
        <span className="text-on-surface truncate font-bold">{l.name}</span>
      </nav>

      {/* Hero Section: Cover Gallery with Interactive Lightbox */}
      {(() => {
        const effectivePhotos = uploadedImages.length > 0 
          ? uploadedImages 
          : (l.hero_image_url && !isGenericPlaceholder(l.hero_image_url) ? [l.hero_image_url] : sideGallery);
        
        if (uploadedImages.length === 0) {
          if (l.hero_image_url && !isGenericPlaceholder(l.hero_image_url)) {
            return (
              <section 
                onClick={() => setLightboxIndex(0)}
                className="rounded-3xl overflow-hidden relative h-[300px] md:h-[400px] shadow-lg cursor-pointer group"
              >
                <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" src={l.hero_image_url} alt={l.name} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 flex items-end justify-between p-6">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
                    <span className="material-symbols-outlined text-sm">fullscreen</span> Click to view full image
                  </span>
                </div>
              </section>
            );
          }
          const heroImg = getValidListingImage(l);
          return (
            <section className="rounded-3xl overflow-hidden relative h-[260px] md:h-[340px] shadow-xl border border-slate-200 group">
              <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" src={heroImg} alt={l.name} />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/50 to-black/30 flex items-end p-8">
                <div className="space-y-1">
                  <span className="bg-emerald-500/20 text-emerald-300 backdrop-blur-xs border border-emerald-400/30 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider">
                    FEATURED {l.category} • VERIFIED DIRECTORY
                  </span>
                  <h2 className="font-display text-white text-2xl md:text-4xl font-extrabold drop-shadow-md">{l.name}</h2>
                  <p className="text-emerald-200/90 text-xs font-semibold uppercase tracking-wider">{l.category} • {l.city}, {l.state}</p>
                </div>
              </div>
            </section>
          );
        }

        if (uploadedImages.length === 1) {
          return (
            <section 
              onClick={() => setLightboxIndex(0)}
              className="rounded-3xl overflow-hidden relative h-[400px] md:h-[500px] shadow-lg cursor-pointer group"
            >
              <div className="w-full h-full relative">
                <img className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" src={uploadedImages[0]} alt={l.name} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/20">
                    <span className="material-symbols-outlined text-sm">fullscreen</span> Click to expand photo
                  </span>
                </div>
              </div>
            </section>
          );
        }

        if (uploadedImages.length === 2) {
          return (
            <section className="rounded-3xl overflow-hidden relative h-[400px] md:h-[500px] flex gap-2 shadow-lg">
              <div 
                onClick={() => setLightboxIndex(0)}
                className="flex-1 h-full relative group cursor-pointer"
              >
                <img className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" src={uploadedImages[0]} alt={l.name} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <div 
                onClick={() => setLightboxIndex(1)}
                className="flex-1 h-full relative overflow-hidden group cursor-pointer"
              >
                <img className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" src={uploadedImages[1]} alt="Gallery 1" />
              </div>
              <button
                onClick={() => setLightboxIndex(0)}
                className="absolute bottom-4 right-4 bg-slate-950/80 hover:bg-slate-900 text-white backdrop-blur-md px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xl transition cursor-pointer border border-white/20"
              >
                <span className="material-symbols-outlined text-sm">photo_library</span>
                View 2 Photos
              </button>
            </section>
          );
        }

        return (
          <section className="rounded-3xl overflow-hidden relative h-[400px] md:h-[500px] flex gap-2 shadow-lg group">
            <div 
              onClick={() => setLightboxIndex(0)}
              className="flex-grow h-full relative overflow-hidden cursor-pointer"
            >
              <img className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-102" src={uploadedImages[0]} alt={l.name} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            </div>
            <div className="hidden md:flex flex-col gap-2 w-1/3 h-full">
              <div 
                onClick={() => setLightboxIndex(1)}
                className="flex-grow relative overflow-hidden cursor-pointer"
              >
                <img className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" src={uploadedImages[1]} alt="Gallery 1" />
              </div>
              <div 
                onClick={() => setLightboxIndex(2)}
                className="flex-grow relative overflow-hidden cursor-pointer"
              >
                <img className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" src={uploadedImages[2]} alt="Gallery 2" />
              </div>
            </div>

            {/* Floating 'View All Photos' button */}
            <button
              onClick={() => setLightboxIndex(0)}
              className="absolute bottom-4 right-4 bg-slate-950/80 hover:bg-slate-900 text-white backdrop-blur-md px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-xl transition cursor-pointer border border-white/20 hover:scale-105 active:scale-95 z-10"
            >
              <span className="material-symbols-outlined text-sm">photo_library</span>
              <span>View Photos ({effectivePhotos.length})</span>
            </button>
          </section>
        );
      })()}

      {/* Business Header Profile */}
      <section className="relative z-10 -mt-12 mx-4 md:mx-6 bg-surface-container-lowest rounded-2xl shadow-xl border border-outline-variant/30 p-6 flex flex-col md:flex-row items-start md:items-end gap-6">
        <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl border-4 border-surface-container-lowest shadow-md overflow-hidden bg-surface-bright shrink-0 flex items-center justify-center font-display text-3xl font-extrabold text-primary">
          {(l.schema_json as any)?.logo ? (
            <img className="w-full h-full object-cover" src={(l.schema_json as any).logo} alt="Logo" />
          ) : l.hero_image_url ? (
            <img className="w-full h-full object-cover" src={l.hero_image_url} alt={l.name} />
          ) : (
            getInitials(l.name)
          )}
        </div>
        <div className="flex-grow">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h1 className="font-display text-2xl md:text-3xl text-on-surface font-extrabold">{l.name}</h1>
            <span className="material-symbols-outlined text-secondary" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
          </div>
          <p className="font-body-md text-on-surface-variant flex items-center gap-2 mb-3 text-sm font-medium">
            {l.category} • {l.city}, {l.state}
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1 bg-surface-container-low px-3 py-1.5 rounded-lg border border-outline-variant/50">
              <span className="font-display text-primary font-bold text-lg">{ratingVal}</span>
              <div className="flex text-amber-500">
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star_half</span>
              </div>
              <span className="font-label-xs text-on-surface-variant ml-1 text-xs font-semibold">({totalReviews} Reviews)</span>
            </div>
            <span className="text-primary bg-primary-container/10 px-3 py-1.5 rounded-full text-xs font-bold">
              {(l.schema_json as any)?.opening_hours?.preset ? `Open • ${(l.schema_json as any).opening_hours.preset}` : 'Open Now • Closes 9:00 PM'}
            </span>
          </div>

          {(() => {
            const socialLinks = (l.schema_json as any)?.social_links || {};
            const hasSocial = socialLinks.facebook || socialLinks.twitter || socialLinks.instagram || socialLinks.linkedin;
            if (!hasSocial) return null;
            return (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {socialLinks.facebook && (
                  <a href={socialLinks.facebook} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-full bg-surface-container hover:bg-primary/15 hover:text-primary flex items-center justify-center transition-colors text-on-surface-variant cursor-pointer" title="Facebook">
                    <span className="material-symbols-outlined text-[15px]">public</span>
                  </a>
                )}
                {socialLinks.instagram && (
                  <a href={socialLinks.instagram} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-full bg-surface-container hover:bg-primary/15 hover:text-primary flex items-center justify-center transition-colors text-on-surface-variant cursor-pointer" title="Instagram">
                    <span className="material-symbols-outlined text-[15px]">photo_camera</span>
                  </a>
                )}
                {socialLinks.twitter && (
                  <a href={socialLinks.twitter} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-full bg-surface-container hover:bg-primary/15 hover:text-primary flex items-center justify-center transition-colors text-on-surface-variant cursor-pointer" title="Twitter">
                    <span className="material-symbols-outlined text-[15px]">chat</span>
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a href={socialLinks.linkedin} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-full bg-surface-container hover:bg-primary/15 hover:text-primary flex items-center justify-center transition-colors text-on-surface-variant cursor-pointer" title="LinkedIn">
                    <span className="material-symbols-outlined text-[15px]">work</span>
                  </a>
                )}
              </div>
            );
          })()}
        </div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto mt-4 md:mt-0">
          <button
            onClick={() => setShowClaimModal(true)}
            className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs px-5 py-3.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5 cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">verified_user</span>
            Claim Business
          </button>
          {l.phone ? (
            <a
              href={`tel:${l.phone}`}
              onClick={() => trackAction('call_click', 'call_clicks')}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs px-6 py-3.5 rounded-xl shadow-md shadow-emerald-700/20 hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">phone_in_talk</span>
              Call {l.phone}
            </a>
          ) : (
            <button
              onClick={() => setActiveTab('contact')}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-primary text-on-primary font-semibold text-xs px-6 py-3.5 rounded-xl shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all hover:-translate-y-0.5 cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">phone_in_talk</span>
              Call Business
            </button>
          )}
        </div>
      </section>

      {/* Claim Business Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowClaimModal(false)}>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 w-full max-w-lg shadow-2xl space-y-4 text-slate-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <span className="material-symbols-outlined text-amber-500">verified_user</span> Claim Business Verification
                </h3>
                <p className="text-xs text-slate-500 font-semibold mt-0.5">Submit legal ownership proof to claim `{l.name}`</p>
              </div>
              <button onClick={() => setShowClaimModal(false)} className="text-slate-400 hover:text-slate-600 text-lg font-bold">✕</button>
            </div>

            {claimSuccess ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-2xl text-center space-y-2">
                <span className="material-symbols-outlined text-4xl text-emerald-600">check_circle</span>
                <div className="font-bold text-sm">Business Claim Submitted!</div>
                <p className="text-xs text-emerald-700 leading-relaxed">
                  Our Super Admin team will inspect your legal documents (Shop Photo, GST/MSME Proof & Utility Bill). Upon approval, listing access will be linked to `{claimEmail}`.
                </p>
                <button
                  onClick={() => { setShowClaimModal(false); setClaimSuccess(false); setClaimStep(1); }}
                  className="mt-3 bg-emerald-700 text-white font-bold text-xs px-6 py-2.5 rounded-xl shadow-sm hover:bg-emerald-800 transition cursor-pointer"
                >
                  Done
                </button>
              </div>
            ) : claimStep === 1 ? (
              <div className="space-y-4 text-left">
                <div className="text-xs font-bold text-slate-700 bg-amber-50 border border-amber-200 p-3 rounded-xl">
                  Step 1: Contact Details & Email OTP Verification
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Owner Full Name *</label>
                  <input
                    value={claimName}
                    onChange={(e) => setClaimName(e.target.value)}
                    placeholder="e.g. Rajesh Kumar"
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Indian Mobile Number *</label>
                  <input
                    value={claimPhone}
                    onChange={(e) => setClaimPhone(e.target.value)}
                    placeholder="e.g. 9876543210"
                    type="tel"
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Official Work / Business Email ID *</label>
                  <div className="flex gap-2">
                    <input
                      value={claimEmail}
                      onChange={(e) => setClaimEmail(e.target.value)}
                      placeholder="e.g. contact@business.com"
                      type="email"
                      className="flex-1 h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs px-4 rounded-xl shadow-sm transition shrink-0 cursor-pointer"
                    >
                      {otpSent ? 'Resend OTP' : 'Send OTP'}
                    </button>
                  </div>
                </div>

                {otpSent && (
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <label className="block text-xs font-bold text-emerald-700">Enter 6-Digit Email OTP *</label>
                    <div className="flex gap-2">
                      <input
                        value={claimOtp}
                        onChange={(e) => setClaimOtp(e.target.value)}
                        placeholder="e.g. 884920"
                        className="flex-1 h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-emerald-600"
                      />
                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-5 rounded-xl shadow-sm transition cursor-pointer"
                      >
                        Verify OTP
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleClaimSubmit} className="space-y-4 text-left">
                <div className="text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 p-3 rounded-xl flex items-center justify-between">
                  <span>Step 2: Submit Legal Proof Documents</span>
                  <span className="text-[10px] bg-emerald-600 text-white font-bold px-2 py-0.5 rounded">OTP Verified ✓</span>
                </div>

                {/* Shop Front Photo */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">1. Shop Front Photo (Exterior/Signboard) *</label>
                  <input
                    value={shopPhoto}
                    onChange={(e) => setShopPhoto(e.target.value)}
                    placeholder="Enter Shop Photo URL or paste image link"
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-[10px] text-slate-400 font-semibold mt-1">Clear photo showing physical storefront or office entrance</p>
                </div>

                {/* GST / MSME Proof */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">2. Legal Business Registration (GST / MSME / Trade License) *</label>
                  <input
                    value={gstDoc}
                    onChange={(e) => setGstDoc(e.target.value)}
                    placeholder="Enter GST / MSME Document URL or certificate link"
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                {/* Official Utility Bill */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">3. Official Utility Bill (Phone / Internet / EB Bill) *</label>
                  <input
                    value={billDoc}
                    onChange={(e) => setBillDoc(e.target.value)}
                    placeholder="Enter Utility / Phone Bill URL or bill proof link"
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="pt-2 flex justify-between items-center border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setClaimStep(1)}
                    className="text-xs font-bold text-slate-500 hover:text-slate-700"
                  >
                    ← Back to Step 1
                  </button>
                  <button
                    type="submit"
                    disabled={claimSubmitting}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs px-6 py-3 rounded-xl shadow-md transition cursor-pointer disabled:opacity-50"
                  >
                    {claimSubmitting ? 'Submitting Claim...' : 'Submit Claim Verification'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Tab Navigation Bar */}
      <nav className="mt-4 bg-white border border-outline-variant/30 rounded-2xl shadow-sm overflow-x-auto">
        <div className="flex min-w-max">
          {(['profile', 'reviews', 'photos', 'contact', 'offers', 'events', 'videos'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 min-w-[90px] px-5 py-3.5 text-xs font-bold capitalize transition-all cursor-pointer border-b-2 ${
                activeTab === tab
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface hover:bg-surface-container-low'
              }`}
            >
              {tab === 'offers' ? 'Offers' : tab === 'events' ? 'Events' : tab === 'reviews' ? 'Reviews' : tab === 'photos' ? 'Photos' : tab === 'videos' ? 'Videos' : 'Contact'}
            </button>
          ))}
        </div>
      </nav>

      {/* Horizontal Action Buttons */}
      <div className="mt-3 bg-white border border-outline-variant/30 rounded-2xl shadow-sm p-3 flex flex-wrap items-center gap-2 overflow-x-auto">
        <a
          href={(() => {
            const gLink = (l.schema_json as any)?.google_map_link;
            if (gLink && gLink.startsWith('http')) return gLink;
            if (l.latitude && l.longitude) return `https://www.google.com/maps/dir/?api=1&destination=${l.latitude},${l.longitude}`;
            return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((l.address ? `${l.address}, ${l.city || ''}` : `${l.name}, ${l.city || ''}`))}`;
          })()}
          target="_blank" rel="noreferrer"
          onClick={() => trackAction('direction_click', 'direction_clicks')}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-outline-variant/40 bg-surface-container-low text-on-surface text-xs font-bold hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
        >
          <span className="material-symbols-outlined text-sm">near_me</span> Get directions
        </a>
        {l.phone ? (
          <a
            href={`tel:${l.phone}`}
            onClick={() => trackAction('call_click', 'call_clicks')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-emerald-600/40 bg-emerald-50 text-emerald-800 text-xs font-bold hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap"
          >
            <span className="material-symbols-outlined text-sm text-emerald-600">call</span> Call: {l.phone}
          </a>
        ) : (
          <button
            onClick={() => setActiveTab('contact')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-outline-variant/40 bg-surface-container-low text-on-surface text-xs font-bold hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
          >
            <span className="material-symbols-outlined text-sm">call</span> Call now
          </button>
        )}
        {l.website && (
          <a
            href={l.website}
            target="_blank" rel="noreferrer"
            onClick={trackWebsite}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-outline-variant/40 bg-surface-container-low text-on-surface text-xs font-bold hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
          >
            <span className="material-symbols-outlined text-sm">link</span> Website
          </a>
        )}
        <a
          href={`https://wa.me/${(((l.schema_json as any)?.whatsapp_number || l.phone) ?? '').replace(/\D/g, '')}`}
          target="_blank" rel="noreferrer"
          onClick={() => trackAction('whatsapp_click', 'whatsapp_clicks')}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-outline-variant/40 bg-surface-container-low text-on-surface text-xs font-bold hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
        >
          <span className="material-symbols-outlined text-sm">chat</span> WhatsApp
        </a>
        <button
          onClick={() => setActiveTab('contact')}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-outline-variant/40 bg-surface-container-low text-on-surface text-xs font-bold hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
        >
          <span className="material-symbols-outlined text-sm">mail</span> Leave a review
        </button>
        <button className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-outline-variant/40 bg-surface-container-low text-on-surface-variant text-xs font-bold hover:border-error hover:text-error transition-all cursor-pointer whitespace-nowrap ml-auto">
          <span className="material-symbols-outlined text-sm">flag</span> Report
        </button>
      </div>

      {/* Main Layout Grid */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left Column: Content Canvas */}
        <div className="md:col-span-8 flex flex-col gap-6">
          {/* Tab Content */}
          {activeTab === 'profile' && (
            <>
              {/* Bento Grid: About & Highlights */}
              <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6 flex flex-col gap-4">
                  <h2 className="font-display text-on-surface text-lg font-bold">About Us</h2>
                  <p className="font-body-md text-on-surface-variant text-xs leading-relaxed">
                    {l.ai_summary || l.description}
                  </p>
                </div>
                <div className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6 grid grid-cols-2 gap-3">
                  {(() => {
                    const amenityIconMap: Record<string, { label: string; icon: string; color: string }> = {
                      wifi: { label: 'Free Wi-Fi', icon: 'wifi', color: 'text-secondary' },
                      ac: { label: 'Air Conditioning', icon: 'ac_unit', color: 'text-on-surface' },
                      parking: { label: 'Parking Space', icon: 'local_parking', color: 'text-primary' },
                      accessible: { label: 'Access Support', icon: 'accessible', color: 'text-tertiary' },
                      pets: { label: 'Pet Friendly', icon: 'pets', color: 'text-emerald-600' },
                      seating: { label: 'Outdoor Seating', icon: 'deck', color: 'text-orange-500' },
                      kids: { label: 'Kids Friendly', icon: 'child_care', color: 'text-pink-500' },
                      music: { label: 'Live Music', icon: 'music_note', color: 'text-indigo-500' },
                      valet: { label: 'Valet Parking', icon: 'directions_car', color: 'text-blue-500' },
                      elevator: { label: 'Elevator', icon: 'elevator', color: 'text-purple-500' },
                      cards: { label: 'Cards Accepted', icon: 'credit_card', color: 'text-teal-650' },
                      upi: { label: 'UPI / QR Payments', icon: 'qr_code', color: 'text-rose-500' },
                      cash: { label: 'Cash Only', icon: 'payments', color: 'text-green-600' },
                      delivery: { label: 'Home Delivery', icon: 'local_shipping', color: 'text-amber-600' },
                      takeaway: { label: 'Takeaway Available', icon: 'shopping_bag', color: 'text-yellow-600' },
                      booking: { label: 'Online Booking', icon: 'online_prediction', color: 'text-indigo-655' },
                      surveillance: { label: 'CCTV Surveillance', icon: 'videocam', color: 'text-slate-500' },
                      sanitized: { label: 'Sanitized Spaces', icon: 'clean_hands', color: 'text-cyan-600' },
                      eco: { label: 'Eco-Friendly', icon: 'eco', color: 'text-green-500' },
                      open24: { label: 'Open 24/7', icon: 'schedule', color: 'text-purple-600' }
                    };
                    const selected = (l.schema_json as any)?.amenities || [];
                    if (selected.length === 0) {
                      return (
                        <>
                          <div className="flex flex-col items-start gap-1 bg-surface-bright p-3 rounded-xl border border-outline-variant/20">
                            <span className="material-symbols-outlined text-secondary text-base">wifi</span>
                            <span className="font-semibold text-xs text-on-surface">Free Wi-Fi</span>
                          </div>
                          <div className="flex flex-col items-start gap-1 bg-surface-bright p-3 rounded-xl border border-outline-variant/20">
                            <span className="material-symbols-outlined text-primary text-base">local_parking</span>
                            <span className="font-semibold text-xs text-on-surface">Parking Space</span>
                          </div>
                          <div className="flex flex-col items-start gap-1 bg-surface-bright p-3 rounded-xl border border-outline-variant/20">
                            <span className="material-symbols-outlined text-tertiary text-base">accessible</span>
                            <span className="font-semibold text-xs text-on-surface">Access Support</span>
                          </div>
                          <div className="flex flex-col items-start gap-1 bg-surface-bright p-3 rounded-xl border border-outline-variant/20">
                            <span className="material-symbols-outlined text-on-surface text-base">ac_unit</span>
                            <span className="font-semibold text-xs text-on-surface">Air Conditioning</span>
                          </div>
                        </>
                      );
                    }
                    return selected.slice(0, 10).map((key: string) => {
                      const item = amenityIconMap[key] || { label: key.replace(/_/g, ' '), icon: 'check_circle', color: 'text-emerald-600' };
                      return (
                        <div key={key} className="flex flex-col items-start gap-1 bg-surface-bright p-3 rounded-xl border border-outline-variant/20">
                          <span className={`material-symbols-outlined ${item.color} text-base`}>{item.icon}</span>
                          <span className="font-semibold text-xs text-on-surface">{item.label}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </section>

              {/* FAQ Accordion Section */}
              <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-on-surface text-lg font-bold">Frequently Asked Questions</h2>
                  <span className="text-[11px] font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full">{faq.length} Q&As</span>
                </div>
                <div className="space-y-3">
                  {faq.map((f, i) => {
                    const isOpen = expandedFaqIdx === i;
                    return (
                      <div
                        key={i}
                        className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                          isOpen ? 'bg-white border-primary/40 shadow-xs' : 'bg-surface-bright border-outline-variant/20 hover:border-outline-variant/40'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedFaqIdx(isOpen ? null : i)}
                          className="w-full text-left p-4 flex items-center justify-between gap-3 cursor-pointer"
                        >
                          <span className="text-xs font-bold text-on-surface flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-extrabold flex items-center justify-center shrink-0">
                              Q
                            </span>
                            {f.q}
                          </span>
                          <ChevronDown
                            className={`h-4 w-4 text-on-surface-variant transition-transform duration-200 shrink-0 ${
                              isOpen ? 'rotate-180 text-primary' : ''
                            }`}
                          />
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 pt-1 text-xs text-on-surface-variant leading-relaxed border-t border-outline-variant/10">
                            {f.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Special Offers Section */}
              {(() => {
                const offers = (l.schema_json as any)?.offers || [];
                const approvedOffers = offers; // Auto-published: display all offers instantly!
                if (approvedOffers.length === 0) return null;
                return (
                  <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6">
                    <h2 className="font-display text-on-surface text-lg font-bold mb-4 flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-primary text-sm">local_offer</span> Special Offers & Coupons
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {approvedOffers.map((offer: any) => (
                        <div key={offer.id} className="bg-surface-bright p-4 rounded-xl border border-outline-variant/20 flex gap-3 text-left">
                          {offer.image && (
                            <img src={offer.image} alt="" className="w-16 h-16 object-cover rounded-xl shrink-0" />
                          )}
                          <div>
                            <div className="text-xs font-bold text-on-surface">{offer.title}</div>
                            <div className="text-[10px] text-on-surface-variant mt-1">{offer.description}</div>
                            {offer.promo_code && (
                              <div className="mt-2 inline-flex items-center gap-1 bg-primary/10 border border-primary/20 text-primary text-[9px] font-bold px-2 py-0.5 rounded uppercase">
                                Code: {offer.promo_code}
                              </div>
                            )}
                            <div className="text-[9px] text-primary font-bold mt-2">Valid: {offer.startDate} to {offer.endDate}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })()}
            </>
          )}

          {/* 🛍️ E-COMMERCE & PRODUCTS TAB (Amazon / Modern D2C Storefront Style) */}
          {activeTab === 'products' && (
            <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-outline-variant/30 pb-4">
                <div>
                  <h2 className="font-display text-on-surface text-lg font-bold flex items-center gap-2">
                    <span className="material-symbols-outlined text-emerald-600 text-xl">storefront</span>
                    Products & Online Storefront
                  </h2>
                  <p className="text-xs text-on-surface-variant font-medium mt-0.5">
                    Direct WhatsApp order • 100% Genuine Quality • Fast Dispatch
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200">
                  <span className="material-symbols-outlined text-sm">verified</span>
                  {productsList.length} Verified Products
                </div>
              </div>

              {/* Product Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {productsList.map((p) => {
                  const discount = p.original_price && p.original_price > p.price
                    ? Math.round(((p.original_price - p.price) / p.original_price) * 100)
                    : null;
                  const firstImg = p.images?.[0] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80';

                  return (
                    <div
                      key={p.id}
                      className="bg-surface-bright rounded-2xl border border-outline-variant/30 overflow-hidden shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col group text-left"
                    >
                      {/* Image Frame */}
                      <a
                        {...linkProps(`/product/${p.slug || p.id || encodeURIComponent(p.title)}`)}
                        className="relative aspect-square overflow-hidden bg-slate-100 block cursor-pointer"
                      >
                        <img
                          src={firstImg}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {p.badge && (
                          <span className="absolute top-3 left-3 bg-emerald-600 text-white font-extrabold text-[10px] px-2.5 py-1 rounded-lg shadow-md uppercase tracking-wider">
                            {p.badge}
                          </span>
                        )}
                        {discount && (
                          <span className="absolute top-3 right-3 bg-red-600 text-white font-black text-[11px] px-2 py-0.5 rounded-lg shadow-md">
                            {discount}% OFF
                          </span>
                        )}
                      </a>

                      {/* Info Body */}
                      <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                        <div>
                          {p.category && (
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                              {p.category}
                            </span>
                          )}
                          <a
                            {...linkProps(`/product/${p.slug || p.id || encodeURIComponent(p.title)}`)}
                            className="block cursor-pointer"
                          >
                            <h3 className="font-bold text-xs text-on-surface line-clamp-2 leading-snug group-hover:text-emerald-700 transition-colors">
                              {p.title}
                            </h3>
                          </a>
                          <p className="text-[11px] text-on-surface-variant line-clamp-2 mt-1 leading-relaxed">
                            {p.description}
                          </p>
                        </div>

                        {/* Feature Pills */}
                        {p.features && p.features.length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-1">
                            {p.features.slice(0, 2).map((feat, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1 text-[9px] font-semibold text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded-md"
                              >
                                <span className="text-emerald-600 font-bold">✓</span> {feat}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Price & Stock */}
                        <div className="pt-2 border-t border-outline-variant/20 flex items-baseline justify-between">
                          <div className="flex items-baseline gap-2">
                            <span className="text-base font-black text-slate-900">
                              {p.currency || '₹'}{p.price.toLocaleString('en-IN')}
                            </span>
                            {p.original_price && p.original_price > p.price && (
                              <span className="text-xs text-slate-400 line-through font-semibold">
                                {p.currency || '₹'}{p.original_price.toLocaleString('en-IN')}
                              </span>
                            )}
                          </div>
                          <span className={`text-[10px] font-bold ${p.in_stock ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {p.in_stock ? '● In Stock' : '● Made to Order'}
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-2 gap-2 pt-1">
                          <a
                            {...linkProps(`/product/${p.slug || p.id || encodeURIComponent(p.title)}`)}
                            className="w-full py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-[11px] rounded-xl transition text-center flex items-center justify-center gap-1 cursor-pointer"
                          >
                            View Details ↗
                          </a>
                          <a
                            href={`https://wa.me/${(l.phone ?? '').replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${l.name}, I would like to enquire about "${p.title}" listed on Zha Search.`)}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => trackAction('whatsapp_click', 'whatsapp_clicks')}
                            className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded-xl shadow-xs transition flex items-center justify-center gap-1 cursor-pointer"
                          >
                            <span className="material-symbols-outlined text-[13px]">chat</span> Order
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* 🏗️ REAL ESTATE & PROJECTS TAB (Housing.com / 99acres Style) */}
          {activeTab === 'projects' && (
            <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-outline-variant/30 pb-4">
                <div>
                  <h2 className="font-display text-on-surface text-lg font-bold flex items-center gap-2">
                    <span className="material-symbols-outlined text-blue-600 text-xl">apartment</span>
                    Projects & Real Estate Developments
                  </h2>
                  <p className="text-xs text-on-surface-variant font-medium mt-0.5">
                    RERA Approved • Direct Builder Deals • Zero Brokerage
                  </p>
                </div>
                {/* Filter Pills */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {(['all', 'Under Construction', 'Ready to Move', 'Pre-Launch'] as const).map((st) => (
                    <button
                      key={st}
                      onClick={() => setProjectFilter(st)}
                      className={`px-3 py-1 text-[11px] font-bold rounded-lg transition cursor-pointer ${
                        projectFilter === st
                          ? 'bg-blue-600 text-white shadow-xs'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {st === 'all' ? 'All Projects' : st}
                    </button>
                  ))}
                </div>
              </div>

              {/* Projects List */}
              <div className="space-y-6">
                {projectsList
                  .filter((p) => projectFilter === 'all' || p.status === projectFilter)
                  .map((proj) => (
                    <div
                      key={proj.id}
                      className="bg-surface-bright rounded-3xl border border-outline-variant/30 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 text-left flex flex-col"
                    >
                      {/* Project Hero Banner */}
                      <a
                        {...linkProps(`/project/${proj.slug || proj.id || encodeURIComponent(proj.project_name)}`)}
                        className="relative h-64 md:h-80 w-full overflow-hidden block cursor-pointer"
                      >
                        <img
                          src={proj.hero_image}
                          alt={proj.project_name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/40 to-transparent flex flex-col justify-between p-6">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider backdrop-blur-md shadow-md ${
                              proj.status === 'Ready to Move'
                                ? 'bg-emerald-500/90 text-white'
                                : proj.status === 'Under Construction'
                                ? 'bg-amber-500/90 text-white'
                                : 'bg-purple-500/90 text-white'
                            }`}>
                              ● {proj.status}
                            </span>
                            {proj.rera_id && (
                              <span className="bg-slate-900/80 text-white border border-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold flex items-center gap-1">
                                <span className="material-symbols-outlined text-[13px] text-emerald-400">verified</span>
                                {proj.rera_id}
                              </span>
                            )}
                          </div>

                          <div className="space-y-1 text-white">
                            <div className="text-xs text-blue-300 font-bold uppercase tracking-wider">{proj.project_type} in {proj.city}</div>
                            <h3 className="font-display text-xl md:text-2xl font-extrabold text-white group-hover:text-blue-300 transition-colors">{proj.project_name}</h3>
                            <p className="text-xs text-slate-300 flex items-center gap-1 font-medium">
                              <span className="material-symbols-outlined text-sm text-red-400">location_on</span>
                              {proj.location}
                            </p>
                          </div>
                        </div>
                      </a>

                      {/* Project Body Details */}
                      <div className="p-6 space-y-6">
                        {/* Price and Possession Bar */}
                        <div className="bg-white rounded-2xl p-4 border border-outline-variant/30 flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Price Range</span>
                            <div className="text-lg md:text-xl font-black text-slate-900">{proj.price_range}</div>
                          </div>
                          {proj.possession_date && (
                            <div>
                              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Possession</span>
                              <div className="text-xs md:text-sm font-bold text-emerald-700">{proj.possession_date}</div>
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-2">
                            <a
                              {...linkProps(`/project/${proj.slug || proj.id || encodeURIComponent(proj.project_name)}`)}
                              className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1"
                            >
                              Project Details ↗
                            </a>
                            <button
                              onClick={() => {
                                setSelectedProject(proj);
                                setShowVisitModal(true);
                              }}
                              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1"
                            >
                              <span className="material-symbols-outlined text-sm">calendar_month</span> Site Visit
                            </button>
                            <a
                              href={`https://wa.me/${(l.phone ?? '').replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${l.name}, I am interested in your project "${proj.project_name}" in ${proj.city}. Please share brochure and price breakup.`)}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => trackAction('whatsapp_click', 'whatsapp_clicks')}
                              className="px-3.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1"
                            >
                              <span className="material-symbols-outlined text-sm">chat</span> WhatsApp
                            </a>
                            {proj.website_url && (
                              <a
                                href={proj.website_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3.5 py-2.5 bg-white border border-slate-300 hover:border-blue-600 text-slate-800 font-bold text-xs rounded-xl transition flex items-center gap-1"
                              >
                                <ExternalLink className="h-3.5 w-3.5 text-blue-600" /> Website
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Configurations Table */}
                        {proj.configurations && proj.configurations.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-bold text-xs text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                              <span className="material-symbols-outlined text-blue-600 text-base">domain</span>
                              Available Configurations & Pricing
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                              {proj.configurations.map((cfg, idx) => (
                                <div key={idx} className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between space-y-1">
                                  <div className="font-bold text-xs text-slate-900">{cfg.type}</div>
                                  <div className="text-[11px] text-slate-500 font-medium">{cfg.builtup_area}</div>
                                  <div className="text-xs font-black text-blue-700 pt-1">{cfg.price}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Key Amenities */}
                        {proj.amenities && proj.amenities.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-bold text-xs text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                              <span className="material-symbols-outlined text-emerald-600 text-base">pool</span>
                              Key Project Amenities
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {proj.amenities.map((amenity, idx) => (
                                <div key={idx} className="bg-white p-2.5 rounded-xl border border-slate-200 text-[11px] font-semibold text-slate-800 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-emerald-600 text-sm">check_circle</span>
                                  <span className="truncate">{amenity}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Landmark Distances */}
                        {proj.landmark_distances && proj.landmark_distances.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-bold text-xs text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                              <span className="material-symbols-outlined text-amber-600 text-base">near_me</span>
                              Location & Connectivity
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {proj.landmark_distances.map((lm, idx) => (
                                <div key={idx} className="bg-white p-3 rounded-xl border border-slate-200 flex items-center justify-between text-xs">
                                  <span className="font-semibold text-slate-700">{lm.name}</span>
                                  <span className="font-bold text-blue-600">{lm.distance}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          )}

          {activeTab === 'reviews' && (
            <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6 space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div>
                  <h2 className="font-display text-on-surface text-lg font-bold">User Ratings & Reviews</h2>
                  <p className="text-xs text-slate-500 font-semibold mt-0.5">
                    {reviewsList.length > 0
                      ? `${reviewsList.length} verified customer review${reviewsList.length > 1 ? 's' : ''} (${avgUserRating} ★ average)`
                      : 'No user reviews yet. Be the first to write a review!'}
                  </p>
                </div>
                {avgUserRating && (
                  <div className="bg-emerald-600 text-white font-bold text-sm px-3 py-1.5 rounded-xl flex items-center gap-1 shadow-sm">
                    ★ {avgUserRating} / 5.0
                  </div>
                )}
              </div>

              {/* Write a Review Form */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5">
                <h3 className="font-bold text-xs text-slate-900 mb-3 uppercase tracking-wider flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-emerald-600 text-base">rate_review</span>
                  Write a Customer Review
                </h3>
                {reviewSuccess ? (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-xl text-xs font-bold flex items-center gap-2">
                    <span className="material-symbols-outlined text-base">check_circle</span>
                    Thank you! Your review has been submitted and added to the listing profile.
                  </div>
                ) : (
                  <form onSubmit={handleReviewSubmit} className="space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Your Rating *</label>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            type="button"
                            key={star}
                            onClick={() => setNewRating(star)}
                            className={`text-2xl cursor-pointer transition ${star <= newRating ? 'text-amber-500' : 'text-slate-300'}`}
                          >
                            ★
                          </button>
                        ))}
                        <span className="ml-2 text-xs font-bold text-slate-700">{newRating}.0 / 5.0</span>
                      </div>
                    </div>

                    <div>
                      <input
                        type="text"
                        value={newReviewerName}
                        onChange={(e) => setNewReviewerName(e.target.value)}
                        placeholder="Your Full Name *"
                        className="w-full h-10 px-4 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>

                    <div>
                      <textarea
                        value={newReviewComment}
                        onChange={(e) => setNewReviewComment(e.target.value)}
                        placeholder="Share details about your experience, service quality, response time..."
                        rows={3}
                        className="w-full p-4 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-emerald-600 resize-none"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submittingReview}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs px-6 py-2.5 rounded-xl shadow-md transition cursor-pointer disabled:opacity-50"
                    >
                      {submittingReview ? 'Submitting...' : 'Post Review'}
                    </button>
                  </form>
                )}
              </div>

              {/* Display Real User Reviews */}
              <div className="space-y-3 pt-2">
                {reviewsList.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 font-semibold text-xs border border-dashed border-slate-200 rounded-2xl">
                    No customer reviews submitted yet. Submit a review above to post the first rating!
                  </div>
                ) : (
                  reviewsList.map((rev) => (
                    <div key={rev.id} className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm text-left">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-emerald-700 text-white font-extrabold flex items-center justify-center text-xs shadow-sm">
                            {rev.user_name[0]?.toUpperCase() || 'U'}
                          </div>
                          <span className="font-bold text-xs text-slate-900">{rev.user_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="bg-emerald-100 text-emerald-800 font-bold text-[10px] px-2 py-0.5 rounded-lg">
                            {rev.rating}.0 ★
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono">
                            {new Date(rev.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed font-medium pl-10">{rev.comment}</p>
                    </div>
                  ))
                )}
              </div>
            </section>
          )}

          {activeTab === 'offers' && (() => {
            const offers = (l.schema_json as any)?.offers || [];
            return (
              <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6">
                <h2 className="font-display text-on-surface text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-base">local_offer</span> Special Offers & Deals
                </h2>
                {offers.length > 0 ? (
                  <div className="space-y-4">
                    {offers.map((offer: any, idx: number) => (
                      <div key={offer.id || idx} className="flex gap-4 bg-surface-bright p-4 rounded-xl border border-outline-variant/20 text-left">
                        {offer.image ? (
                          <img src={offer.image} alt="" className="w-16 h-16 object-cover rounded-xl shrink-0 border border-outline-variant/30" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-600 flex flex-col items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-lg">local_offer</span>
                          </div>
                        )}
                        <div>
                          <div className="font-bold text-xs text-on-surface mb-0.5">{offer.title}</div>
                          <p className="text-xs text-on-surface-variant leading-relaxed">{offer.description}</p>
                          {offer.promo_code && (
                            <div className="mt-2 inline-flex items-center gap-1 bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold px-2.5 py-1 rounded-lg uppercase">
                              Promo Code: {offer.promo_code}
                            </div>
                          )}
                          <div className="text-[10px] text-primary font-bold mt-2">Valid: {offer.startDate} to {offer.endDate}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-on-surface-variant font-semibold text-xs border border-dashed border-outline-variant/30 rounded-xl">
                    No active offers published at this time.
                  </div>
                )}
              </section>
            );
          })()}

          {activeTab === 'events' && (() => {
            const events = (l.schema_json as any)?.events || [];
            return (
              <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6">
                <h2 className="font-display text-on-surface text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-base">event</span> Upcoming Events
                </h2>
                {events.length > 0 ? (
                  <div className="space-y-4">
                    {events.map((ev: any, idx: number) => (
                      <div key={ev.id || idx} className="flex gap-4 bg-surface-bright p-4 rounded-xl border border-outline-variant/20 text-left">
                        {ev.image ? (
                          <img src={ev.image} alt="" className="w-16 h-16 object-cover rounded-xl shrink-0 border border-outline-variant/30" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex flex-col items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-lg">event</span>
                          </div>
                        )}
                        <div>
                          <div className="font-bold text-xs text-on-surface mb-0.5">{ev.title}</div>
                          <div className="text-[10px] text-primary font-bold mb-1">
                            {ev.startDate} {ev.endDate && ev.endDate !== ev.startDate ? ` - ${ev.endDate}` : ''}
                          </div>
                          <p className="text-xs text-on-surface-variant leading-relaxed">{ev.description}</p>
                          {ev.event_location && (
                            <div className="text-[10px] text-on-surface-variant font-semibold mt-2 flex items-center gap-1">
                              <span className="material-symbols-outlined text-sm text-red-500">location_on</span>
                              Location: {ev.event_location}
                            </div>
                          )}
                          {ev.event_map_link && (
                            <div className="mt-1">
                              <a
                                href={ev.event_map_link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[9px] text-primary hover:underline font-bold inline-flex items-center gap-0.5"
                              >
                                Get Directions <span className="material-symbols-outlined text-[10px]">open_in_new</span>
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-on-surface-variant font-semibold text-xs border border-dashed border-outline-variant/30 rounded-xl">
                    No upcoming events scheduled at this time.
                  </div>
                )}
              </section>
            );
          })()}

          {activeTab === 'photos' && (
            <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-on-surface text-lg font-bold">Photo Gallery</h2>
                <span className="text-xs text-slate-500 font-medium">Click any photo to view full-screen</span>
              </div>
              {uploadedImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {uploadedImages.map((img, i) => (
                    <div 
                      key={i} 
                      onClick={() => setLightboxIndex(i)}
                      className="aspect-square rounded-xl overflow-hidden border border-outline-variant/20 bg-surface-bright relative group cursor-pointer shadow-xs hover:shadow-md transition"
                    >
                      <img src={img} alt={`Photo ${i+1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                        <span className="material-symbols-outlined text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md">
                          zoom_in
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : l.hero_image_url ? (
                <div className="grid grid-cols-1 max-w-md gap-3">
                  <div 
                    onClick={() => setLightboxIndex(0)}
                    className="aspect-video rounded-xl overflow-hidden border border-outline-variant/20 relative group cursor-pointer"
                  >
                    <img src={l.hero_image_url} alt="Cover" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                      <span className="material-symbols-outlined text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                        zoom_in
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-on-surface-variant font-semibold text-xs border border-dashed border-outline-variant/30 rounded-xl">
                  No photo gallery uploaded by business owner yet.
                </div>
              )}
            </section>
          )}

          {activeTab === 'videos' && (() => {
            const rawLinks = (l.schema_json as any)?.youtube_links || [];
            const singleVideo = (l.schema_json as any)?.video_url;
            const youtubeLinks: string[] = Array.from(new Set([
              ...(Array.isArray(rawLinks) ? rawLinks : []),
              ...(singleVideo ? [singleVideo] : [])
            ])).filter(Boolean);

            const getYoutubeEmbedUrl = (urlStr: string) => {
              const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
              const match = urlStr.match(regExp);
              return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
            };

            return (
              <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6 text-center">
                <h2 className="font-display text-on-surface text-lg font-bold mb-4">Videos</h2>
                {youtubeLinks.length === 0 ? (
                  <div className="py-12 flex flex-col items-center gap-3 text-on-surface-variant">
                    <span className="material-symbols-outlined text-5xl text-outline">videocam_off</span>
                    <p className="text-xs font-semibold">No videos have been added yet.</p>
                    <p className="text-[11px]">The business owner can add videos from their dashboard.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {youtubeLinks.map((urlStr: string, idx: number) => {
                      const embed = getYoutubeEmbedUrl(urlStr);
                      if (embed) {
                        return (
                          <div key={idx} className="aspect-video w-full rounded-xl overflow-hidden shadow-md bg-black ring-1 ring-outline-variant/20">
                            <iframe
                              className="w-full h-full"
                              src={embed}
                              title={`Video ${idx+1}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        );
                      }
                      return (
                        <a
                          key={idx}
                          href={urlStr}
                          target="_blank" rel="noreferrer"
                          className="flex items-center gap-3 p-4 bg-surface-bright border border-outline-variant/30 rounded-2xl hover:border-primary hover:text-primary transition"
                        >
                          <span className="material-symbols-outlined text-red-500 text-3xl">play_circle</span>
                          <div className="min-w-0 text-left">
                            <div className="text-xs font-bold text-on-surface truncate">Watch YouTube Video {idx+1}</div>
                            <div className="text-[10px] text-on-surface-variant truncate">{urlStr}</div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })()}

          {activeTab === 'contact' && (
            <section className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 p-6">
              <h2 className="font-display text-on-surface text-lg font-bold mb-4">Contact Business</h2>
              {l.phone && (
                <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-lg">call</span>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Direct Verified Contact Number</div>
                      <a href={`tel:${l.phone}`} className="text-base font-black text-emerald-800 hover:underline">
                        {l.phone}
                      </a>
                    </div>
                  </div>
                  <a
                    href={`tel:${l.phone}`}
                    onClick={() => trackAction('call_click', 'call_clicks')}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs px-5 py-2.5 rounded-xl shadow-xs transition cursor-pointer"
                  >
                    Call Business Now
                  </a>
                </div>
              )}
              {enqSuccess ? (
                <div className="bg-primary/10 border border-primary/30 text-primary rounded-xl p-6 text-center">
                  <span className="material-symbols-outlined text-4xl mb-2 block">check_circle</span>
                  <div className="font-bold text-sm">Enquiry Sent!</div>
                  <div className="text-xs mt-1">The business will get back to you shortly.</div>
                </div>
              ) : (
                <form onSubmit={handleEnquirySubmit} className="flex flex-col gap-3 max-w-md">
                  <input value={enqName} onChange={(e) => setEnqName(e.target.value)} className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-emerald-600 outline-none" placeholder="Your Full Name *" type="text" disabled={enqSubmitting} />
                  <input value={enqPhone} onChange={(e) => setEnqPhone(e.target.value)} className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-emerald-600 outline-none" placeholder="Phone Number *" type="tel" disabled={enqSubmitting} />
                  <textarea value={enqMsg} onChange={(e) => setEnqMsg(e.target.value)} className="w-full p-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-emerald-600 outline-none resize-none" placeholder="How can we help? Enter message details..." rows={4} disabled={enqSubmitting} />
                  <button type="submit" disabled={enqSubmitting} className="w-full bg-emerald-700 text-white font-bold text-xs h-11 rounded-xl shadow-md hover:bg-emerald-800 transition-all flex items-center justify-center cursor-pointer disabled:opacity-50">
                    {enqSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </section>
          )}
        </div>

        {/* Right Column: Sidebar */}
        <aside className="md:col-span-4 flex flex-col gap-6">
          {/* Location Card */}
          <div className="bg-surface-container-lowest rounded-2xl shadow-sm border border-outline-variant/30 overflow-hidden flex flex-col">
            <div className="h-48 w-full bg-surface-container relative overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                marginHeight={0}
                marginWidth={0}
                src={(() => {
                  if (l.latitude && l.longitude) {
                    return `https://maps.google.com/maps?q=${l.latitude},${l.longitude}&t=&z=16&ie=UTF8&iwloc=&output=embed`;
                  }
                  const gLink = (l.schema_json as any)?.google_map_link || '';
                  const atM = gLink.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/) || gLink.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/) || gLink.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
                  if (atM) {
                    return `https://maps.google.com/maps?q=${atM[1]},${atM[2]}&t=&z=16&ie=UTF8&iwloc=&output=embed`;
                  }
                  const locQuery = l.address ? `${l.address}, ${l.city || ''}` : `${l.name}, ${l.city || ''}`;
                  return `https://maps.google.com/maps?q=${encodeURIComponent(locQuery)}&t=&z=16&ie=UTF8&iwloc=&output=embed`;
                })()}
                className="w-full h-full border-0"
              />
              <a
                href={(() => {
                  const gLink = (l.schema_json as any)?.google_map_link;
                  if (gLink && gLink.startsWith('http')) return gLink;
                  if (l.latitude && l.longitude) return `https://www.google.com/maps/search/?api=1&query=${l.latitude},${l.longitude}`;
                  const locQuery = l.address ? `${l.address}, ${l.city || ''}` : `${l.name}, ${l.city || ''}`;
                  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locQuery)}`;
                })()}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-2 right-2 bg-white/95 hover:bg-white text-slate-800 text-[11px] font-bold px-2.5 py-1 rounded-lg shadow-md border border-slate-200 transition flex items-center gap-1 cursor-pointer"
              >
                <span>View on Map</span>
                <span className="material-symbols-outlined text-[12px]">open_in_new</span>
              </a>
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary text-[20px] mt-0.5">location_on</span>
                <div>
                  <p className="font-semibold text-xs text-on-surface">{l.address}</p>
                  <p className="text-on-surface-variant text-[11px]">{l.city}, {l.state} {l.country}</p>
                </div>
              </div>
              {l.phone && (
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-emerald-600 text-[20px] mt-0.5">call</span>
                  <div className="w-full">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Phone Number</p>
                    <a href={`tel:${l.phone}`} className="font-bold text-xs text-emerald-700 hover:underline">
                      {l.phone}
                    </a>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary text-[20px] mt-0.5">schedule</span>
                <div className="w-full">
                  <div className="flex justify-between items-center w-full text-xs">
                    <p className="font-bold text-primary">Open Now</p>
                    <p className="text-on-surface-variant">11:00 AM - 11:30 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Enquiry Form (sidebar) */}
          <div className="bg-surface-container-low rounded-2xl shadow-sm border border-outline-variant/30 p-6">
            <h3 className="font-display text-on-surface text-base font-bold mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-[20px]">mail</span>
              Send Enquiry
            </h3>
            {enqSuccess ? (
              <div className="bg-primary/10 border border-primary/30 text-primary rounded-xl p-4 text-center">
                <span className="material-symbols-outlined text-3xl mb-2 block">check_circle</span>
                <div className="font-bold text-xs">Enquiry Sent Successfully!</div>
              </div>
            ) : (
              <form onSubmit={handleEnquirySubmit} className="flex flex-col gap-3">
                <input
                  value={enqName}
                  onChange={(e) => setEnqName(e.target.value)}
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 placeholder:text-slate-600 placeholder:font-bold focus:ring-2 focus:ring-emerald-600 outline-none"
                  placeholder="Your Full Name *"
                  type="text"
                  disabled={enqSubmitting}
                />
                <input
                  value={enqPhone}
                  onChange={(e) => setEnqPhone(e.target.value)}
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 placeholder:text-slate-600 placeholder:font-bold focus:ring-2 focus:ring-emerald-600 outline-none"
                  placeholder="Phone Number *"
                  type="tel"
                  disabled={enqSubmitting}
                />
                <textarea
                  value={enqMsg}
                  onChange={(e) => setEnqMsg(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 placeholder:text-slate-600 placeholder:font-bold focus:ring-2 focus:ring-emerald-600 outline-none resize-none"
                  placeholder="How can we help? Enter enquiry details..."
                  rows={3}
                  disabled={enqSubmitting}
                />
                <button
                  type="submit"
                  disabled={enqSubmitting}
                  className="w-full bg-primary text-on-primary font-bold text-xs h-11 rounded-xl shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all hover:-translate-y-0.5 flex items-center justify-center cursor-pointer disabled:opacity-50"
                >
                  {enqSubmitting ? 'Sending...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>

          {/* Related Search Widget below Contact form */}
          <div className="bg-white border border-outline-variant/30 rounded-2xl p-5 shadow-sm">
            <h4 className="font-display font-extrabold text-on-surface text-xs mb-3 uppercase tracking-wider">Related Searches</h4>
            <ul className="space-y-2">
              {TOP_CITIES.slice(0, 5).map((city) => (
                <li key={city}>
                  <a
                    href={`/search/${(l.category ?? 'services').toLowerCase().replace(/\s+/g, '-')}/${city.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex items-center justify-between group py-1"
                  >
                    <span className="text-xs font-semibold text-on-surface group-hover:text-primary transition-colors">
                      {l.category}s in {city}
                    </span>
                    <span className="material-symbols-outlined text-outline group-hover:text-primary text-[14px] transition-colors">chevron_right</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      {/* Full-width: You May Also Be Interested In */}
      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-on-surface text-xl font-extrabold mb-6 text-center">You May Also Be Interested In</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.slice(0, 3).map((r) => {
              const relImg = getValidListingImage(r);
              return (
                <a {...linkProps(`/b/${r.slug}`)} key={r.id} className="group bg-white rounded-2xl border border-outline-variant/30 overflow-hidden hover:shadow-xl hover:border-emerald-600 transition-all duration-300">
                  <div className="relative h-44 overflow-hidden">
                    <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src={relImg} alt={r.name} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10" />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="bg-white/95 backdrop-blur text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 text-amber-600 shadow-xs">
                        <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: "'FILL' 1" }}>star</span> 5.0 ★ (2)
                      </span>
                      <span className="bg-emerald-700 text-white text-[9px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider">OPEN NOW</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-display font-extrabold text-sm text-slate-900 group-hover:text-emerald-700 transition-colors truncate mb-1">{r.name}</div>
                    <div className="text-[11px] font-semibold text-slate-500 mb-3">{r.category} · {r.city}</div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <span className="flex items-center gap-1 bg-emerald-50 text-emerald-800 px-2.5 py-1 rounded-full text-[10px] font-bold">
                        <span className="material-symbols-outlined text-[12px]">storefront</span> {r.category}
                      </span>
                      <span className="bg-emerald-900 text-white font-extrabold px-3 py-1.5 rounded-xl text-[11px] group-hover:bg-emerald-800 transition flex items-center gap-1">
                        View Profile ↗
                      </span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </section>
      )}
      {/* Product Quick View & Inquiry Modal */}
      {showProductModal && selectedProduct && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowProductModal(false)}
        >
          <div
            className="bg-white border border-slate-200 rounded-3xl p-6 w-full max-w-xl shadow-2xl space-y-4 text-slate-800 text-left max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                  {selectedProduct.category || 'Product Details'}
                </span>
                <h3 className="font-bold text-slate-900 text-base">{selectedProduct.title}</h3>
              </div>
              <button
                onClick={() => setShowProductModal(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="aspect-square rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
                <img
                  src={selectedProduct.images?.[0] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80'}
                  alt={selectedProduct.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-black text-slate-900">
                      {selectedProduct.currency || '₹'}{selectedProduct.price.toLocaleString('en-IN')}
                    </span>
                    {selectedProduct.original_price && selectedProduct.original_price > selectedProduct.price && (
                      <span className="text-xs text-slate-400 line-through font-semibold">
                        {selectedProduct.currency || '₹'}{selectedProduct.original_price.toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] font-bold text-emerald-700 mt-0.5">
                    {selectedProduct.in_stock ? '● In Stock • Ready for Dispatch' : '● Made to Order'}
                  </div>
                  {selectedProduct.sku && (
                    <div className="text-[10px] text-slate-400 font-mono mt-1">
                      SKU: {selectedProduct.sku}
                    </div>
                  )}
                  <p className="text-xs text-slate-600 leading-relaxed mt-2">
                    {selectedProduct.description}
                  </p>
                </div>

                <a
                  href={`https://wa.me/${(l.phone ?? '').replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${l.name}, I am interested in "${selectedProduct.title}" from your Zha Search storefront.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackAction('whatsapp_click', 'whatsapp_clicks')}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-sm">chat</span> Instant WhatsApp Checkout
                </a>
              </div>
            </div>

            {/* Detailed Features */}
            {selectedProduct.features && selectedProduct.features.length > 0 && (
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Product Specifications & Highlights
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {selectedProduct.features.map((feat, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-50 border border-slate-200 p-2.5 rounded-xl text-xs font-semibold text-slate-700 flex items-center gap-2"
                    >
                      <span className="text-emerald-600 font-bold">✓</span> {feat}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Real Estate Schedule Site Visit Modal */}
      {showVisitModal && selectedProject && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowVisitModal(false)}
        >
          <div
            className="bg-white border border-slate-200 rounded-3xl p-6 w-full max-w-lg shadow-2xl space-y-4 text-slate-800 text-left max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">
                  VIP Site Visit & Brochure Request
                </span>
                <h3 className="font-bold text-slate-900 text-base">{selectedProject.project_name}</h3>
              </div>
              <button
                onClick={() => setShowVisitModal(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold"
              >
                ✕
              </button>
            </div>

            {visitSuccess ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-2xl text-center space-y-3">
                <span className="material-symbols-outlined text-4xl text-emerald-600">check_circle</span>
                <div className="font-bold text-sm">Site Visit Scheduled Successfully!</div>
                <p className="text-xs text-emerald-700 leading-relaxed">
                  Our developer relationship manager from <strong>{selectedProject.developer_name || l.name}</strong> will contact you at <strong>{visitPhone}</strong> to confirm your complimentary pick-up & site walkthrough on <strong>{visitDate}</strong> ({visitSlot}).
                </p>
                <div className="pt-2">
                  <button
                    onClick={() => {
                      setShowVisitModal(false);
                      setVisitSuccess(false);
                    }}
                    className="bg-emerald-700 text-white font-bold text-xs px-6 py-2.5 rounded-xl shadow-sm hover:bg-emerald-800 transition cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!visitName.trim() || !visitPhone.trim() || !visitDate) {
                    alert('Please provide your name, phone number, and preferred visit date.');
                    return;
                  }
                  setVisitSubmitting(true);
                  setTimeout(() => {
                    setVisitSubmitting(false);
                    setVisitSuccess(true);
                  }, 800);
                }}
                className="space-y-4"
              >
                <div className="bg-blue-50 border border-blue-200 p-3.5 rounded-2xl flex items-center justify-between text-xs">
                  <div>
                    <span className="font-bold text-blue-900">{selectedProject.project_type}</span> • {selectedProject.location}
                  </div>
                  <span className="font-extrabold text-blue-700">{selectedProject.price_range}</span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Full Name *</label>
                  <input
                    value={visitName}
                    onChange={(e) => setVisitName(e.target.value)}
                    placeholder="e.g. Ramesh Kumar"
                    required
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Mobile Number *</label>
                    <input
                      value={visitPhone}
                      onChange={(e) => setVisitPhone(e.target.value)}
                      placeholder="e.g. 9876543210"
                      type="tel"
                      required
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Email Address</label>
                    <input
                      value={visitEmail}
                      onChange={(e) => setVisitEmail(e.target.value)}
                      placeholder="name@example.com"
                      type="email"
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Preferred Date *</label>
                    <input
                      value={visitDate}
                      onChange={(e) => setVisitDate(e.target.value)}
                      type="date"
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Time Slot *</label>
                    <select
                      value={visitSlot}
                      onChange={(e) => setVisitSlot(e.target.value)}
                      className="w-full h-11 px-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer"
                    >
                      <option value="10:00 AM - Morning">10:00 AM - Morning</option>
                      <option value="12:00 PM - Afternoon">12:00 PM - Afternoon</option>
                      <option value="03:00 PM - Evening">03:00 PM - Evening</option>
                      <option value="05:30 PM - Sunset">05:30 PM - Sunset</option>
                    </select>
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-between gap-3">
                  <a
                    href={`https://wa.me/${(l.phone ?? '').replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${l.name}, I want to schedule a visit to ${selectedProject.project_name}.`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition flex items-center gap-1 cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-sm">chat</span> WhatsApp Inquiry
                  </a>
                  <button
                    type="submit"
                    disabled={visitSubmitting}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-md transition cursor-pointer disabled:opacity-50"
                  >
                    {visitSubmitting ? 'Booking Visit...' : 'Confirm Free Site Visit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      {/* Interactive Full-Screen Lightbox Modal */}
      {lightboxIndex !== null && Boolean(l) && (
        <ImageLightboxModal
          photos={(((l?.images && Array.isArray(l.images) && l.images.length > 0) ? l.images : (l?.hero_image_url ? [l.hero_image_url] : [])) as string[]).filter((x): x is string => Boolean(x))}
          initialIndex={Number(lightboxIndex)}
          businessName={String(l?.name || 'Business Photos')}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

function BlogPage({ slug }: { slug: string }) {
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      // 1. Try fetching by slug from Supabase
      const { data } = await supabase.from('blogs').select('*').eq('slug', slug).maybeSingle();
      if (data) {
        setBlog(data as BlogPost);
        // Increment view count
        await supabase.from('blogs').update({ views: (data.views || 0) + 1 }).eq('id', data.id);
      } else {
        // Fallback generator for preview if slug matches generated pattern
        const cityMatch = slug.match(/in-([a-z-]+)$/);
        const cityName = cityMatch ? cityMatch[1].replace(/-/g, ' ').toUpperCase() : 'THANE';
        const categoryMatch = slug.replace(/^top-10-best-/, '').replace(/-in-[a-z-]+$/, '').replace(/-/g, ' ');
        const categoryName = categoryMatch ? categoryMatch.toUpperCase() : 'WINE SHOPS';
        const generated = generateTopTenRealEstateBlog(cityName, categoryName, []);
        setBlog({
          id: 'preview',
          title: generated.title,
          slug: generated.slug,
          meta_title: generated.meta_title,
          meta_description: generated.meta_description,
          focus_keyword: generated.focus_keyword,
          author: generated.author,
          featured_image: generated.featured_image,
          content: generated.content,
          views: 128,
          status: 'published',
          city: cityName,
          category: categoryName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="animate-spin text-primary text-3xl mb-2">⏳</div>
        <p className="text-xs font-bold text-slate-500">Loading Blog Post...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Blog Post Not Found</h1>
        <p className="text-xs text-slate-500 mb-4">The blog post you requested could not be located.</p>
        <a {...linkProps('/')} className="inline-block bg-primary text-white text-xs font-bold px-5 py-2.5 rounded-xl">Back to Home</a>
      </div>
    );
  }

  return (
    <article className="max-w-4xl mx-auto px-6 py-10 space-y-8 animate-fade-in bg-white text-slate-900 shadow-sm rounded-3xl border border-slate-200/80 my-8">
      {/* Meta Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-xs font-bold text-emerald-700 uppercase tracking-wider">
          <span>📍 {blog.city || 'Thane'}</span>
          <span>•</span>
          <span>🏷️ {blog.category || 'Directory Guide'}</span>
        </div>

        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
          {blog.title}
        </h1>

        <div className="flex items-center justify-between border-y border-slate-200 py-3.5 text-xs text-slate-700 font-semibold flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs shadow-sm">
              Z
            </div>
            <div>
              <div className="font-bold text-slate-900">{blog.author || 'Zha Search Author'}</div>
              <div className="text-[10px] text-slate-500">E-EAT Verified Author</div>
            </div>
          </div>

          <div className="flex items-center gap-4 text-slate-600 font-bold">
            <span>👁️ {blog.views || 1} Views</span>
            <span>📅 {blog.created_at ? new Date(blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>
      </div>

      {/* Featured Cover Image */}
      {blog.featured_image && (
        <div className="aspect-[16/9] w-full rounded-3xl overflow-hidden shadow-md border border-slate-200 bg-slate-100">
          <img
            src={blog.featured_image}
            alt={blog.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Rich Blog Body Content */}
      <div
        className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-extrabold prose-h2:text-xl prose-h2:text-slate-900 prose-h2:mt-6 prose-p:text-slate-800 prose-p:leading-relaxed prose-a:text-emerald-700 prose-a:font-bold hover:prose-a:underline text-sm font-semibold text-slate-900"
        dangerouslySetInnerHTML={{ __html: blog.content }}
      />

      {/* Author Bio Box */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-6 flex items-start gap-4 text-emerald-950">
        <div className="w-12 h-12 rounded-2xl bg-emerald-700 text-white font-extrabold text-lg flex items-center justify-center shrink-0">
          Z
        </div>
        <div>
          <div className="font-extrabold text-sm text-emerald-900">Written by {blog.author || 'Zha Search Author'}</div>
          <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
            Content Editor & Local Search Specialist at Zha Search. Dedicated to delivering authentic, verified, E-EAT compliant guides for real estate, healthcare, dining, and local services across India.
          </p>
        </div>
      </div>
    </article>
  );
}
